import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Settings.css';
import { useAuth } from './AuthProvider';
import { API_BASE } from './apiBase';

function Settings({ onOrgSettingsChange, initialTab, showToast, onVerificationStatusChange }) {
  const { authFetch } = useAuth();
  const API_URL = API_BASE;

  // Authenticated fetch wrapper
  const apiFetch = useCallback(async (url, options = {}) => {
    return authFetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }, [authFetch]);

  const [activeTab, setActiveTab] = useState(initialTab || 'organizations');
  const [credentialsSubTab, setCredentialsSubTab] = useState('management'); // 'management', 'psa', 'edr', 'service-delivery'
  const [orgView, setOrgView] = useState('overview'); // 'overview' or 'matched'
  const [organizations, setOrganizations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, matched, unmatched, manual
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [rematching, setRematching] = useState(false);

  // Event Log state
  const [events, setEvents] = useState([]);
  const [eventStats, setEventStats] = useState({ total: 0 });
  const [eventLoading, setEventLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Sorting state for organizations
  const [sortField, setSortField] = useState('master_org_name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Credentials state
  const [credentials, setCredentials] = useState({});
  const [credentialsStatus, setCredentialsStatus] = useState({});
  const [credentialsEnvironment, setCredentialsEnvironment] = useState('local');
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [testingSource, setTestingSource] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [maskedFields, setMaskedFields] = useState({});
  const [primaryManagement, setPrimaryManagement] = useState('ncentral');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [cwConfigTypes, setCwConfigTypes] = useState([]);
  const [cwSelectedTypes, setCwSelectedTypes] = useState([]);
  const [cwTypeSearch, setCwTypeSearch] = useState('');
  const [cwConfigTypesLoading, setCwConfigTypesLoading] = useState(false);
  const [cwCompanyTypes, setCwCompanyTypes] = useState([]);
  const [cwSelectedCompanyTypes, setCwSelectedCompanyTypes] = useState([]);
  const [cwCompanyTypeSearch, setCwCompanyTypeSearch] = useState('');
  const [cwCompanyTypesLoading, setCwCompanyTypesLoading] = useState(false);
  const [databaseType, setDatabaseType] = useState('sqlite');

  // Datto RMM filtering state
  const [dattoSites, setDattoSites] = useState([]);
  const [dattoSelectedSites, setDattoSelectedSites] = useState([]);
  const [dattoSiteSearch, setDattoSiteSearch] = useState('');
  const [dattoDeviceTypes, setDattoDeviceTypes] = useState([]);
  const [dattoSelectedDeviceTypes, setDattoSelectedDeviceTypes] = useState([]);
  const [dattoDeviceTypeSearch, setDattoDeviceTypeSearch] = useState('');
  const [dattoSyncAllOrgs, setDattoSyncAllOrgs] = useState(true);

  // Ticketing configuration state
  const [ticketingSettings, setTicketingSettings] = useState({});
  const [cwBoards, setCwBoards] = useState([]);
  const [cwBoardStatuses, setCwBoardStatuses] = useState([]);
  const [cwBoardTypes, setCwBoardTypes] = useState([]);
  const [cwBoardSubtypes, setCwBoardSubtypes] = useState([]);
  const [cwBoardItems, setCwBoardItems] = useState([]);
  const [cwPriorities, setCwPriorities] = useState([]);
  const [ticketingLoading, setTicketingLoading] = useState(false);
  const [ticketingSaving, setTicketingSaving] = useState(false);
  const [ticketingTestResult, setTicketingTestResult] = useState(null);
  const [ticketingConfiguring, setTicketingConfiguring] = useState(false);
  const [cwBoardsLoading, setCwBoardsLoading] = useState(false);
  const [cwStatusesLoading, setCwStatusesLoading] = useState(false);
  const [cwTypesLoading, setCwTypesLoading] = useState(false);
  const [cwSubtypesLoading, setCwSubtypesLoading] = useState(false);
  const [cwItemsLoading, setCwItemsLoading] = useState(false);
  const [cwPrioritiesLoading, setCwPrioritiesLoading] = useState(false);
  const [cwCacheRefreshing, setCwCacheRefreshing] = useState(false);

  // General settings state
  const [generalSettings, setGeneralSettings] = useState({
    stale_threshold_days: 30,
    sync_drift_threshold_days: 3,
    warning_threshold_days: 7,
    timezone: 'America/Vancouver',
    log_level: 'minimal'
  });
  const [generalSettingsLoading, setGeneralSettingsLoading] = useState(false);
  const [generalSettingsSaving, setGeneralSettingsSaving] = useState(false);

  // Production settings state (v2.5+)
  const [productionSettings, setProductionSettings] = useState({
    cors_origins: '*',
    rate_limit_default: '200 per minute',
    rate_limit_storage: 'memory://'
  });
  const [productionBackend, setProductionBackend] = useState(null);
  const [productionVersion, setProductionVersion] = useState(null);
  const [productionRequiredRole, setProductionRequiredRole] = useState('Admin');
  const [productionRoles, setProductionRoles] = useState({ admin: 'Admin', technician: 'Technician', viewer: 'Viewer' });
  const [productionSettingsLoading, setProductionSettingsLoading] = useState(false);
  const [productionSettingsSaving, setProductionSettingsSaving] = useState(false);
  const [productionAccessDenied, setProductionAccessDenied] = useState(false);
  const [showProductionHelp, setShowProductionHelp] = useState(false);

  // Branding / Whitelabel settings state
  const [brandingSettings, setBrandingSettings] = useState({
    app_name: '',
    logo_dark: '',
    logo_light: '',
    favicon: '',
    primary_color: '',
    accent_color: '',
  });
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingPreviewTheme, setBrandingPreviewTheme] = useState('dark');

  // Organization settings state
  const [orgSettings, setOrgSettings] = useState({});
  const [editingOrgSettings, setEditingOrgSettings] = useState(null); // master_org_name being edited
  const [orgSettingsTab, setOrgSettingsTab] = useState('general'); // Tab state for org settings modal: 'general', 'integrations', 'macos', 'microsoft'
  const [mspOrgName, setMspOrgName] = useState(null); // The designated MSP organization
  const [mspSelectorOpen, setMspSelectorOpen] = useState(false); // Show MSP selector dropdown
  const [mspSearchTerm, setMspSearchTerm] = useState(''); // Search term for MSP selector
  const [mspSaving, setMspSaving] = useState(false);
  const [orgSettingsForm, setOrgSettingsForm] = useState({
    is_hidden: false,
    is_msp: false,  // Only one org can be MSP
    ncentral_enabled: true,
    connectwise_enabled: true,
    huntress_enabled: true,
    immybot_enabled: true,
    cloudradial_enabled: true,
    datto_rmm_enabled: false,  // Default to false - requires explicit opt-in
    microsoft_enabled: false,  // Default to false - requires consent per org
    custom_stale_threshold: null,
    primary_rmm: 'global',  // 'global', 'ncentral', or 'datto' - allows per-org override of primary RMM
    notes: '',
    // MacOS settings
    mac_health_checks_enabled: false,
    mac_ncentral_required: false,
    mac_datto_rmm_required: false,
    mac_cloudradial_required: false,
    mac_huntress_required: false,
    mac_connectwise_required: false,
    mac_microsoft_required: false,
    // Mobile device settings
    hide_mobile_devices: true
  });

  // Microsoft consent state
  const [microsoftConsents, setMicrosoftConsents] = useState({});

  const [microsoftTestingTenant, setMicrosoftTestingTenant] = useState(null);
  const [microsoftArchivingStatus, setMicrosoftArchivingStatus] = useState({});
  const [microsoftArchivingSetupLoading, setMicrosoftArchivingSetupLoading] = useState(null);
  const [orgSettingsSaving, setOrgSettingsSaving] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState(new Set()); // For bulk selection
  const [bulkActionSaving, setBulkActionSaving] = useState(false);
  const [dattoRmmFilterWarning, setDattoRmmFilterWarning] = useState(null);
  const [dattoRmmChecking, setDattoRmmChecking] = useState(false);
  const [dattoAuditRefreshing, setDattoAuditRefreshing] = useState(false);
  const [dattoAuditResult, setDattoAuditResult] = useState(null);

  // Microsoft Duplicate Cleanup state
  const [duplicatesScanData, setDuplicatesScanData] = useState(null);
  const [duplicatesScanLoading, setDuplicatesScanLoading] = useState(false);
  const [duplicatesExpandedOrg, setDuplicatesExpandedOrg] = useState(null);
  const [duplicatesExpandedGroup, setDuplicatesExpandedGroup] = useState(null);
  const [duplicatesSelectedDevices, setDuplicatesSelectedDevices] = useState(new Set());
  const [duplicatesDeleting, setDuplicatesDeleting] = useState(false);
  const [duplicatesManageModal, setDuplicatesManageModal] = useState(null); // { hostname, org, devices }
  const [duplicatesOrgFilter, setDuplicatesOrgFilter] = useState(''); // Empty = all orgs
  const [duplicatesSubTab, setDuplicatesSubTab] = useState('desktop'); // 'desktop' or 'mobile'

  // Server-driven verification queue state
  const [verificationSessions, setVerificationSessions] = useState({}); // { unique_id: { status, priority_score, match_result, ... } } — populated from queue-status endpoint
  // verificationResults removed — match results now come from verificationSessions[uid].match_result via queue-status endpoint
  const [verifiedDevices, setVerifiedDevices] = useState({}); // { unique_id: verified_entra_device_id }
  const [queueTotals, setQueueTotals] = useState({ queued: 0, processing: 0, scheduled: 0, verified: 0, failed: 0, expired: 0 });
  const manuallyTriggeredRef = useRef(new Set()); // Track device unique_ids triggered via "Verify Now"

  // Scheduled/background verification data from backend
  const [scheduledVerifications, setScheduledVerifications] = useState([]);

  // Derived: count of devices currently being verified
  const verificationQueueCount = queueTotals.processing + queueTotals.queued;

  // Report verification status to parent (App.js task drawer)
  useEffect(() => {
    if (!onVerificationStatusChange) return;
    onVerificationStatusChange({
      active: queueTotals.processing,
      scheduled: queueTotals.scheduled + queueTotals.queued,
      completed: queueTotals.verified
    });
  }, [queueTotals, onVerificationStatusChange]);

  // Debounced search term for event log
  const [debouncedEventSearch, setDebouncedEventSearch] = useState('');

  // Fetch system info on mount
  useEffect(() => {
    const fetchSystemInfo = async () => {
      try {
        const response = await apiFetch(`${API_URL}/system-info`);
        const data = await response.json();
        if (data.status === 'success') {
          setDatabaseType(data.database_type);
        }
      } catch (error) {
        console.error('Error fetching system info:', error);
      }
    };
    fetchSystemInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodically refresh scheduled verification timing while background checks exist
  useEffect(() => {
    if (scheduledVerifications.length === 0 && !Object.values(verificationSessions).some(s => s.status === 'scheduled')) return;
    const interval = setInterval(() => {
      fetchScheduledVerifications();
    }, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledVerifications.length, verificationSessions]);

  // Debounce the search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      // Reset to page 1 when search changes
      if (eventSearchTerm !== debouncedEventSearch) {
        setCurrentPage(1);
      }
      setDebouncedEventSearch(eventSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSearchTerm]);

  useEffect(() => {
    if (activeTab === 'organizations') {
      fetchOrganizations();
      fetchStats();
      fetchOrgSettings();
    } else if (activeTab === 'eventlog') {
      fetchEvents();
    } else if (activeTab === 'credentials') {
      fetchCredentials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, categoryFilter, sourceFilter, orgFilter, currentPage, debouncedEventSearch]);

  useEffect(() => {
    // Only fetch ConnectWise metadata when user is actually on the PSA sub-tab
    // Skip if data is already loaded (e.g. switching tabs and back)
    if (credentialsStatus.connectwise && activeTab === 'credentials' && credentialsSubTab === 'psa' && cwConfigTypes.length === 0) {
      fetchAllCwMetadata();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialsStatus.connectwise, activeTab, credentialsSubTab]);

  useEffect(() => {
    // Load selected configuration types from credentials when credentials change
    if (credentials.CW_CONFIG_TYPES && cwConfigTypes.length > 0) {
      const selected = credentials.CW_CONFIG_TYPES.split(',').map(t => t.trim().replace(/^'|'$/g, '')).filter(t => t);
      setCwSelectedTypes(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials.CW_CONFIG_TYPES, cwConfigTypes]);

  useEffect(() => {
    // Load selected company types from credentials when credentials change
    if (credentials.CW_COMPANY_TYPE_FILTER && cwCompanyTypes.length > 0) {
      const selected = credentials.CW_COMPANY_TYPE_FILTER.split(',').map(t => t.trim().replace(/^'|'$/g, '')).filter(t => t);
      setCwSelectedCompanyTypes(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials.CW_COMPANY_TYPE_FILTER, cwCompanyTypes]);

  // Load Datto RMM filter settings when credentials change
  useEffect(() => {
    if (credentials.DATTO_RMM_SYNC_ALL_ORGS !== undefined) {
      setDattoSyncAllOrgs(credentials.DATTO_RMM_SYNC_ALL_ORGS !== 'false');
    }
    if (credentials.DATTO_RMM_SITE_FILTER && dattoSites.length > 0) {
      const selected = credentials.DATTO_RMM_SITE_FILTER.split(',').map(t => t.trim()).filter(t => t);
      setDattoSelectedSites(selected);
    }
    if (credentials.DATTO_RMM_DEVICE_TYPE_FILTER && dattoDeviceTypes.length > 0) {
      const selected = credentials.DATTO_RMM_DEVICE_TYPE_FILTER.split(',').map(t => t.trim()).filter(t => t);
      setDattoSelectedDeviceTypes(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials.DATTO_RMM_SYNC_ALL_ORGS, credentials.DATTO_RMM_SITE_FILTER, credentials.DATTO_RMM_DEVICE_TYPE_FILTER, dattoSites, dattoDeviceTypes]);

  // Fetch all Datto RMM metadata in a single cached request (only on Management sub-tab)
  useEffect(() => {
    if (credentialsStatus.datto_rmm && activeTab === 'credentials' && credentialsSubTab === 'management' && dattoSites.length === 0) {
      fetchAllDattoMetadata();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialsStatus.datto_rmm, activeTab, credentialsSubTab]);

  // Fetch general settings when general tab is selected
  useEffect(() => {
    if (activeTab === 'general') {
      fetchGeneralSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch production settings when production tab is selected
  useEffect(() => {
    if (activeTab === 'production') {
      fetchProductionSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch duplicates scan when duplicates tab is selected
  useEffect(() => {
    if (activeTab === 'duplicates') {
      fetchDuplicatesScan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch branding settings when branding tab is selected
  useEffect(() => {
    if (activeTab === 'branding') {
      fetchBrandingSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchOrganizations = async () => {
    try {
      const response = await apiFetch(`${API_URL}/organizations/mappings`);
      const data = await response.json();

      // Check if API returned an error
      if (!response.ok || data.error) {
        console.error('API error fetching organizations:', response.status, data);
        setOrganizations([]);
        setLoading(false);
        return;
      }

      // Ensure we always set an array, even if API returns unexpected data
      setOrganizations(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiFetch(`${API_URL}/organizations/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOrgSettings = async () => {
    try {
      const response = await apiFetch(`${API_URL}/organizations/settings`);
      const data = await response.json();
      if (data.status === 'success') {
        setOrgSettings(data.settings);
        // Find the MSP org from settings
        const mspOrg = Object.entries(data.settings).find(([_, settings]) => settings.is_msp);
        if (mspOrg) {
          setMspOrgName(mspOrg[0]);
        } else {
          setMspOrgName(null);
        }
      }
    } catch (error) {
      console.error('Error fetching org settings:', error);
    }
  };

  // Set an organization as the MSP
  const setMspOrganization = async (orgName) => {
    setMspSaving(true);
    try {
      // Save MSP flag to the selected org (backend will clear from others)
      const response = await apiFetch(`${API_URL}/organizations/settings/${encodeURIComponent(orgName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_msp: true })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setMspOrgName(orgName);
        setMspSelectorOpen(false);
        setMspSearchTerm('');
        // Refresh org settings to reflect the change
        await fetchOrgSettings();
      } else {
        console.error('Failed to set MSP:', data.message);
      }
    } catch (error) {
      console.error('Error setting MSP organization:', error);
    } finally {
      setMspSaving(false);
    }
  };

  // Clear the MSP designation
  const clearMspOrganization = async () => {
    if (!mspOrgName) return;
    setMspSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/organizations/settings/${encodeURIComponent(mspOrgName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_msp: false })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setMspOrgName(null);
        await fetchOrgSettings();
      }
    } catch (error) {
      console.error('Error clearing MSP organization:', error);
    } finally {
      setMspSaving(false);
    }
  };

  // Check if an org inherits MSP status (is a child of the MSP org)
  const isInheritedMsp = (org) => {
    if (!mspOrgName || org.master_org_name === mspOrgName) return false;
    // Check if any source in this org is mapped to the MSP org
    return org.sources?.some(source =>
      source.source_org_name === mspOrgName ||
      (orgSettings[source.source_org_name]?.is_msp)
    );
  };

  const openOrgSettings = async (masterOrgName) => {
    const existing = orgSettings[masterOrgName] || {
      is_hidden: false,
      is_msp: false,
      ncentral_enabled: true,
      connectwise_enabled: true,
      huntress_enabled: true,
      immybot_enabled: true,
      cloudradial_enabled: true,
      datto_rmm_enabled: false,  // Default to false - requires explicit opt-in
      microsoft_enabled: false,  // Default to false - requires consent per org
      custom_stale_threshold: null,
      primary_rmm: 'global',  // Default to global setting
      notes: '',
      // MacOS settings (default to false/disabled)
      mac_health_checks_enabled: false,
      mac_ncentral_required: false,
      mac_datto_rmm_required: false,
      mac_cloudradial_required: false,
      mac_huntress_required: false,
      mac_connectwise_required: false,
      mac_microsoft_required: false,
      // Mobile device settings
      hide_mobile_devices: true
    };
    setOrgSettingsForm({...existing, is_msp: existing.is_msp || false});
    setEditingOrgSettings(masterOrgName);
    setOrgSettingsTab('general'); // Reset to general tab when opening modal
    setDattoRmmFilterWarning(null); // Clear any previous warning

    // Fetch Microsoft consent status for this org
    try {
      const response = await apiFetch(`${API_URL}/microsoft/consent-status?org_name=${encodeURIComponent(masterOrgName)}`);
      const data = await response.json();
      if (data.status === 'success' && data.consents && data.consents.length > 0) {
        setMicrosoftConsents(prev => ({ ...prev, [masterOrgName]: data.consents[0] }));
      }
    } catch (err) {
      console.error('Error fetching Microsoft consent status:', err);
    }

    // Fetch Microsoft archiving status for this org
    try {
      const archiveResponse = await apiFetch(`${API_URL}/microsoft/archiving-status?org_name=${encodeURIComponent(masterOrgName)}`);
      const archiveData = await archiveResponse.json();
      if (archiveData.status === 'success') {
        setMicrosoftArchivingStatus(prev => ({ ...prev, [masterOrgName]: archiveData }));
      }
    } catch (err) {
      console.error('Error fetching Microsoft archiving status:', err);
    }
  };

  const handleDattoRmmToggle = async (checked, masterOrgName) => {
    setOrgSettingsForm(prev => ({...prev, datto_rmm_enabled: checked}));

    if (checked && masterOrgName) {
      // Check if the org's Datto RMM site is in the filter
      setDattoRmmChecking(true);
      try {
        const response = await apiFetch(`${API_URL}/organizations/check-datto-rmm-filter/${encodeURIComponent(masterOrgName)}`);
        const data = await response.json();
        if (data.status === 'success' && !data.in_filter && data.message) {
          setDattoRmmFilterWarning(data.message);
        } else {
          setDattoRmmFilterWarning(null);
        }
      } catch (err) {
        console.error('Failed to check Datto RMM filter:', err);
        setDattoRmmFilterWarning(null);
      } finally {
        setDattoRmmChecking(false);
      }
    } else {
      setDattoRmmFilterWarning(null);
    }
  };

  const saveOrgSettings = async () => {
    if (!editingOrgSettings) return;
    setOrgSettingsSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/organizations/settings/${encodeURIComponent(editingOrgSettings)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orgSettingsForm)
      });
      const data = await response.json();
      if (data.status === 'success') {
        // Update local state
        setOrgSettings(prev => ({
          ...prev,
          [editingOrgSettings]: orgSettingsForm
        }));
        setEditingOrgSettings(null);
        // Notify parent (App.js) to refresh its org settings
        if (onOrgSettingsChange) {
          onOrgSettingsChange();
        }
      } else {
        showToast('Error saving settings: ' + data.message, { type: 'error' });
      }
    } catch (error) {
      console.error('Error saving org settings:', error);
      showToast('Error saving settings', { type: 'error' });
    } finally {
      setOrgSettingsSaving(false);
    }
  };

  // Bulk selection functions
  const toggleOrgSelection = (orgName) => {
    setSelectedOrgs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orgName)) {
        newSet.delete(orgName);
      } else {
        newSet.add(orgName);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrgs.size === filteredOrganizations.length) {
      // Deselect all
      setSelectedOrgs(new Set());
    } else {
      // Select all filtered orgs
      setSelectedOrgs(new Set(filteredOrganizations.map(org => org.master_org_name)));
    }
  };

  const clearSelection = () => {
    setSelectedOrgs(new Set());
  };

  const handleBulkIntegrationChange = async (integration, enabled) => {
    if (selectedOrgs.size === 0) return;

    const confirmMsg = `${enabled ? 'Enable' : 'Disable'} ${integration} for ${selectedOrgs.size} organization(s)?`;
    if (!window.confirm(confirmMsg)) return;

    setBulkActionSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/organizations/settings/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizations: Array.from(selectedOrgs),
          setting: integration,
          value: enabled
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        // Refresh org settings
        await fetchOrgSettings();
        if (onOrgSettingsChange) {
          onOrgSettingsChange();
        }
        // Don't clear selection - user may want to perform more actions
        showToast(`Successfully updated ${data.updated} organization(s)`, { type: 'success' });
      } else {
        showToast('Error: ' + data.message, { type: 'error' });
      }
    } catch (error) {
      console.error('Error bulk updating settings:', error);
      showToast('Error updating settings', { type: 'error' });
    } finally {
      setBulkActionSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrgs.size === 0) return;

    const confirmMsg = `Are you sure you want to DELETE ${selectedOrgs.size} organization(s)?\n\nThis will remove all organization mappings and settings. Source organizations and devices will NOT be deleted but will appear as unmatched.`;
    if (!window.confirm(confirmMsg)) return;

    setBulkActionSaving(true);
    let deleted = 0;
    let failed = 0;

    try {
      for (const orgName of selectedOrgs) {
        try {
          const response = await apiFetch(`${API_URL}/organizations/${encodeURIComponent(orgName)}`, {
            method: 'DELETE'
          });
          const result = await response.json();
          if (result.status === 'success') {
            deleted++;
          } else {
            failed++;
          }
        } catch (error) {
          console.error(`Error deleting ${orgName}:`, error);
          failed++;
        }
      }

      // Refresh data
      await fetchOrganizations();
      await fetchStats();
      await fetchOrgSettings();
      clearSelection();

      if (failed > 0) {
        showToast(`Deleted ${deleted} organization(s). ${failed} failed.`, { type: 'warning' });
      } else {
        showToast(`Successfully deleted ${deleted} organization(s).`, { type: 'success' });
      }
    } catch (error) {
      console.error('Error in bulk delete:', error);
      showToast('Error deleting organizations', { type: 'error' });
    } finally {
      setBulkActionSaving(false);
    }
  };

  const fetchEvents = async () => {
    setEventLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (sourceFilter) params.append('source', sourceFilter);
      if (orgFilter) params.append('organization', orgFilter);
      if (debouncedEventSearch) params.append('search', debouncedEventSearch);
      params.append('limit', itemsPerPage);
      params.append('offset', (currentPage - 1) * itemsPerPage);

      const response = await apiFetch(`${API_URL}/event-log?${params}`);
      const data = await response.json();
      setEvents(data.events || []);
      setEventStats({ total: data.total, limit: data.limit, offset: data.offset });
      setEventLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEventLoading(false);
    }
  };

  const fetchCredentials = async () => {
    setCredentialsLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/credentials`);
      const data = await response.json();
      if (data.status === 'success') {
        setCredentials(data.credentials);
        setCredentialsEnvironment(data.environment || 'local');

        // Set masked fields for credentials that have values (sensitive fields)
        const masked = {};
        if (data.credentials.NABLE_JWT) masked.NABLE_JWT = true;
        if (data.credentials.HUNTRESS_API_KEY) masked.HUNTRESS_API_KEY = true;
        if (data.credentials.HUNTRESS_API_SECRET) masked.HUNTRESS_API_SECRET = true;
        if (data.credentials.CLOUDRADIAL_PUBLIC_KEY) masked.CLOUDRADIAL_PUBLIC_KEY = true;
        if (data.credentials.CLOUDRADIAL_PRIVATE_KEY) masked.CLOUDRADIAL_PRIVATE_KEY = true;
        if (data.credentials.CW_PUBLIC_KEY) masked.CW_PUBLIC_KEY = true;
        if (data.credentials.CW_PRIVATE_KEY) masked.CW_PRIVATE_KEY = true;
        if (data.credentials.AZURE_CLIENT_SECRET) masked.AZURE_CLIENT_SECRET = true;
        if (data.credentials.DATTO_RMM_API_KEY) masked.DATTO_RMM_API_KEY = true;
        if (data.credentials.DATTO_RMM_API_SECRET) masked.DATTO_RMM_API_SECRET = true;
        if (data.credentials.MICROSOFT_CLIENT_SECRET) masked.MICROSOFT_CLIENT_SECRET = true;
        setMaskedFields(masked);
      }

      const statusResponse = await apiFetch(`${API_URL}/credentials/status`);
      const statusData = await statusResponse.json();
      if (statusData.status === 'success') {
        setCredentialsStatus(statusData.credentials);
      }

      setCredentialsLoading(false);
    } catch (error) {
      console.error('Error fetching credentials:', error);
      setCredentialsLoading(false);
    }
  };

  const handleCredentialChange = (key, value) => {
    setCredentials(prev => ({ ...prev, [key]: value }));
  };

  const handleUnmaskField = (fieldKey) => {
    setMaskedFields(prev => ({ ...prev, [fieldKey]: false }));
    setCredentials(prev => ({ ...prev, [fieldKey]: '' }));
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const fetchCWConfigTypes = async () => {
    setCwConfigTypesLoading(true);
    try {
      // Send current form credentials to the API (null for masked fields so backend uses .env)
      const response = await apiFetch(`${API_URL}/connectwise/configuration-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server_url: credentials.CW_SERVER_URL || null,
          company_name: credentials.CW_COMPANY_NAME || null,
          public_key: (credentials.CW_PUBLIC_KEY && !maskedFields.CW_PUBLIC_KEY) ? credentials.CW_PUBLIC_KEY : null,
          private_key: (credentials.CW_PRIVATE_KEY && !maskedFields.CW_PRIVATE_KEY) ? credentials.CW_PRIVATE_KEY : null,
          client_id: credentials.CW_CLIENT_ID || null
        })
      });

      const responseText = await response.text();

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response: ' + responseText.substring(0, 200));
      }
      if (data.status === 'success') {
        setCwConfigTypes(data.types);
        // Load selected types from credentials (comma-separated string)
        if (credentials.CW_CONFIG_TYPES) {
          const selected = credentials.CW_CONFIG_TYPES.split(',').map(t => t.trim()).filter(t => t);
          setCwSelectedTypes(selected);
        }
      } else {
        showToast('Error loading configuration types: ' + data.message, { type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching ConnectWise configuration types:', error);
      showToast('Error loading configuration types: ' + error.message, { type: 'error' });
    } finally {
      setCwConfigTypesLoading(false);
    }
  };

  const toggleCWConfigType = (type) => {
    setCwSelectedTypes(prev => {
      const newSelected = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];
      // Update credentials with comma-separated string
      handleCredentialChange('CW_CONFIG_TYPES', newSelected.join(','));
      return newSelected;
    });
  };

  const fetchCWCompanyTypes = async () => {
    setCwCompanyTypesLoading(true);
    try {
      // Send current form credentials to the API (null for masked fields so backend uses .env)
      const response = await apiFetch(`${API_URL}/connectwise/company-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server_url: credentials.CW_SERVER_URL || null,
          company_name: credentials.CW_COMPANY_NAME || null,
          public_key: (credentials.CW_PUBLIC_KEY && !maskedFields.CW_PUBLIC_KEY) ? credentials.CW_PUBLIC_KEY : null,
          private_key: (credentials.CW_PRIVATE_KEY && !maskedFields.CW_PRIVATE_KEY) ? credentials.CW_PRIVATE_KEY : null,
          client_id: credentials.CW_CLIENT_ID || null
        })
      });

      const responseText = await response.text();

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response: ' + responseText.substring(0, 200));
      }
      if (data.status === 'success') {
        setCwCompanyTypes(data.company_types);
        // Load selected company types from credentials (comma-separated string)
        if (credentials.CW_COMPANY_TYPE_FILTER) {
          const selected = credentials.CW_COMPANY_TYPE_FILTER.split(',').map(t => t.trim()).filter(t => t);
          setCwSelectedCompanyTypes(selected);
        }
      } else {
        showToast('Error loading company types: ' + data.message, { type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching ConnectWise company types:', error);
      showToast('Error loading company types: ' + error.message, { type: 'error' });
    } finally {
      setCwCompanyTypesLoading(false);
    }
  };

  const toggleCWCompanyType = (typeName) => {
    setCwSelectedCompanyTypes(prev => {
      const newSelected = prev.includes(typeName)
        ? prev.filter(t => t !== typeName)
        : [...prev, typeName];
      // Update credentials with comma-separated string
      handleCredentialChange('CW_COMPANY_TYPE_FILTER', newSelected.join(','));
      return newSelected;
    });
  };

  // --- Datto RMM Filtering Functions ---

  // Single bulk request for all Datto RMM metadata (sites + device types + saved filters)
  const fetchAllDattoMetadata = async (refresh = false) => {
    try {
      const url = `${API_URL}/datto-rmm/cached-metadata${refresh ? '?refresh=true' : ''}`;
      const response = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: (credentials.DATTO_RMM_API_KEY && !maskedFields.DATTO_RMM_API_KEY) ? credentials.DATTO_RMM_API_KEY : null,
          api_secret: (credentials.DATTO_RMM_API_SECRET && !maskedFields.DATTO_RMM_API_SECRET) ? credentials.DATTO_RMM_API_SECRET : null,
          api_url: credentials.DATTO_RMM_API_URL || null
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        if (data.sites) setDattoSites(data.sites);
        if (data.device_types) setDattoDeviceTypes(data.device_types);
        // Load saved filter selections
        if (data.site_filter) {
          const selected = data.site_filter.split(',').map(t => t.trim()).filter(t => t);
          setDattoSelectedSites(selected);
        }
        if (data.device_type_filter) {
          const selected = data.device_type_filter.split(',').map(t => t.trim()).filter(t => t);
          setDattoSelectedDeviceTypes(selected);
        }
        if (data.sync_all_orgs !== undefined) {
          setDattoSyncAllOrgs(data.sync_all_orgs !== 'false');
        }
      } else {
        console.error('Error loading Datto RMM metadata:', data.message);
      }
    } catch (error) {
      console.error('Error fetching Datto RMM metadata:', error);
    }
  };

  // Individual fetch functions kept for manual refresh from UI buttons
  const fetchDattoSites = async () => {
    try {
      const response = await apiFetch(`${API_URL}/datto-rmm/sites?refresh=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: (credentials.DATTO_RMM_API_KEY && !maskedFields.DATTO_RMM_API_KEY) ? credentials.DATTO_RMM_API_KEY : null,
          api_secret: (credentials.DATTO_RMM_API_SECRET && !maskedFields.DATTO_RMM_API_SECRET) ? credentials.DATTO_RMM_API_SECRET : null,
          api_url: credentials.DATTO_RMM_API_URL || null
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        setDattoSites(data.sites);
        if (credentials.DATTO_RMM_SITE_FILTER) {
          const selected = credentials.DATTO_RMM_SITE_FILTER.split(',').map(t => t.trim()).filter(t => t);
          setDattoSelectedSites(selected);
        }
      } else {
        console.error('Error loading Datto RMM sites:', data.message);
      }
    } catch (error) {
      console.error('Error fetching Datto RMM sites:', error);
    }
  };

  const fetchDattoDeviceTypes = async () => {
    try {
      const response = await apiFetch(`${API_URL}/datto-rmm/device-types?refresh=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: (credentials.DATTO_RMM_API_KEY && !maskedFields.DATTO_RMM_API_KEY) ? credentials.DATTO_RMM_API_KEY : null,
          api_secret: (credentials.DATTO_RMM_API_SECRET && !maskedFields.DATTO_RMM_API_SECRET) ? credentials.DATTO_RMM_API_SECRET : null,
          api_url: credentials.DATTO_RMM_API_URL || null
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        setDattoDeviceTypes(data.device_types);
        if (credentials.DATTO_RMM_DEVICE_TYPE_FILTER) {
          const selected = credentials.DATTO_RMM_DEVICE_TYPE_FILTER.split(',').map(t => t.trim()).filter(t => t);
          setDattoSelectedDeviceTypes(selected);
        }
      } else {
        console.error('Error loading Datto RMM device types:', data.message);
      }
    } catch (error) {
      console.error('Error fetching Datto RMM device types:', error);
    }
  };

  const toggleDattoSite = (siteId) => {
    setDattoSelectedSites(prev => {
      const newSelected = prev.includes(siteId)
        ? prev.filter(s => s !== siteId)
        : [...prev, siteId];
      handleCredentialChange('DATTO_RMM_SITE_FILTER', newSelected.join(','));
      return newSelected;
    });
  };

  const toggleDattoDeviceType = (deviceType) => {
    setDattoSelectedDeviceTypes(prev => {
      const newSelected = prev.includes(deviceType)
        ? prev.filter(t => t !== deviceType)
        : [...prev, deviceType];
      handleCredentialChange('DATTO_RMM_DEVICE_TYPE_FILTER', newSelected.join(','));
      return newSelected;
    });
  };

  const handleDattoSyncAllOrgsChange = (value) => {
    setDattoSyncAllOrgs(value);
    handleCredentialChange('DATTO_RMM_SYNC_ALL_ORGS', value ? 'true' : 'false');
    // Clear site filter if enabling sync all
    if (value) {
      setDattoSelectedSites([]);
      handleCredentialChange('DATTO_RMM_SITE_FILTER', '');
    }
  };

  // --- Datto RMM Audit Refresh ---
  const handleDattoAuditRefresh = async () => {
    setDattoAuditRefreshing(true);
    setDattoAuditResult(null);
    try {
      const response = await apiFetch(`${API_URL}/sync-source/datto_rmm/refresh-audit`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDattoAuditResult({ success: true, message: data.message || 'Audit data refreshed successfully' });
      } else {
        setDattoAuditResult({ success: false, message: data.message || 'Failed to refresh audit data' });
      }
    } catch (error) {
      console.error('Error refreshing Datto RMM audit data:', error);
      setDattoAuditResult({ success: false, message: 'Error connecting to server' });
    } finally {
      setDattoAuditRefreshing(false);
      // Clear result after 5 seconds
      setTimeout(() => setDattoAuditResult(null), 5000);
    }
  };

  // --- Ticketing Configuration Functions ---

  const fetchAllCwMetadata = async (refresh = false) => {
    try {
      const url = `${API_URL}/connectwise/cached-metadata${refresh ? '?refresh=true' : ''}`;
      const response = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server_url: credentials.CW_SERVER_URL || null,
          company_name: credentials.CW_COMPANY_NAME || null,
          public_key: (credentials.CW_PUBLIC_KEY && !maskedFields.CW_PUBLIC_KEY) ? credentials.CW_PUBLIC_KEY : null,
          private_key: (credentials.CW_PRIVATE_KEY && !maskedFields.CW_PRIVATE_KEY) ? credentials.CW_PRIVATE_KEY : null,
          client_id: credentials.CW_CLIENT_ID || null
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        // Populate all state in one shot
        if (data.configuration_types) setCwConfigTypes(data.configuration_types);
        if (data.company_types) setCwCompanyTypes(data.company_types);
        if (data.ticketing) {
          setTicketingSettings(data.ticketing);
          if (data.ticketing.board_id) {
            setTicketingConfiguring(true);
          }
        }
        if (data.boards && data.boards.length > 0) setCwBoards(data.boards);
        if (data.priorities && data.priorities.length > 0) setCwPriorities(data.priorities);
        if (data.statuses && data.statuses.length > 0) setCwBoardStatuses(data.statuses);
        if (data.types && data.types.length > 0) setCwBoardTypes(data.types);
        if (data.subtypes && data.subtypes.length > 0) setCwBoardSubtypes(data.subtypes);
        if (data.items && data.items.length > 0) setCwBoardItems(data.items);
      }
    } catch (error) {
      console.error('Error fetching ConnectWise metadata:', error);
    }
  };

  const fetchGeneralSettings = async () => {
    setGeneralSettingsLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/settings/general`);
      const data = await response.json();
      if (data.status === 'success') {
        setGeneralSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching general settings:', error);
    } finally {
      setGeneralSettingsLoading(false);
    }
  };

  const saveGeneralSettings = async () => {
    setGeneralSettingsSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/settings/general`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(generalSettings)
      });
      const data = await response.json();
      if (data.status === 'success') {
        showToast('Settings saved successfully!', { type: 'success' });
      } else {
        showToast('Error saving settings: ' + data.message, { type: 'error' });
      }
    } catch (error) {
      console.error('Error saving general settings:', error);
      showToast('Error saving settings', { type: 'error' });
    } finally {
      setGeneralSettingsSaving(false);
    }
  };

  // Branding / Whitelabel Settings
  const fetchBrandingSettings = async () => {
    setBrandingLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/settings/branding`);
      const data = await response.json();
      if (data.status === 'success') {
        setBrandingSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching branding settings:', error);
    } finally {
      setBrandingLoading(false);
    }
  };

  const saveBrandingSettings = async () => {
    setBrandingSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/settings/branding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(brandingSettings)
      });
      const data = await response.json();
      if (data.status === 'success') {
        alert('Branding settings saved successfully!');
      } else {
        alert('Error saving branding settings: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving branding settings:', error);
      alert('Error saving branding settings');
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleLogoUpload = (field, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG, SVG, etc.)');
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 512000) {
      alert('Image must be under 500KB. Please use a smaller or compressed image.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setBrandingSettings(prev => ({ ...prev, [field]: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const clearBrandingField = (field) => {
    setBrandingSettings(prev => ({ ...prev, [field]: '' }));
  };

  const resetBrandingSettings = () => {
    if (window.confirm('Reset all branding to defaults? This will remove your custom logo, name, and colors.')) {
      setBrandingSettings({
        app_name: '',
        logo_dark: '',
        logo_light: '',
        favicon: '',
        primary_color: '',
        accent_color: '',
      });
    }
  };

  // Production Settings (v2.5+)
  const fetchProductionSettings = async () => {
    setProductionSettingsLoading(true);
    setProductionAccessDenied(false);
    try {
      const response = await apiFetch(`${API_URL}/settings/production`);

      // Handle 403 Forbidden (missing Admin role)
      if (response.status === 403) {
        setProductionAccessDenied(true);
        const data = await response.json();
        console.error('Access denied to production settings:', data.error);
        if (data.required_role) {
          setProductionRequiredRole(data.required_role);
        }
        return;
      }

      const data = await response.json();
      if (data.status === 'success') {
        setProductionSettings(data.settings);
        setProductionBackend(data.backend);
        setProductionVersion({ version: data.version, codename: data.codename });
        setProductionRequiredRole(data.required_role || 'Admin');
        if (data.roles) {
          setProductionRoles(data.roles);
        }
      }
    } catch (error) {
      console.error('Error fetching production settings:', error);
    } finally {
      setProductionSettingsLoading(false);
    }
  };

  const saveProductionSettings = async () => {
    // SECURITY: Require explicit confirmation for production settings
    const confirmed = window.confirm(
      'WARNING: You are about to change production security settings.\n\n' +
      'These changes affect:\n' +
      '- CORS (which domains can access the API)\n' +
      '- Rate Limiting (API abuse protection)\n\n' +
      'Changes require an application restart to take effect.\n\n' +
      'Are you sure you want to save these settings?'
    );

    if (!confirmed) {
      return;
    }

    setProductionSettingsSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/settings/production`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...productionSettings, confirm: true })
      });
      const data = await response.json();
      if (data.status === 'success') {
        showToast(data.message, { type: 'success', subtitle: 'Application restart required for changes to take effect.' });
      } else {
        showToast('Error saving settings: ' + data.message, { type: 'error' });
      }
    } catch (error) {
      console.error('Error saving production settings:', error);
      showToast('Error saving settings', { type: 'error' });
    } finally {
      setProductionSettingsSaving(false);
    }
  };

  // Fetch Microsoft duplicates scan
  const fetchDuplicatesScan = async (orgFilter = null) => {
    setDuplicatesScanLoading(true);
    try {
      // Use provided org filter or fall back to state
      const org = orgFilter !== null ? orgFilter : duplicatesOrgFilter;
      const url = org
        ? `${API_URL}/microsoft/duplicates/scan?organization=${encodeURIComponent(org)}`
        : `${API_URL}/microsoft/duplicates/scan`;
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.status === 'success') {
        setDuplicatesScanData(data);

        // Fetch any existing scheduled/background verifications
        fetchScheduledVerifications();

        // Verification is now handled server-side: duplicates are auto-queued
        // after Microsoft import, and processed in batches by the backend.
        // The pollQueueStatus() effect handles UI updates every 20 seconds.
      } else {
        console.error('Error scanning duplicates:', data.message);
      }
    } catch (error) {
      console.error('Error fetching duplicates scan:', error);
    } finally {
      setDuplicatesScanLoading(false);
    }
  };

  // Fetch scheduled/background verification data from backend
  const fetchScheduledVerifications = async () => {
    try {
      const response = await apiFetch(`${API_URL}/microsoft/verification/pending`);
      const data = await response.json();
      if (data.status === 'success') {
        setScheduledVerifications(data.pending_verifications || []);
      }
    } catch (error) {
      console.error('Error fetching scheduled verifications:', error);
    }
  };

  // Delete selected duplicate devices
  const deleteDuplicates = async (deviceIds, reason = 'Duplicate device cleanup') => {
    if (!deviceIds || deviceIds.length === 0) return;

    setDuplicatesDeleting(true);
    try {
      const response = await apiFetch(`${API_URL}/microsoft/duplicates/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unique_ids: deviceIds,
          reason: reason
          // PSA ticket is now mandatory - no create_ticket flag needed
        })
      });
      const data = await response.json();

      if (data.ticket_error) {
        // PSA ticket creation failed - cleanup aborted
        showToast('PSA Ticket Creation Failed', { type: 'error', subtitle: `Cleanup aborted. Error: ${data.ticket_error}. Check ConnectWise ticketing configuration in Settings.`, duration: 8000 });
        return data;
      }

      if (data.success_count > 0) {
        let message = `Successfully cleaned up ${data.success_count} of ${deviceIds.length} duplicate devices.`;
        let hasSkipped = false;
        // Add details about Entra archiving/skipping
        if (data.results) {
          const archived = data.results.filter(r => r.entra_archived).length;
          const skipped = data.results.filter(r => r.entra_skipped).length;
          if (archived > 0) message += ` ${archived} Entra object(s) archived.`;
          if (skipped > 0) { message += ` ${skipped} Entra archive(s) skipped (safety check).`; hasSkipped = true; }
        }
        const subtitle = data.ticket_id ? `PSA Ticket #${data.ticket_id} has been created.` : undefined;
        showToast(message, { type: 'success', subtitle, duration: hasSkipped ? 8000 : undefined });
        // Clear selections and refresh
        setDuplicatesSelectedDevices(new Set());
        setDuplicatesManageModal(null);
        fetchDuplicatesScan();
      } else {
        showToast(`Failed to clean up devices: ${data.message}`, { type: 'error' });
      }

      return data;
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      showToast('Error cleaning up duplicates: ' + error.message, { type: 'error' });
    } finally {
      setDuplicatesDeleting(false);
    }
  };

  // Trigger Entra device verification via ImmyBot
  const triggerVerifyNow = async (device) => {
    if (!device.computer_id) {
      showToast('Cannot verify: Device has no ImmyBot computer_id', { type: 'warning' });
      return;
    }

    const uniqueId = device.unique_id;

    // Optimistic UI: show as triggering
    setVerificationSessions(prev => ({
      ...prev,
      [uniqueId]: { ...prev[uniqueId], status: 'triggering' }
    }));

    try {
      const response = await apiFetch(`${API_URL}/microsoft/verify-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          computer_id: device.computer_id,
          device_unique_id: uniqueId
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        showToast('Verification triggered — results will appear shortly', { type: 'success' });
        manuallyTriggeredRef.current.add(uniqueId);
        setVerificationSessions(prev => ({
          ...prev,
          [uniqueId]: { status: 'running', session_group_id: data.session_group_id }
        }));
      } else if (data.status === 'already_verified') {
        showToast('Device is already verified', { type: 'info' });
        setVerificationSessions(prev => ({
          ...prev,
          [uniqueId]: { status: 'verified', verified_entra_device_id: data.verified_entra_device_id }
        }));
      } else if (data.status === 'already_pending') {
        showToast('Verification already in progress', { type: 'info' });
        setVerificationSessions(prev => ({
          ...prev,
          [uniqueId]: { status: 'running' }
        }));
      } else {
        showToast(data.message || 'Failed to trigger verification', { type: 'error' });
        setVerificationSessions(prev => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });
      }
    } catch (error) {
      console.error('Error triggering verification:', error);
      showToast('Error triggering verification', { type: 'error' });
      setVerificationSessions(prev => {
        const updated = { ...prev };
        delete updated[uniqueId];
        return updated;
      });
    }
  };

  // --- Server-driven verification queue: single lightweight poller ---
  // Replaces per-device polling with a single GET every 20 seconds.
  // The backend handles all ImmyBot session polling and match logic.

  const pollQueueStatus = useCallback(async () => {
    // Gather all unique_ids visible in scan data or current modal
    const uniqueIds = new Set();
    if (duplicatesManageModal?.devices) {
      duplicatesManageModal.devices.forEach(d => uniqueIds.add(d.unique_id));
    } else if (duplicatesScanData?.organizations) {
      duplicatesScanData.organizations.forEach(org => {
        org.groups?.forEach(group => {
          group.devices?.forEach(d => uniqueIds.add(d.unique_id));
        });
      });
    }

    if (uniqueIds.size === 0) return;

    try {
      const idsParam = Array.from(uniqueIds).join(',');
      const response = await apiFetch(
        `${API_URL}/microsoft/verification/queue-status?unique_ids=${encodeURIComponent(idsParam)}`
      );
      const data = await response.json();
      if (data.status === 'success') {
        setQueueTotals(data.totals || { queued: 0, processing: 0, scheduled: 0, verified: 0, failed: 0, expired: 0 });

        // Update verificationSessions from backend device statuses
        if (data.devices) {
          setVerificationSessions(prev => {
            const updated = { ...prev };
            for (const [uid, info] of Object.entries(data.devices)) {
              const backendStatus = info.status;
              let uiStatus = 'unverified';
              if (backendStatus === 'Queued') uiStatus = 'queued';
              else if (backendStatus === 'VerificationPending') uiStatus = 'running';
              else if (backendStatus === 'ScheduledPending') uiStatus = 'scheduled';
              else if (backendStatus === 'Verified') uiStatus = 'verified';
              else if (backendStatus === 'Expired') uiStatus = 'expired';
              else if (backendStatus === 'Cancelled') uiStatus = 'cancelled';

              updated[uid] = {
                ...updated[uid],
                status: uiStatus,
                verified_entra_device_id: info.verified_entra_device_id,
                match_result: info.match_result,
                priority_score: info.priority_score,
              };
            }
            return updated;
          });

          // Update verifiedDevices map + toast for manually triggered completions
          for (const [uid, info] of Object.entries(data.devices)) {
            if (info.verified_entra_device_id) {
              setVerifiedDevices(prev => ({ ...prev, [uid]: info.verified_entra_device_id }));
            }
            // Show toast when a manually triggered verification completes
            if (manuallyTriggeredRef.current.has(uid)) {
              const backendStatus = info.status;
              if (backendStatus === 'Verified' && info.match_result) {
                const label = info.match_result.is_active ? 'Active' : 'Stale';
                const method = info.match_result.match_method === 'thumbprint' ? 'Thumbprint'
                  : info.match_result.match_method === 'activity' ? 'Activity' : 'Device ID';
                showToast(`Verification complete: ${label} (matched via ${method})`, { type: info.match_result.is_active ? 'success' : 'warning' });
                manuallyTriggeredRef.current.delete(uid);
              } else if (backendStatus === 'Expired' || backendStatus === 'Cancelled' || backendStatus === 'Unverified') {
                showToast('Verification failed — device may be offline', { type: 'error' });
                manuallyTriggeredRef.current.delete(uid);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error polling queue status:', error);
    }
  }, [duplicatesManageModal, duplicatesScanData, API_URL, apiFetch, showToast]);

  // Poll every 20 seconds when duplicates tab is active
  useEffect(() => {
    if (activeTab !== 'duplicates') return;
    const interval = setInterval(pollQueueStatus, 20000);
    pollQueueStatus(); // immediate first call
    return () => clearInterval(interval);
  }, [activeTab, pollQueueStatus]);

  // saveVerifiedDevice removed — backend handles match + save via process_pending_verifications()

  // Get verification status indicator for a device
  const getVerificationStatus = (device) => {
    const uniqueId = device.unique_id;
    const session = verificationSessions[uniqueId];

    // 1. Check live session status from queue-status poller
    if (session) {
      if (session.status === 'triggering' || session.status === 'running') {
        return { status: 'verifying', label: '⏳ Verifying...', color: '#f59e0b' };
      }
      if (session.status === 'queued') {
        return { status: 'queued', label: '🕐 Queued', color: '#3b82f6' };
      }
      if (session.status === 'scheduled') {
        return { status: 'scheduled', label: '📅 Scheduled', color: '#8b5cf6' };
      }
      // Check match_result from server-side match logic
      if (session.match_result) {
        if (session.match_result.is_active) {
          const method = session.match_result.match_method === 'thumbprint' ? 'Thumbprint'
            : session.match_result.match_method === 'activity' ? 'Activity' : 'Device ID';
          return { status: 'verified', label: `✅ Active (${method})`, color: '#22c55e' };
        } else {
          return { status: 'stale', label: '🗑️ Stale', color: '#ef4444' };
        }
      }
      if (session.status === 'verified') {
        return { status: 'verified', label: '✅ Verified', color: '#22c55e' };
      }
      if (session.status === 'expired') {
        return { status: 'expired', label: '⏰ Expired', color: '#6b7280' };
      }
      if (session.status === 'cancelled') {
        return { status: 'cancelled', label: '❌ Cancelled', color: '#6b7280' };
      }
    }

    // 2. Check stored verification
    if (verifiedDevices[uniqueId]) {
      return { status: 'verified', label: '✅ Verified', color: '#22c55e' };
    }

    // 3. Check device's verification_status from backend scan data
    if (device.verification_status) {
      switch (device.verification_status) {
        case 'Queued':
          return { status: 'queued', label: '🕐 Queued', color: '#3b82f6' };
        case 'Verified':
          return { status: 'verified', label: '✅ Verified', color: '#22c55e' };
        case 'VerificationPending':
          return { status: 'verifying', label: '⏳ Verifying...', color: '#f59e0b' };
        case 'ScheduledPending':
          return { status: 'scheduled', label: '📅 Scheduled', color: '#8b5cf6' };
        case 'ReverificationRequired':
          return { status: 'reverify', label: '🔄 Re-verify', color: '#f97316' };
        case 'Cancelled':
          return { status: 'cancelled', label: '❌ Cancelled', color: '#6b7280' };
        case 'Expired':
          return { status: 'expired', label: '⏰ Expired', color: '#6b7280' };
        default:
          break;
      }
    }

    // 4. Check if device has a microsoft_device_id that we can compare
    if (device.microsoft_device_id && device.verified_entra_device_id) {
      if (device.microsoft_device_id === device.verified_entra_device_id) {
        return { status: 'verified', label: '✅ Verified', color: '#22c55e' };
      } else {
        return { status: 'mismatch', label: '⚠️ ID Mismatch', color: '#f59e0b' };
      }
    }

    return { status: 'unverified', label: '❓ Unverified', color: '#6b7280' };
  };

  // Helper to format days since checkin
  const formatDaysSince = (days) => {
    if (days === null || days === undefined) return 'Never';
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  // Helper to format device date display for duplicates
  // Shows ApproximateLastSignInDateTime as primary decision-making data
  const formatDeviceDate = (device, showFullDate = false) => {
    const lastCheckin = device.last_checkin;
    const registrationDate = device.registration_date;
    let daysSince = device.days_since_checkin;

    // If we have a valid last_checkin date (ApproximateLastSignInDateTime)
    if (lastCheckin && lastCheckin !== 'N/A' && lastCheckin !== '0000' && lastCheckin !== '' && lastCheckin !== 'Never') {
      try {
        const date = new Date(lastCheckin);
        const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const timeFormatted = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

        // Calculate days since if backend didn't provide it
        if (daysSince === null || daysSince === undefined) {
          const now = new Date();
          daysSince = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        }

        const daysText = formatDaysSince(daysSince);

        return (
          <div style={{ lineHeight: '1.3' }}>
            <div style={{ fontWeight: '500', color: daysSince <= 7 ? '#22c55e' : daysSince <= 30 ? '#f59e0b' : '#ef4444' }}>
              {daysText}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {formatted} {timeFormatted}
            </div>
          </div>
        );
      } catch {
        return formatDaysSince(daysSince);
      }
    }

    // If we have registration_date but no activity, show it with a warning
    if (registrationDate && registrationDate !== 'N/A' && registrationDate !== '0000' && registrationDate !== '') {
      try {
        const date = new Date(registrationDate);
        const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return (
          <div style={{ lineHeight: '1.3' }} title="No sign-in activity recorded - only registration date available">
            <div style={{ color: '#6b7280', fontWeight: '500' }}>
              ⚠ No Activity
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              Registered: {formatted}
            </div>
          </div>
        );
      } catch {
        return <span style={{ color: 'var(--text-secondary)' }}>Registered: {registrationDate.split('T')[0]}</span>;
      }
    }

    // No date available
    return <span style={{ color: '#ef4444', fontWeight: '500' }}>⚠ Never</span>;
  };

  const fetchCwBoards = async () => {
    setCwBoardsLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/connectwise/service-boards`);
      const data = await response.json();
      if (data.status === 'success') {
        setCwBoards(data.boards);
      }
    } catch (error) {
      console.error('Error fetching service boards:', error);
    } finally {
      setCwBoardsLoading(false);
    }
  };

  const fetchCwBoardStatuses = async (boardId) => {
    if (!boardId) return;
    setCwStatusesLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/connectwise/service-boards/${boardId}/statuses`);
      const data = await response.json();
      if (data.status === 'success') {
        setCwBoardStatuses(data.statuses);
      }
    } catch (error) {
      console.error('Error fetching board statuses:', error);
    } finally {
      setCwStatusesLoading(false);
    }
  };

  const fetchCwBoardTypes = async (boardId) => {
    if (!boardId) return;
    setCwTypesLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/connectwise/service-boards/${boardId}/types`);
      const data = await response.json();
      if (data.status === 'success') {
        setCwBoardTypes(data.types);
      }
    } catch (error) {
      console.error('Error fetching board types:', error);
    } finally {
      setCwTypesLoading(false);
    }
  };

  const fetchCwBoardSubtypes = async (boardId, typeId = null) => {
    if (!boardId) return;
    setCwSubtypesLoading(true);
    try {
      let url = `${API_URL}/connectwise/service-boards/${boardId}/subtypes`;
      if (typeId) {
        url += `?type_id=${typeId}`;
      }
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.status === 'success') {
        setCwBoardSubtypes(data.subtypes);
      }
    } catch (error) {
      console.error('Error fetching board subtypes:', error);
    } finally {
      setCwSubtypesLoading(false);
    }
  };

  const fetchCwBoardItems = async (boardId, subtypeId = null) => {
    if (!boardId) return;
    setCwItemsLoading(true);
    try {
      let url = `${API_URL}/connectwise/service-boards/${boardId}/items`;
      if (subtypeId) {
        url += `?subtype_id=${subtypeId}`;
      }
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.status === 'success') {
        setCwBoardItems(data.items);
      }
    } catch (error) {
      console.error('Error fetching board items:', error);
    } finally {
      setCwItemsLoading(false);
    }
  };

  const fetchCwPriorities = async () => {
    setCwPrioritiesLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/connectwise/service/priorities`);
      const data = await response.json();
      if (data.status === 'success') {
        setCwPriorities(data.priorities);
      }
    } catch (error) {
      console.error('Error fetching priorities:', error);
    } finally {
      setCwPrioritiesLoading(false);
    }
  };

  const startTicketingConfiguration = async () => {
    setTicketingConfiguring(true);
    // Fetch boards and priorities when user starts configuring
    await Promise.all([fetchCwBoards(), fetchCwPriorities()]);
  };

  const handleTicketingChange = (key, value, name = null) => {
    setTicketingSettings(prev => {
      const updated = { ...prev, [key]: value };
      if (name) {
        updated[key.replace('_id', '_name')] = name;
      }
      return updated;
    });
  };

  const handleBoardChange = (boardId, boardName) => {
    handleTicketingChange('board_id', boardId, boardName);
    // Clear dependent selections and reload data
    setTicketingSettings(prev => ({
      ...prev,
      board_id: boardId,
      board_name: boardName,
      type_id: '', type_name: '',
      subtype_id: '', subtype_name: '',
      item_id: '', item_name: '',
      open_status_id: '', open_status_name: '',
      closed_status_id: '', closed_status_name: ''
    }));
    // Clear dependent dropdowns
    setCwBoardSubtypes([]);
    setCwBoardItems([]);
    if (boardId) {
      fetchCwBoardStatuses(boardId);
      fetchCwBoardTypes(boardId);
      // Don't fetch subtypes/items until type is selected
    }
  };

  const handleTypeChange = (typeId, typeName) => {
    // Update type and clear dependent selections
    setTicketingSettings(prev => ({
      ...prev,
      type_id: typeId,
      type_name: typeName,
      subtype_id: '', subtype_name: '',
      item_id: '', item_name: ''
    }));
    // Clear dependent dropdowns
    setCwBoardSubtypes([]);
    setCwBoardItems([]);
    if (typeId && ticketingSettings.board_id) {
      fetchCwBoardSubtypes(ticketingSettings.board_id, typeId);
    }
  };

  const handleSubtypeChange = (subtypeId, subtypeName) => {
    // Update subtype and clear dependent selections
    setTicketingSettings(prev => ({
      ...prev,
      subtype_id: subtypeId,
      subtype_name: subtypeName,
      item_id: '', item_name: ''
    }));
    // Clear dependent dropdown
    setCwBoardItems([]);
    if (subtypeId && ticketingSettings.board_id) {
      fetchCwBoardItems(ticketingSettings.board_id, subtypeId);
    }
  };

  const saveTicketingSettings = async () => {
    setTicketingSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/settings/ticketing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketingSettings)
      });
      const data = await response.json();
      if (data.status === 'success') {
        showToast('Ticketing settings saved successfully!', { type: 'success' });
      } else {
        showToast('Error saving ticketing settings: ' + data.message, { type: 'error' });
      }
    } catch (error) {
      console.error('Error saving ticketing settings:', error);
      showToast('Error saving ticketing settings', { type: 'error' });
    }
    setTicketingSaving(false);
  };

  const testTicketCreation = async () => {
    setTicketingLoading(true);
    setTicketingTestResult(null);
    try {
      const response = await apiFetch(`${API_URL}/connectwise/tickets/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          board_id: parseInt(ticketingSettings.board_id),
          open_status_id: parseInt(ticketingSettings.open_status_id),
          type_id: ticketingSettings.type_id ? parseInt(ticketingSettings.type_id) : null,
          subtype_id: ticketingSettings.subtype_id ? parseInt(ticketingSettings.subtype_id) : null,
          item_id: ticketingSettings.item_id ? parseInt(ticketingSettings.item_id) : null,
          priority_id: ticketingSettings.priority_id ? parseInt(ticketingSettings.priority_id) : null
        })
      });
      const data = await response.json();
      setTicketingTestResult(data);
      // Auto-clear result after 10 seconds
      setTimeout(() => setTicketingTestResult(null), 10000);
    } catch (error) {
      console.error('Error testing ticket creation:', error);
      setTicketingTestResult({ status: 'error', message: error.message });
    }
    setTicketingLoading(false);
  };

  const refreshCwCache = async () => {
    setCwCacheRefreshing(true);
    try {
      // Single request: clears cache and re-fetches everything from CW API
      await fetchAllCwMetadata(true);
    } catch (error) {
      console.error('Error refreshing ConnectWise cache:', error);
    } finally {
      setCwCacheRefreshing(false);
    }
  };

  const handleSaveCredentials = async () => {
    setSavingCredentials(true);
    try {
      const response = await apiFetch(`${API_URL}/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();
      if (data.status === 'success') {
        showToast(`Successfully updated ${data.updated.length} credential(s)`, { type: 'success' });
        await fetchCredentials(); // Refresh
      } else {
        showToast('Error saving credentials: ' + JSON.stringify(data.failed), { type: 'error' });
      }
    } catch (error) {
      console.error('Error saving credentials:', error);
      showToast('Error saving credentials', { type: 'error' });
    }
    setSavingCredentials(false);
  };

  const handleTestConnection = async (source) => {
    setTestingSource(source);
    try {
      // Build request body with current credentials
      // Send null for masked fields so backend uses stored credentials
      const requestBody = {};
      if (source === 'ncentral') {
        requestBody.NABLE_JWT = maskedFields.NABLE_JWT ? null : credentials.NABLE_JWT;
        requestBody.NABLE_URL = credentials.NABLE_URL;
      } else if (source === 'huntress') {
        requestBody.HUNTRESS_API_KEY = maskedFields.HUNTRESS_API_KEY ? null : credentials.HUNTRESS_API_KEY;
        requestBody.HUNTRESS_API_SECRET = maskedFields.HUNTRESS_API_SECRET ? null : credentials.HUNTRESS_API_SECRET;
      } else if (source === 'immybot') {
        requestBody.IMMYBOT_SUBDOMAIN = credentials.IMMYBOT_SUBDOMAIN;
        requestBody.IMMYBOT_TAKE = credentials.IMMYBOT_TAKE;
      } else if (source === 'cloudradial') {
        requestBody.CLOUDRADIAL_PUBLIC_KEY = maskedFields.CLOUDRADIAL_PUBLIC_KEY ? null : credentials.CLOUDRADIAL_PUBLIC_KEY;
        requestBody.CLOUDRADIAL_PRIVATE_KEY = maskedFields.CLOUDRADIAL_PRIVATE_KEY ? null : credentials.CLOUDRADIAL_PRIVATE_KEY;
        requestBody.CLOUDRADIAL_BASE_URL = credentials.CLOUDRADIAL_BASE_URL;
      } else if (source === 'connectwise') {
        requestBody.CW_SERVER_URL = credentials.CW_SERVER_URL;
        requestBody.CW_COMPANY_NAME = credentials.CW_COMPANY_NAME;
        requestBody.CW_PUBLIC_KEY = maskedFields.CW_PUBLIC_KEY ? null : credentials.CW_PUBLIC_KEY;
        requestBody.CW_PRIVATE_KEY = maskedFields.CW_PRIVATE_KEY ? null : credentials.CW_PRIVATE_KEY;
        requestBody.CW_CLIENT_ID = credentials.CW_CLIENT_ID;
      } else if (source === 'datto_rmm') {
        requestBody.DATTO_RMM_API_URL = credentials.DATTO_RMM_API_URL;
        requestBody.DATTO_RMM_API_KEY = maskedFields.DATTO_RMM_API_KEY ? null : credentials.DATTO_RMM_API_KEY;
        requestBody.DATTO_RMM_API_SECRET = maskedFields.DATTO_RMM_API_SECRET ? null : credentials.DATTO_RMM_API_SECRET;
      } else if (source === 'microsoft') {
        requestBody.MICROSOFT_CLIENT_ID = credentials.MICROSOFT_CLIENT_ID;
        requestBody.MICROSOFT_CLIENT_SECRET = maskedFields.MICROSOFT_CLIENT_SECRET ? null : credentials.MICROSOFT_CLIENT_SECRET;
        requestBody.MICROSOFT_TENANT_ID = credentials.MICROSOFT_TENANT_ID;
      }

      const response = await apiFetch(`${API_URL}/credentials/test/${source}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      setTestResults(prev => ({ ...prev, [source]: data }));

      // Auto-fade the result after 8 seconds, then clear after 10 seconds
      setTimeout(() => {
        setTestResults(prev => prev[source] ? { ...prev, [source]: { ...prev[source], fading: true } } : prev);
      }, 8000);
      setTimeout(() => {
        setTestResults(prev => {
          const newResults = { ...prev };
          delete newResults[source];
          return newResults;
        });
      }, 10000);
    } catch (error) {
      console.error(`Error testing ${source}:`, error);
      setTestResults(prev => ({ ...prev, [source]: { success: false, error: 'Connection failed' } }));

      // Auto-fade errors too
      setTimeout(() => {
        setTestResults(prev => prev[source] ? { ...prev, [source]: { ...prev[source], fading: true } } : prev);
      }, 8000);
      setTimeout(() => {
        setTestResults(prev => {
          const newResults = { ...prev };
          delete newResults[source];
          return newResults;
        });
      }, 10000);
    }
    setTestingSource(null);
  };

  const handleRematch = async () => {
    setRematching(true);
    try {
      const response = await apiFetch(`${API_URL}/organizations/rematch`, {
        method: 'POST'
      });
      const result = await response.json();
      if (result.status === 'success') {
        await fetchOrganizations();
        await fetchStats();
        showToast('Fuzzy matching complete!', { type: 'success', subtitle: `Matched: ${result.stats.matched}, Auto mappings: ${result.stats.auto_mappings}` });
      }
    } catch (error) {
      console.error('Error rematching:', error);
      showToast('Error running fuzzy matching', { type: 'error' });
    }
    setRematching(false);
  };

  const handleUnmatch = async (source, sourceOrgId) => {
    if (!window.confirm('Are you sure you want to unmatch this organization?')) {
      return;
    }

    try {
      const response = await apiFetch(`${API_URL}/organizations/unmatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, source_org_id: sourceOrgId })
      });

      const result = await response.json();
      if (result.status === 'success') {
        await fetchOrganizations();
        await fetchStats();
      } else {
        showToast('Error unmatching organization', { type: 'error' });
      }
    } catch (error) {
      console.error('Error unmatching:', error);
      showToast('Error unmatching organization', { type: 'error' });
    }
  };

  const handleDeleteOrganization = async (masterOrgName) => {
    if (!window.confirm(`Are you sure you want to delete "${masterOrgName}"?\n\nThis will remove all organization mappings and settings. Source organizations and devices will NOT be deleted but will appear as unmatched.`)) {
      return;
    }

    try {
      const response = await apiFetch(`${API_URL}/organizations/${encodeURIComponent(masterOrgName)}`, {
        method: 'DELETE'
      });

      const result = await response.json();
      if (result.status === 'success') {
        await fetchOrganizations();
        await fetchStats();
        await fetchOrgSettings();
      } else {
        showToast(`Error deleting organization: ${result.message}`, { type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      showToast('Error deleting organization', { type: 'error' });
    }
  };

  const handleManualMatch = async (source, sourceOrgId, sourceName) => {
    const masterName = window.prompt(
      `Enter the master organization name to match "${sourceName}" to:`,
      sourceName
    );

    if (!masterName) return;

    try {
      const response = await apiFetch(`${API_URL}/organizations/manual-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source,
          source_org_id: sourceOrgId,
          master_org_name: masterName
        })
      });

      const result = await response.json();
      if (result.status === 'success') {
        await fetchOrganizations();
        await fetchStats();
      } else {
        showToast('Error creating manual mapping', { type: 'error' });
      }
    } catch (error) {
      console.error('Error creating manual mapping:', error);
      showToast('Error creating manual mapping', { type: 'error' });
    }
  };

  const filteredOrganizations = organizations.filter(org => {
    // Search filter
    if (searchTerm && !org.master_org_name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Type filter
    if (filterType === 'matched' && !org.has_matches) {
      return false;
    }
    if (filterType === 'unmatched' && org.has_matches) {
      return false;
    }
    if (filterType === 'manual' && !org.sources.some(s => s.is_manual)) {
      return false;
    }

    return true;
  }).sort((a, b) => {
    // Apply sorting
    let aVal, bVal;

    if (sortField === 'master_org_name') {
      aVal = a.master_org_name.toLowerCase();
      bVal = b.master_org_name.toLowerCase();
    } else if (sortField === 'total_devices') {
      aVal = a.total_devices;
      bVal = b.total_devices;
    } else if (sortField === 'sources') {
      aVal = a.sources.length;
      bVal = b.sources.length;
    } else {
      return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const toggleExpand = (orgName) => {
    setExpandedOrg(expandedOrg === orgName ? null : orgName);
  };

  // Get all matched pairs with their confidence scores
  const getMatchedPairs = () => {
    const pairs = [];

    organizations.forEach(org => {
      if (org.has_matches && org.sources.length > 1) {
        // For each source in this organization group
        org.sources.forEach(source => {
          // Include fuzzy, exact, and manual matches (exclude only 'unmatched' type)
          if (source.match_type === 'fuzzy' || source.match_type === 'exact' || source.is_manual) {
            pairs.push({
              masterOrg: org.master_org_name,
              sourceName: source.source_org_name,
              source: source.source,
              sourceOrgId: source.source_org_id,
              confidence: Math.round(source.confidence_score),
              matchType: source.is_manual ? 'Manual' : 'Auto',
              deviceCount: source.device_count,
              id: source.id
            });
          }
        });
      }
    });

    // Sort by confidence score descending
    return pairs.sort((a, b) => b.confidence - a.confidence);
  };

  const matchedPairs = getMatchedPairs();

  // Count unique matched organizations (not total pairs)
  const uniqueMatchedOrgs = new Set(matchedPairs.map(pair => pair.masterOrg)).size;

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'organizations' ? 'active' : ''}`}
          onClick={() => setActiveTab('organizations')}
        >
          Organizations
        </button>
        <button
          className={`settings-tab ${activeTab === 'eventlog' ? 'active' : ''}`}
          onClick={() => setActiveTab('eventlog')}
        >
          Event Log
        </button>
        <button
          className={`settings-tab ${activeTab === 'credentials' ? 'active' : ''}`}
          onClick={() => setActiveTab('credentials')}
        >
          API Credentials
        </button>
        <button
          className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button
          className={`settings-tab ${activeTab === 'production' ? 'active' : ''}`}
          onClick={() => setActiveTab('production')}
        >
          Production
        </button>
        <button
          className={`settings-tab ${activeTab === 'duplicates' ? 'active' : ''}`}
          onClick={() => setActiveTab('duplicates')}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          Duplicates
          {duplicatesScanData?.summary?.total_stale_devices > 0 && (
            <span style={{
              background: '#ef4444',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '10px',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              {duplicatesScanData.summary.total_stale_devices}
            </span>
          )}
        </button>
        <button
          className={`settings-tab ${activeTab === 'branding' ? 'active' : ''}`}
          onClick={() => setActiveTab('branding')}
        >
          Branding
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'organizations' && (
          <div className="organizations-section">
            <div className="section-header">
              <div>
                <h2>Organization Management</h2>
                <p>Manage organization matching and fuzzy matching settings</p>
              </div>
              <div className="view-toggle">
                <button
                  className={`view-toggle-btn ${orgView === 'overview' ? 'active' : ''}`}
                  onClick={() => setOrgView('overview')}
                >
                  Overview
                </button>
                <button
                  className={`view-toggle-btn ${orgView === 'matched' ? 'active' : ''}`}
                  onClick={() => setOrgView('matched')}
                >
                  Matched Companies ({uniqueMatchedOrgs})
                </button>
              </div>
            </div>

            {stats && (
              <div className="org-stats">
                <div className="stat-card">
                  <div className="stat-value">{stats.total_organizations}</div>
                  <div className="stat-label">Unique Organizations</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.unique_masters}</div>
                  <div className="stat-label">Unique Master Orgs</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.matched_organizations}</div>
                  <div className="stat-label">Fuzzy Matched</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.manual_mappings}</div>
                  <div className="stat-label">Manual Mappings</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.total_unique_devices || 0}</div>
                  <div className="stat-label">Unique Devices</div>
                </div>
                <div className="stat-card highlight">
                  <div className="stat-value">{stats.match_rate}%</div>
                  <div className="stat-label">Match Rate</div>
                </div>
              </div>
            )}

            <div className="org-controls">
              <div className="org-filters">
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="org-search"
                    style={{ paddingRight: '2rem', width: '100%' }}
                  />
                  {searchTerm && <button className="clear-search-btn" onClick={() => setSearchTerm('')}>{'\u2715'}</button>}
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="org-filter"
                >
                  <option value="all">All Organizations</option>
                  <option value="matched">Fuzzy Matched Only</option>
                  <option value="unmatched">Unmatched Only</option>
                  <option value="manual">Manual Mappings Only</option>
                </select>
              </div>
              <button
                onClick={handleRematch}
                disabled={rematching}
                className="btn-rematch"
              >
                {rematching ? 'Rematching...' : 'Run Fuzzy Matching'}
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading organizations...</div>
            ) : orgView === 'matched' ? (
              /* MATCHED PAIRS VIEW */
              <div className="matched-pairs-view">
                <div className="matched-pairs-header">
                  <h3>Fuzzy Matched Organizations</h3>
                  <p>Review automatically matched companies and their confidence scores. Unmatch any incorrect associations.</p>
                </div>

                <div className="confidence-legend">
                  <div className="legend-title">Confidence Score Legend:</div>
                  <div className="legend-items">
                    <div className="legend-item">
                      <div className="legend-badge" style={{ background: '#00c875' }}></div>
                      <span>95-100% - High confidence match (likely identical or minor variations)</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-badge" style={{ background: '#4c9aff' }}></div>
                      <span>90-94% - Good confidence match (auto-matched, safe to trust)</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-badge" style={{ background: '#ff9f43' }}></div>
                      <span>&lt;90% - Lower confidence, review recommended</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-badge" style={{ background: '#9b59b6' }}></div>
                      <span>Manual - User-defined mapping (overrides automatic matching)</span>
                    </div>
                  </div>
                  <div className="legend-details" style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>How Fuzzy Matching Works:</strong>
                    <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                      <li>Uses token-based comparison (ignores word order)</li>
                      <li>Normalizes names by removing suffixes (Inc., Ltd., Corp., LLC)</li>
                      <li>Auto-matches organizations at 90%+ confidence</li>
                      <li>Groups similar names under the shortest variant as "master"</li>
                      <li>Manual mappings are preserved during re-matching</li>
                    </ul>
                  </div>
                </div>
                {matchedPairs.length === 0 ? (
                  <div className="no-results">No fuzzy matched organizations found. Run fuzzy matching to find similarities.</div>
                ) : (
                  <div className="matched-pairs-list">
                    {matchedPairs.map((pair, idx) => (
                      <div key={`${pair.source}-${pair.sourceOrgId}`} className="matched-pair-card">
                        <div className="pair-number">#{idx + 1}</div>
                        <div className="pair-content">
                          <div className="pair-main">
                            <div className="pair-source-info">
                              <div className="pair-label">Source Organization</div>
                              <div className="pair-name">{pair.sourceName}</div>
                              <div className="pair-meta">
                                <span className="pair-source-badge">{pair.source}</span>
                                <span className="pair-devices">{pair.deviceCount} devices</span>
                              </div>
                            </div>
                            <div className="pair-arrow">
                              <div className="arrow-line"></div>
                              <div className="confidence-badge" style={{
                                background: pair.confidence >= 95 ? '#00c875' :
                                           pair.confidence >= 90 ? '#4c9aff' : '#ff9f43'
                              }}>
                                {pair.confidence}% Match
                              </div>
                              <div className="arrow-line"></div>
                            </div>
                            <div className="pair-master-info">
                              <div className="pair-label">Master Organization</div>
                              <div className="pair-name">{pair.masterOrg}</div>
                              <div className="pair-meta">
                                <span className={`badge badge-${pair.matchType.toLowerCase()}`}>
                                  {pair.matchType}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="pair-actions">
                            <button
                              className="btn-action-small"
                              onClick={() => handleManualMatch(pair.source, pair.sourceOrgId, pair.sourceName)}
                              title="Change the master organization this maps to"
                            >
                              Remap
                            </button>
                            <button
                              className="btn-action-small btn-unmatch"
                              onClick={() => handleUnmatch(pair.source, pair.sourceOrgId)}
                              title="Unmatch this organization (make it standalone)"
                            >
                              Unmatch
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* OVERVIEW - ORIGINAL LIST VIEW */
              <div className="org-list">
                {/* Bulk Action Bar */}
                {selectedOrgs.size > 0 && (
                  <div className="bulk-action-bar">
                    <span className="bulk-selection-count">
                      {selectedOrgs.size} organization{selectedOrgs.size !== 1 ? 's' : ''} selected
                    </span>
                    <div className="bulk-actions">
                      <span className="bulk-action-label">Bulk Actions:</span>
                      <div className="bulk-action-group">
                        <span className="bulk-action-group-label">Enable:</span>
                        <button onClick={() => handleBulkIntegrationChange('ncentral_enabled', true)} disabled={bulkActionSaving}>N-Central</button>
                        <button onClick={() => handleBulkIntegrationChange('datto_rmm_enabled', true)} disabled={bulkActionSaving}>Datto RMM</button>
                        <button onClick={() => handleBulkIntegrationChange('connectwise_enabled', true)} disabled={bulkActionSaving}>ConnectWise</button>
                        <button onClick={() => handleBulkIntegrationChange('huntress_enabled', true)} disabled={bulkActionSaving}>Huntress</button>
                        <button onClick={() => handleBulkIntegrationChange('immybot_enabled', true)} disabled={bulkActionSaving}>ImmyBot</button>
                        <button onClick={() => handleBulkIntegrationChange('cloudradial_enabled', true)} disabled={bulkActionSaving}>CloudRadial</button>
                        <button onClick={() => handleBulkIntegrationChange('microsoft_enabled', true)} disabled={bulkActionSaving}>Microsoft</button>
                      </div>
                      <div className="bulk-action-group">
                        <span className="bulk-action-group-label">Disable:</span>
                        <button onClick={() => handleBulkIntegrationChange('ncentral_enabled', false)} disabled={bulkActionSaving}>N-Central</button>
                        <button onClick={() => handleBulkIntegrationChange('datto_rmm_enabled', false)} disabled={bulkActionSaving}>Datto RMM</button>
                        <button onClick={() => handleBulkIntegrationChange('connectwise_enabled', false)} disabled={bulkActionSaving}>ConnectWise</button>
                        <button onClick={() => handleBulkIntegrationChange('huntress_enabled', false)} disabled={bulkActionSaving}>Huntress</button>
                        <button onClick={() => handleBulkIntegrationChange('immybot_enabled', false)} disabled={bulkActionSaving}>ImmyBot</button>
                        <button onClick={() => handleBulkIntegrationChange('cloudradial_enabled', false)} disabled={bulkActionSaving}>CloudRadial</button>
                        <button onClick={() => handleBulkIntegrationChange('microsoft_enabled', false)} disabled={bulkActionSaving}>Microsoft</button>
                      </div>
                      <div className="bulk-action-group bulk-action-macos">
                        <span className="bulk-action-group-label">macOS:</span>
                        <button onClick={() => handleBulkIntegrationChange('mac_health_checks_enabled', true)} disabled={bulkActionSaving}>Enable</button>
                        <button onClick={() => handleBulkIntegrationChange('mac_health_checks_enabled', false)} disabled={bulkActionSaving}>Disable</button>
                      </div>
                    </div>
                    <button
                      className="bulk-delete-btn"
                      onClick={handleBulkDelete}
                      disabled={bulkActionSaving}
                    >
                      Delete Selected
                    </button>
                    <button className="bulk-clear-btn" onClick={clearSelection}>Clear Selection</button>
                  </div>
                )}

                {/* MSP Organization Selector */}
                <div className="msp-selector-section">
                  <div className="msp-selector-label">
                    <span className="msp-star">★</span>
                    <span>MSP Organization:</span>
                  </div>
                  {mspOrgName ? (
                    <div className="msp-selected">
                      <span className="msp-org-name">{mspOrgName}</span>
                      <button
                        className="msp-change-btn"
                        onClick={() => setMspSelectorOpen(true)}
                        disabled={mspSaving}
                      >
                        Change
                      </button>
                      <button
                        className="msp-clear-btn"
                        onClick={clearMspOrganization}
                        disabled={mspSaving}
                      >
                        Clear
                      </button>
                    </div>
                  ) : (
                    <div className="msp-unset">
                      <span className="msp-placeholder">No MSP organization set</span>
                      <button
                        className="msp-set-btn"
                        onClick={() => setMspSelectorOpen(true)}
                        disabled={mspSaving}
                      >
                        Set MSP
                      </button>
                    </div>
                  )}
                  {mspSelectorOpen && (
                    <div className="msp-dropdown">
                      <div className="msp-dropdown-header">
                        <input
                          type="text"
                          className="msp-search-input"
                          placeholder="Search organizations..."
                          value={mspSearchTerm}
                          onChange={(e) => setMspSearchTerm(e.target.value)}
                          autoFocus
                        />
                        <button
                          className="msp-dropdown-close"
                          onClick={() => { setMspSelectorOpen(false); setMspSearchTerm(''); }}
                        >
                          ×
                        </button>
                      </div>
                      <div className="msp-dropdown-list">
                        {organizations
                          .filter(org => org.master_org_name.toLowerCase().includes(mspSearchTerm.toLowerCase()))
                          .slice(0, 10)
                          .map(org => (
                            <div
                              key={org.master_org_name}
                              className={`msp-dropdown-item ${org.master_org_name === mspOrgName ? 'msp-dropdown-item-selected' : ''}`}
                              onClick={() => setMspOrganization(org.master_org_name)}
                            >
                              {org.master_org_name}
                              {org.master_org_name === mspOrgName && <span className="msp-check">✓</span>}
                            </div>
                          ))}
                        {organizations.filter(org => org.master_org_name.toLowerCase().includes(mspSearchTerm.toLowerCase())).length === 0 && (
                          <div className="msp-dropdown-empty">No organizations found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="org-list-header">
                  <div className="org-checkbox-col">
                    <input
                      type="checkbox"
                      checked={filteredOrganizations.length > 0 && selectedOrgs.size === filteredOrganizations.length}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </div>
                  <div
                    onClick={() => handleSort('master_org_name')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Organization (Master) {sortField === 'master_org_name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </div>
                  <div
                    onClick={() => handleSort('sources')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Sources {sortField === 'sources' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </div>
                  <div
                    onClick={() => handleSort('total_devices')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Devices {sortField === 'total_devices' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </div>
                  <div>Status</div>
                  <div>Actions</div>
                </div>
                {filteredOrganizations.map(org => {
                  const settings = orgSettings[org.master_org_name];
                  const isHidden = settings?.is_hidden;
                  const isMsp = org.master_org_name === mspOrgName;
                  const inheritsMsp = isInheritedMsp(org);
                  const hasMacEnabled = settings?.mac_health_checks_enabled;
                  const hasCustomSettings = settings && (
                    settings.is_hidden ||
                    !settings.ncentral_enabled ||
                    !settings.connectwise_enabled ||
                    !settings.huntress_enabled ||
                    !settings.immybot_enabled ||
                    !settings.cloudradial_enabled ||
                    !settings.datto_rmm_enabled ||
                    !settings.microsoft_enabled ||
                    settings.mac_health_checks_enabled
                  );
                  return (
                  <div key={org.master_org_name} className={`org-item ${isHidden ? 'org-hidden' : ''} ${selectedOrgs.has(org.master_org_name) ? 'org-selected' : ''} ${isMsp ? 'org-msp' : ''} ${inheritsMsp ? 'org-msp-inherited' : ''}`}>
                    <div className="org-item-main" onClick={() => toggleExpand(org.master_org_name)}>
                      <div className="org-checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedOrgs.has(org.master_org_name)}
                          onChange={() => toggleOrgSelection(org.master_org_name)}
                        />
                      </div>
                      <div className="org-name">
                        <span className={`expand-icon ${expandedOrg === org.master_org_name ? 'expanded' : ''}`}>
                          ▶
                        </span>
                        {isMsp && <span className="msp-badge" title="MSP Organization">★</span>}
                        {inheritsMsp && <span className="msp-inherited-badge" title="Inherits MSP status">☆</span>}
                        {org.master_org_name}
                        {isMsp && <span className="badge badge-msp">MSP</span>}
                        {inheritsMsp && <span className="badge badge-msp-inherited">MSP Child</span>}
                        {isHidden && <span className="badge badge-hidden">Hidden</span>}
                        {hasMacEnabled && <span className="badge badge-macos" title="MacOS health checks enabled">macOS</span>}
                        {hasCustomSettings && !isHidden && !isMsp && !hasMacEnabled && <span className="badge badge-custom">Custom</span>}
                      </div>
                      <div className="org-sources">
                        {org.sources.map(s => s.source).join(', ')}
                      </div>
                      <div className="org-devices">{org.total_devices}</div>
                      <div className="org-status">
                        {org.has_matches ? (
                          <span className="badge badge-matched">
                            {org.sources.some(s => s.is_manual) ? 'Manual' : 'Auto-Matched'}
                          </span>
                        ) : (
                          <span className="badge badge-unmatched">Unmatched</span>
                        )}
                      </div>
                      <div className="org-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn-action btn-settings"
                          onClick={() => openOrgSettings(org.master_org_name)}
                          title="Organization Settings"
                        >
                          ⚙
                        </button>
                        <button
                          className="btn-action"
                          onClick={() => toggleExpand(org.master_org_name)}
                        >
                          {expandedOrg === org.master_org_name ? 'Collapse' : 'Expand'}
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDeleteOrganization(org.master_org_name)}
                          title="Delete Organization"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {expandedOrg === org.master_org_name && (
                      <div className="org-details">
                        <table className="source-table">
                          <thead>
                            <tr>
                              <th>Source</th>
                              <th>Organization Name</th>
                              <th>Match Type</th>
                              <th>Confidence</th>
                              <th>Devices</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {org.sources.map(source => (
                              <tr key={`${source.source}-${source.source_org_id}`}>
                                <td>{source.source}</td>
                                <td>{source.source_org_name}</td>
                                <td>
                                  <span className={`badge badge-${source.match_type}`}>
                                    {source.is_manual ? 'Manual' : source.match_type}
                                  </span>
                                </td>
                                <td>
                                  {source.confidence_score
                                    ? `${Math.round(source.confidence_score)}%`
                                    : '-'}
                                </td>
                                <td>{source.device_count}</td>
                                <td>
                                  <button
                                    className="btn-action-small"
                                    onClick={() => handleManualMatch(source.source, source.source_org_id, source.source_org_name)}
                                  >
                                    Remap
                                  </button>
                                  {(source.match_type === 'fuzzy' || source.is_manual) && (
                                    <button
                                      className="btn-action-small btn-unmatch"
                                      onClick={() => handleUnmatch(source.source, source.source_org_id)}
                                    >
                                      Unmatch
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );})}
                {filteredOrganizations.length === 0 && (
                  <div className="no-results">No organizations found matching your filters</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* EVENT LOG TAB */}
        {activeTab === 'eventlog' && (
          <div className="eventlog-section">
            <div className="section-header">
              <div>
                <h2>Event Log</h2>
                <p>System activity log with filtering by category, source, and organization</p>
              </div>
              <button
                className="btn-action"
                onClick={() => {
                  setCategoryFilter('');
                  setSourceFilter('');
                  setOrgFilter('');
                  setEventSearchTerm('');
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </button>
            </div>

            {/* Filters */}
            <div className="event-filters">
              <div className="filter-group">
                <label>Category:</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                  className="filter-select"
                >
                  <option value="">All Categories</option>
                  <option value="SYNC">Sync Operations</option>
                  <option value="DELETE">Device Deletions</option>
                  <option value="CONFIG">Configuration Changes</option>
                  <option value="ORG_MAPPING">Organization Mappings</option>
                  <option value="SYSTEM">System Events</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Source:</label>
                <select
                  value={sourceFilter}
                  onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }}
                  className="filter-select"
                >
                  <option value="">All Sources</option>
                  <option value="N-Central">N-Central</option>
                  <option value="Datto RMM">Datto RMM</option>
                  <option value="Huntress">Huntress</option>
                  <option value="ImmyBot">ImmyBot</option>
                  <option value="CloudRadial">CloudRadial</option>
                  <option value="ImmyBot+CloudRadial">ImmyBot+CloudRadial</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Organization:</label>
                <input
                  type="text"
                  value={orgFilter}
                  onChange={(e) => { setOrgFilter(e.target.value); setCurrentPage(1); }}
                  placeholder="Filter by organization..."
                  className="filter-input"
                />
              </div>

              <div className="filter-group">
                <label>Search:</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={eventSearchTerm}
                    onChange={(e) => setEventSearchTerm(e.target.value)}
                    placeholder="Search events..."
                    className="filter-input"
                    style={{ paddingRight: '2rem', width: '100%' }}
                  />
                  {eventSearchTerm && <button className="clear-search-btn" onClick={() => { setEventSearchTerm(''); setCurrentPage(1); }}>{'\u2715'}</button>}
                </div>
              </div>
            </div>

            {/* Event Stats */}
            <div className="event-stats">
              <span>Total Events: <strong>{eventStats.total || 0}</strong></span>
              <span>
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, eventStats.total)} - {Math.min(currentPage * itemsPerPage, eventStats.total)} of {eventStats.total}
              </span>
            </div>

            {/* Event List */}
            {eventLoading ? (
              <div className="loading">Loading events...</div>
            ) : (
              <div className="event-list">
                <div className="event-list-header">
                  <div style={{ width: '180px' }}>Timestamp</div>
                  <div style={{ width: '120px' }}>Category</div>
                  <div style={{ width: '120px' }}>Source</div>
                  <div style={{ width: '150px' }}>Organization</div>
                  <div style={{ width: '150px' }}>Device</div>
                  <div style={{ flex: 1 }}>Details</div>
                </div>

                {events.map(event => (
                  <div key={event.id} className="event-item">
                    <div className="event-row">
                      <div style={{ width: '180px' }} className="event-timestamp">
                        {new Date(event.timestamp).toLocaleString()}
                      </div>
                      <div style={{ width: '120px' }}>
                        <span className={`category-badge category-${event.event_category.toLowerCase()}`}>
                          {event.event_category}
                        </span>
                      </div>
                      <div style={{ width: '120px' }} className="event-source">
                        {event.source || '-'}
                      </div>
                      <div style={{ width: '150px' }} className="event-org">
                        {event.organization || '-'}
                      </div>
                      <div style={{ width: '150px' }} className="event-device">
                        {event.device_hostname || '-'}
                      </div>
                      <div style={{ flex: 1 }} className="event-details">
                        <div className="event-action">{event.user_action}</div>
                        <div className="event-details-text">{event.details}</div>
                      </div>
                    </div>
                  </div>
                ))}

                {events.length === 0 && (
                  <div className="no-results">No events found</div>
                )}
              </div>
            )}

            {/* Pagination */}
            {eventStats.total > itemsPerPage && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-page"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {Math.ceil(eventStats.total / itemsPerPage)}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(eventStats.total / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(eventStats.total / itemsPerPage)}
                  className="btn-page"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* CREDENTIALS TAB */}
        {activeTab === 'credentials' && (
          <div className="credentials-section">
            <div className="section-header">
              <div>
                <h2>API Credentials</h2>
                <p>Manage API credentials for all integration sources</p>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                {databaseType !== 'postgresql' && (
                  <button
                    className="btn-action"
                    onClick={fetchCredentials}
                    disabled={credentialsLoading}
                    style={{ background: '#4c9aff' }}
                  >
                    {credentialsLoading ? 'Loading...' : credentialsEnvironment === 'local' ? 'Reload from .env' : 'Reload from Secrets'}
                  </button>
                )}
                <button
                  className="btn-action"
                  onClick={handleSaveCredentials}
                  disabled={savingCredentials}
                >
                  {savingCredentials ? 'Saving...' : 'Save All Changes'}
                </button>
              </div>
            </div>

            {/* Sub-Tabs for Credentials */}
            <div className="sub-tabs" style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '2rem',
              borderBottom: '2px solid rgba(76, 154, 255, 0.2)',
              paddingBottom: '0.5rem'
            }}>
              <button
                className={`sub-tab ${credentialsSubTab === 'management' ? 'active' : ''}`}
                onClick={() => setCredentialsSubTab('management')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: credentialsSubTab === 'management' ? 'rgba(76, 154, 255, 0.2)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  color: credentialsSubTab === 'management' ? '#4c9aff' : '#888',
                  cursor: 'pointer',
                  fontWeight: credentialsSubTab === 'management' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                🔧 Management
              </button>
              <button
                className={`sub-tab ${credentialsSubTab === 'psa' ? 'active' : ''}`}
                onClick={() => setCredentialsSubTab('psa')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: credentialsSubTab === 'psa' ? 'rgba(76, 154, 255, 0.2)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  color: credentialsSubTab === 'psa' ? '#4c9aff' : '#888',
                  cursor: 'pointer',
                  fontWeight: credentialsSubTab === 'psa' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                🎫 PSA
              </button>
              <button
                className={`sub-tab ${credentialsSubTab === 'edr' ? 'active' : ''}`}
                onClick={() => setCredentialsSubTab('edr')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: credentialsSubTab === 'edr' ? 'rgba(76, 154, 255, 0.2)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  color: credentialsSubTab === 'edr' ? '#4c9aff' : '#888',
                  cursor: 'pointer',
                  fontWeight: credentialsSubTab === 'edr' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                🛡️ EDR
              </button>
              <button
                className={`sub-tab ${credentialsSubTab === 'service-delivery' ? 'active' : ''}`}
                onClick={() => setCredentialsSubTab('service-delivery')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: credentialsSubTab === 'service-delivery' ? 'rgba(76, 154, 255, 0.2)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  color: credentialsSubTab === 'service-delivery' ? '#4c9aff' : '#888',
                  cursor: 'pointer',
                  fontWeight: credentialsSubTab === 'service-delivery' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                📊 Service Delivery
              </button>
              <button
                className={`sub-tab ${credentialsSubTab === 'microsoft' ? 'active' : ''}`}
                onClick={() => setCredentialsSubTab('microsoft')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: credentialsSubTab === 'microsoft' ? 'rgba(76, 154, 255, 0.2)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  color: credentialsSubTab === 'microsoft' ? '#4c9aff' : '#888',
                  cursor: 'pointer',
                  fontWeight: credentialsSubTab === 'microsoft' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                ☁️ Microsoft
              </button>
            </div>

            {credentialsLoading ? (
              <div className="loading">Loading credentials...</div>
            ) : (
              <>
                <div className="credentials-grid">
                {/* MANAGEMENT SECTION */}
                {credentialsSubTab === 'management' && (
                <div className="cards-vertical-stack">
                  {/* N-CENTRAL Collapsible Card */}
                  <div className="collapsible-card">
                    <div
                      className={`collapsible-card-header ${!collapsedSections.ncentral_card ? 'expanded' : ''}`}
                      onClick={() => toggleSection('ncentral_card')}
                    >
                      <div className="collapsible-card-header-left">
                        <h3>N-Central (N-able)</h3>
                      </div>
                      <div className="collapsible-card-header-right">
                        <label
                          className={`primary-radio-label ${primaryManagement === 'ncentral' ? 'selected' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="radio"
                            name="primaryManagement"
                            value="ncentral"
                            checked={primaryManagement === 'ncentral'}
                            onChange={(e) => setPrimaryManagement(e.target.value)}
                          />
                          Primary
                        </label>
                        <span className={`status-badge ${
                          testResults.ncentral?.success ? 'connected' :
                          testResults.ncentral?.error ? 'failed' :
                          credentialsStatus.ncentral ? 'configured' : 'not-configured'
                        }`}>
                          {testResults.ncentral?.success ? 'Connected' :
                           testResults.ncentral?.error ? 'Failed' :
                           credentialsStatus.ncentral ? 'Configured' : 'Not Configured'}
                        </span>
                        <span className="collapse-arrow">{collapsedSections.ncentral_card ? '▼' : '▲'}</span>
                      </div>
                    </div>

                    {!collapsedSections.ncentral_card && (
                      <div className="collapsible-card-content">

                  <div className="credential-field">
                    <label>JWT Token</label>
                    {maskedFields.NABLE_JWT ? (
                      <div
                        style={{
                          padding: '0.75rem',
                          backgroundColor: 'rgba(76, 154, 255, 0.1)',
                          border: '1px solid rgba(76, 154, 255, 0.3)',
                          borderRadius: '6px',
                          color: '#4c9aff',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                        onClick={() => handleUnmaskField('NABLE_JWT')}
                      >
                        🔒 Click here to change
                      </div>
                    ) : (
                      <input
                        type="password"
                        value={credentials.NABLE_JWT || ''}
                        onChange={(e) => handleCredentialChange('NABLE_JWT', e.target.value)}
                        placeholder="Enter N-able JWT token"
                      />
                    )}
                  </div>

                  <div className="credential-field">
                    <label>Server URL</label>
                    <input
                      type="text"
                      value={credentials.NABLE_URL || ''}
                      onChange={(e) => handleCredentialChange('NABLE_URL', e.target.value)}
                      placeholder="https://your-server.n-able.com"
                    />
                  </div>

                  <div className="credential-field">
                    <label>Filter ID (Optional)</label>
                    <input
                      type="text"
                      value={credentials.NABLE_FILTER_ID || ''}
                      onChange={(e) => handleCredentialChange('NABLE_FILTER_ID', e.target.value)}
                      placeholder="Filter ID"
                    />
                  </div>

                  <div className="credential-field">
                    <label>User Filter (Optional)</label>
                    <input
                      type="text"
                      value={credentials.NABLE_USER_FILTER || ''}
                      onChange={(e) => handleCredentialChange('NABLE_USER_FILTER', e.target.value)}
                      placeholder="User filter"
                    />
                  </div>

                  <button
                    className="btn-test"
                    onClick={() => handleTestConnection('ncentral')}
                    disabled={testingSource === 'ncentral'}
                  >
                    {testingSource === 'ncentral' ? 'Testing...' : 'Test Connection'}
                  </button>

                        {testResults.ncentral && (
                          <div className={`test-result ${testResults.ncentral.success ? 'success' : 'error'} ${testResults.ncentral.fading ? 'fading' : ''}`}>
                            {testResults.ncentral.success ? '✓' : '✗'} {testResults.ncentral.message || testResults.ncentral.error}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* IMMYBOT Collapsible Card */}
                  <div className="collapsible-card">
                    <div
                      className={`collapsible-card-header ${!collapsedSections.immybot_card ? 'expanded' : ''}`}
                      onClick={() => toggleSection('immybot_card')}
                    >
                      <div className="collapsible-card-header-left">
                        <h3>ImmyBot</h3>
                      </div>
                      <div className="collapsible-card-header-right">
                        <label
                          className={`primary-radio-label ${primaryManagement === 'immybot' ? 'selected' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="radio"
                            name="primaryManagement"
                            value="immybot"
                            checked={primaryManagement === 'immybot'}
                            onChange={(e) => setPrimaryManagement(e.target.value)}
                          />
                          Primary
                        </label>
                        <span className={`status-badge ${
                          testResults.immybot?.success ? 'connected' :
                          testResults.immybot?.error ? 'failed' :
                          credentialsStatus.immybot ? 'configured' : 'not-configured'
                        }`}>
                          {testResults.immybot?.success ? 'Connected' :
                           testResults.immybot?.error ? 'Failed' :
                           credentialsStatus.immybot ? 'Configured' : 'Not Configured'}
                        </span>
                        <span className="collapse-arrow">{collapsedSections.immybot_card ? '▼' : '▲'}</span>
                      </div>
                    </div>

                    {!collapsedSections.immybot_card && (
                      <div className="collapsible-card-content">

                  <div className="credential-field">
                    <label>Subdomain</label>
                    <input
                      type="text"
                      value={credentials.IMMYBOT_SUBDOMAIN || ''}
                      onChange={(e) => handleCredentialChange('IMMYBOT_SUBDOMAIN', e.target.value)}
                      placeholder="your-instance"
                    />
                    <small style={{ color: '#888', fontSize: '0.75rem' }}>Will be used as: https://your-instance.immy.bot</small>
                  </div>

                  <div className="credential-field">
                    <label>Take (Page Size)</label>
                    <input
                      type="text"
                      value={credentials.IMMYBOT_TAKE || ''}
                      onChange={(e) => handleCredentialChange('IMMYBOT_TAKE', e.target.value)}
                      placeholder="100"
                    />
                  </div>

                  <div className="credential-field">
                    <label>Azure Client ID</label>
                    <input
                      type="text"
                      value={credentials.AZURE_CLIENT_ID || ''}
                      onChange={(e) => handleCredentialChange('AZURE_CLIENT_ID', e.target.value)}
                      placeholder="Azure Application (client) ID"
                    />
                  </div>

                  <div className="credential-field">
                    <label>Azure Client Secret</label>
                    <input
                      type="password"
                      value={credentials.AZURE_CLIENT_SECRET || ''}
                      onChange={(e) => handleCredentialChange('AZURE_CLIENT_SECRET', e.target.value)}
                      placeholder="Azure client secret"
                    />
                  </div>

                  <div className="credential-field">
                    <label>Azure Domain</label>
                    <input
                      type="text"
                      value={credentials.AZURE_DOMAIN || ''}
                      onChange={(e) => handleCredentialChange('AZURE_DOMAIN', e.target.value)}
                      placeholder="your-domain.onmicrosoft.com"
                    />
                  </div>

                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '0.5rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Duplicate Verification</h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', marginBottom: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={credentials.IMMY_AUTO_VERIFY_ON_SCAN === 'true'}
                        onChange={(e) => handleCredentialChange('IMMY_AUTO_VERIFY_ON_SCAN', e.target.checked ? 'true' : 'false')}
                      />
                      <div>
                        <div style={{ fontWeight: '500' }}>Auto-verify on duplicate scan</div>
                        <small style={{ color: '#888', fontSize: '0.75rem' }}>
                          Automatically queue verification for unverified devices when duplicates are found (max 10 per 30 min)
                        </small>
                      </div>
                    </label>
                  </div>

                  <button
                    className="btn-test"
                    onClick={() => handleTestConnection('immybot')}
                    disabled={testingSource === 'immybot'}
                  >
                    {testingSource === 'immybot' ? 'Testing...' : 'Test Connection'}
                  </button>

                        {testResults.immybot && (
                          <div className={`test-result ${testResults.immybot.success ? 'success' : 'error'} ${testResults.immybot.fading ? 'fading' : ''}`}>
                            {testResults.immybot.success ? '✓' : '✗'} {testResults.immybot.message || testResults.immybot.error}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* DATTO RMM Collapsible Card */}
                  <div className="collapsible-card">
                    <div
                      className={`collapsible-card-header ${!collapsedSections.datto_rmm_card ? 'expanded' : ''}`}
                      onClick={() => toggleSection('datto_rmm_card')}
                    >
                      <div className="collapsible-card-header-left">
                        <h3>Datto RMM</h3>
                      </div>
                      <div className="collapsible-card-header-right">
                        <label
                          className={`primary-radio-label ${primaryManagement === 'datto_rmm' ? 'selected' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="radio"
                            name="primaryManagement"
                            value="datto_rmm"
                            checked={primaryManagement === 'datto_rmm'}
                            onChange={(e) => setPrimaryManagement(e.target.value)}
                          />
                          Primary
                        </label>
                        <span className={`status-badge ${
                          testResults.datto_rmm?.success ? 'connected' :
                          testResults.datto_rmm?.error ? 'failed' :
                          credentialsStatus.datto_rmm ? 'configured' : 'not-configured'
                        }`}>
                          {testResults.datto_rmm?.success ? 'Connected' :
                           testResults.datto_rmm?.error ? 'Failed' :
                           credentialsStatus.datto_rmm ? 'Configured' : 'Not Configured'}
                        </span>
                        <span className="collapse-arrow">{collapsedSections.datto_rmm_card ? '▼' : '▲'}</span>
                      </div>
                    </div>

                    {!collapsedSections.datto_rmm_card && (
                      <div className="collapsible-card-content">

                  <div className="credential-field">
                    <label>API URL</label>
                    <input
                      type="text"
                      value={credentials.DATTO_RMM_API_URL || ''}
                      onChange={(e) => handleCredentialChange('DATTO_RMM_API_URL', e.target.value)}
                      placeholder="https://pinotage-api.centrastage.net"
                    />
                    <small style={{ color: '#888', fontSize: '0.75rem' }}>Regional API endpoint (e.g., pinotage, merlot, concord)</small>
                  </div>

                  <div className="credential-field">
                    <label>API Key</label>
                    {maskedFields.DATTO_RMM_API_KEY ? (
                      <div
                        style={{
                          padding: '0.75rem',
                          backgroundColor: 'rgba(76, 154, 255, 0.1)',
                          border: '1px solid rgba(76, 154, 255, 0.3)',
                          borderRadius: '6px',
                          color: '#4c9aff',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                        onClick={() => handleUnmaskField('DATTO_RMM_API_KEY')}
                      >
                        🔒 Click here to change
                      </div>
                    ) : (
                      <input
                        type="password"
                        value={credentials.DATTO_RMM_API_KEY || ''}
                        onChange={(e) => handleCredentialChange('DATTO_RMM_API_KEY', e.target.value)}
                        placeholder="Enter Datto RMM API key"
                      />
                    )}
                  </div>

                  <div className="credential-field">
                    <label>API Secret</label>
                    {maskedFields.DATTO_RMM_API_SECRET ? (
                      <div
                        style={{
                          padding: '0.75rem',
                          backgroundColor: 'rgba(76, 154, 255, 0.1)',
                          border: '1px solid rgba(76, 154, 255, 0.3)',
                          borderRadius: '6px',
                          color: '#4c9aff',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                        onClick={() => handleUnmaskField('DATTO_RMM_API_SECRET')}
                      >
                        🔒 Click here to change
                      </div>
                    ) : (
                      <input
                        type="password"
                        value={credentials.DATTO_RMM_API_SECRET || ''}
                        onChange={(e) => handleCredentialChange('DATTO_RMM_API_SECRET', e.target.value)}
                        placeholder="Enter Datto RMM API secret"
                      />
                    )}
                  </div>

                  <button
                    className="btn-test"
                    onClick={() => handleTestConnection('datto_rmm')}
                    disabled={testingSource === 'datto_rmm'}
                  >
                    {testingSource === 'datto_rmm' ? 'Testing...' : 'Test Connection'}
                  </button>

                        {testResults.datto_rmm && (
                          <div className={`test-result ${testResults.datto_rmm.success ? 'success' : 'error'} ${testResults.datto_rmm.fading ? 'fading' : ''}`}>
                            {testResults.datto_rmm.success ? '✓' : '✗'} {testResults.datto_rmm.message || testResults.datto_rmm.error}
                          </div>
                        )}

                        {/* Organization/Site Filtering */}
                        <div className="credential-field" style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem' }}>
                          <label style={{ marginBottom: '0.75rem', display: 'block' }}>Organization Sync Mode</label>
                          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name="dattoSyncMode"
                                checked={dattoSyncAllOrgs}
                                onChange={() => handleDattoSyncAllOrgsChange(true)}
                                style={{ marginRight: '0.5rem' }}
                              />
                              Sync all organizations
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name="dattoSyncMode"
                                checked={!dattoSyncAllOrgs}
                                onChange={() => handleDattoSyncAllOrgsChange(false)}
                                style={{ marginRight: '0.5rem' }}
                              />
                              Select specific organizations
                            </label>
                          </div>

                          {!dattoSyncAllOrgs && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <label>Sites/Organizations to Sync</label>
                                <button
                                  className="btn-test"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                  onClick={fetchDattoSites}
                                  disabled={testingSource === 'datto_rmm'}
                                >
                                  {dattoSites.length > 0 ? 'Refresh Sites' : 'Load Sites'}
                                </button>
                              </div>
                              <small style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.5rem', display: 'block' }}>
                                {dattoSites.length === 0
                                  ? 'Click "Load Sites" to fetch available sites from Datto RMM'
                                  : 'Type to search and select sites. Only selected sites will be synced.'}
                              </small>

                              {dattoSites.length > 0 && (
                                <div className="cw-filter-container">
                                  <div className="cw-filter-input-wrapper">
                                    <input
                                      type="text"
                                      className="cw-filter-input"
                                      placeholder="Type to search sites..."
                                      value={dattoSiteSearch}
                                      onChange={(e) => setDattoSiteSearch(e.target.value)}
                                    />
                                    {dattoSiteSearch && (
                                      <div className="cw-dropdown">
                                        {dattoSites
                                          .filter(site => site.name.toLowerCase().includes(dattoSiteSearch.toLowerCase()))
                                          .slice(0, 10)
                                          .map(site => (
                                            <div
                                              key={site.id}
                                              className={`cw-dropdown-item ${dattoSelectedSites.includes(site.id) ? 'selected' : ''}`}
                                              onMouseDown={() => {
                                                toggleDattoSite(site.id);
                                                setDattoSiteSearch('');
                                              }}
                                            >
                                              <span className="cw-dropdown-name">{site.name}</span>
                                              {dattoSelectedSites.includes(site.id) && <span className="checkmark"> ✓</span>}
                                            </div>
                                          ))}
                                        {dattoSites.filter(site => site.name.toLowerCase().includes(dattoSiteSearch.toLowerCase())).length === 0 && (
                                          <div className="cw-dropdown-item disabled">No matches found</div>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {dattoSelectedSites.length > 0 && (
                                    <div className="cw-selected-tags">
                                      {dattoSelectedSites.map(siteId => {
                                        const site = dattoSites.find(s => s.id === siteId);
                                        return (
                                          <span key={siteId} className="cw-tag">
                                            {site ? site.name : siteId}
                                            <button
                                              className="cw-tag-remove"
                                              onClick={() => toggleDattoSite(siteId)}
                                              title={`Remove ${site ? site.name : siteId}`}
                                            >
                                              ✕
                                            </button>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Device Type Filtering */}
                        <div className="credential-field" style={{ marginTop: '1rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <label>Device Types to Sync</label>
                            <button
                              className="btn-test"
                              style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                              onClick={fetchDattoDeviceTypes}
                              disabled={testingSource === 'datto_rmm'}
                            >
                              {dattoDeviceTypes.length > 0 ? 'Refresh Types' : 'Load Types'}
                            </button>
                          </div>
                          <small style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.5rem', display: 'block' }}>
                            {dattoDeviceTypes.length === 0
                              ? 'Click "Load Types" to fetch available device types from Datto RMM'
                              : 'Type to search and select device types. Leave empty to sync all types.'}
                          </small>

                          {dattoDeviceTypes.length > 0 && (
                            <div className="cw-filter-container">
                              <div className="cw-filter-input-wrapper">
                                <input
                                  type="text"
                                  className="cw-filter-input"
                                  placeholder="Type to search device types..."
                                  value={dattoDeviceTypeSearch}
                                  onChange={(e) => setDattoDeviceTypeSearch(e.target.value)}
                                />
                                {dattoDeviceTypeSearch && (
                                  <div className="cw-dropdown">
                                    {dattoDeviceTypes
                                      .filter(type => type.toLowerCase().includes(dattoDeviceTypeSearch.toLowerCase()))
                                      .slice(0, 10)
                                      .map(type => (
                                        <div
                                          key={type}
                                          className={`cw-dropdown-item ${dattoSelectedDeviceTypes.includes(type) ? 'selected' : ''}`}
                                          onMouseDown={() => {
                                            toggleDattoDeviceType(type);
                                            setDattoDeviceTypeSearch('');
                                          }}
                                        >
                                          <span className="cw-dropdown-name">{type}</span>
                                          {dattoSelectedDeviceTypes.includes(type) && <span className="checkmark"> ✓</span>}
                                        </div>
                                      ))}
                                    {dattoDeviceTypes.filter(type => type.toLowerCase().includes(dattoDeviceTypeSearch.toLowerCase())).length === 0 && (
                                      <div className="cw-dropdown-item disabled">No matches found</div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {dattoSelectedDeviceTypes.length > 0 && (
                                <div className="cw-selected-tags">
                                  {dattoSelectedDeviceTypes.map(type => (
                                    <span key={type} className="cw-tag">
                                      {type}
                                      <button
                                        className="cw-tag-remove"
                                        onClick={() => toggleDattoDeviceType(type)}
                                        title={`Remove ${type}`}
                                      >
                                        ✕
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Audit Data Refresh */}
                        <div className="credential-field" style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem' }}>
                          <label style={{ marginBottom: '0.5rem', display: 'block' }}>Audit Data Management</label>
                          <small style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.75rem', display: 'block' }}>
                            Regular syncs use cached serial numbers and MAC addresses for faster performance.
                            Use "Refresh Audit Data" to fetch fresh hardware details from all devices.
                          </small>
                          <button
                            className="btn-test"
                            onClick={handleDattoAuditRefresh}
                            disabled={dattoAuditRefreshing || testingSource === 'datto_rmm'}
                            style={{ marginRight: '0.5rem' }}
                          >
                            {dattoAuditRefreshing ? 'Refreshing...' : 'Refresh Audit Data'}
                          </button>
                          {dattoAuditResult && (
                            <span style={{
                              marginLeft: '0.5rem',
                              color: dattoAuditResult.success ? '#57d9a3' : '#ff6b6b',
                              fontSize: '0.85rem'
                            }}>
                              {dattoAuditResult.success ? '✓' : '✗'} {dattoAuditResult.message}
                            </span>
                          )}
                        </div>

                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* PSA SECTION */}
                {credentialsSubTab === 'psa' && (
                <div className="cards-vertical-stack">
                  {/* CONNECTION SETTINGS Collapsible Card */}
                  <div className="collapsible-card">
                    <div
                      className={`collapsible-card-header ${!collapsedSections.cw_connection ? 'expanded' : ''}`}
                      onClick={() => toggleSection('cw_connection')}
                    >
                      <div className="collapsible-card-header-left">
                        <h3>Connection Settings</h3>
                      </div>
                      <div className="collapsible-card-header-right">
                        <span className={`status-badge ${
                          testResults.connectwise?.success ? 'connected' :
                          testResults.connectwise?.error ? 'failed' :
                          credentialsStatus.connectwise ? 'configured' : 'not-configured'
                        }`}>
                          {testResults.connectwise?.success ? 'Connected' :
                           testResults.connectwise?.error ? 'Failed' :
                           credentialsStatus.connectwise ? 'Configured' : 'Not Configured'}
                        </span>
                        <span className="collapse-arrow">{collapsedSections.cw_connection ? '▼' : '▲'}</span>
                      </div>
                    </div>

                    {!collapsedSections.cw_connection && (
                      <div className="collapsible-card-content">
                        <div className="credential-field">
                          <label>Server URL</label>
                          <input
                            type="text"
                            value={credentials.CW_SERVER_URL || ''}
                            onChange={(e) => handleCredentialChange('CW_SERVER_URL', e.target.value)}
                            placeholder="na.myconnectwise.net"
                          />
                          <small style={{ color: '#888', fontSize: '0.75rem' }}>Your ConnectWise instance domain</small>
                        </div>

                        <div className="credential-field">
                          <label>Company Name</label>
                          <input
                            type="text"
                            value={credentials.CW_COMPANY_NAME || ''}
                            onChange={(e) => handleCredentialChange('CW_COMPANY_NAME', e.target.value)}
                            placeholder="Your company identifier"
                          />
                        </div>

                        <div className="credential-field">
                          <label>Public Key</label>
                          {maskedFields.CW_PUBLIC_KEY ? (
                            <div
                              style={{
                                padding: '0.75rem',
                                backgroundColor: 'rgba(76, 154, 255, 0.1)',
                                border: '1px solid rgba(76, 154, 255, 0.3)',
                                borderRadius: '6px',
                                color: '#4c9aff',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                              }}
                              onClick={() => handleUnmaskField('CW_PUBLIC_KEY')}
                            >
                              🔒 Click here to change
                            </div>
                          ) : (
                            <input
                              type="password"
                              value={credentials.CW_PUBLIC_KEY || ''}
                              onChange={(e) => handleCredentialChange('CW_PUBLIC_KEY', e.target.value)}
                              placeholder="API public key"
                            />
                          )}
                        </div>

                        <div className="credential-field">
                          <label>Private Key</label>
                          {maskedFields.CW_PRIVATE_KEY ? (
                            <div
                              style={{
                                padding: '0.75rem',
                                backgroundColor: 'rgba(76, 154, 255, 0.1)',
                                border: '1px solid rgba(76, 154, 255, 0.3)',
                                borderRadius: '6px',
                                color: '#4c9aff',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                              }}
                              onClick={() => handleUnmaskField('CW_PRIVATE_KEY')}
                            >
                              🔒 Click here to change
                            </div>
                          ) : (
                            <input
                              type="password"
                              value={credentials.CW_PRIVATE_KEY || ''}
                              onChange={(e) => handleCredentialChange('CW_PRIVATE_KEY', e.target.value)}
                              placeholder="API private key"
                            />
                          )}
                        </div>

                        <div className="credential-field">
                          <label>Client ID</label>
                          <input
                            type="text"
                            value={credentials.CW_CLIENT_ID || ''}
                            onChange={(e) => handleCredentialChange('CW_CLIENT_ID', e.target.value)}
                            placeholder="Your API client identifier"
                          />
                        </div>

                        <button
                          className="btn-test"
                          onClick={() => handleTestConnection('connectwise')}
                          disabled={testingSource === 'connectwise'}
                        >
                          {testingSource === 'connectwise' ? 'Testing...' : 'Test Connection'}
                        </button>

                        {testResults.connectwise && (
                          <div className={`test-result ${testResults.connectwise.success ? 'success' : 'error'} ${testResults.connectwise.fading ? 'fading' : ''}`}>
                            {testResults.connectwise.success ? '✓' : '✗'} {testResults.connectwise.message || testResults.connectwise.error}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CONNECTWISE INTEGRATION Collapsible Card */}
                  <div className="collapsible-card">
                    <div
                      className={`collapsible-card-header ${!collapsedSections.cw_integration ? 'expanded' : ''}`}
                      onClick={() => toggleSection('cw_integration')}
                    >
                      <div className="collapsible-card-header-left">
                        <h3>ConnectWise Integration</h3>
                      </div>
                      <div className="collapsible-card-header-right">
                        <span className={`status-badge ${ticketingSettings.board_id ? 'configured' : 'not-configured'}`}>
                          {ticketingSettings.board_id ? 'Ticketing Configured' : 'Ticketing Not Configured'}
                        </span>
                        <span className="collapse-arrow">{collapsedSections.cw_integration ? '▼' : '▲'}</span>
                      </div>
                    </div>

                    {!collapsedSections.cw_integration && (
                      <div className="collapsible-card-content">
                        {/* Sync Filters Sub-Section */}
                        <h4 style={{ marginBottom: '1rem', color: '#4c9aff', borderBottom: '1px solid rgba(76, 154, 255, 0.3)', paddingBottom: '0.5rem' }}>Sync Filters</h4>
                        {/* Configuration Types Filter */}
                        <div className="credential-field">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <label>Configuration Types to Sync {cwConfigTypesLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                            <button
                              className="btn-test"
                              style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                              onClick={fetchCWConfigTypes}
                              disabled={cwConfigTypesLoading}
                            >
                              {cwConfigTypesLoading ? 'Loading...' : (cwConfigTypes.length > 0 ? 'Refresh Types' : 'Load Types')}
                            </button>
                          </div>
                          <small style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.5rem', display: 'block' }}>
                            {cwConfigTypesLoading
                              ? 'Fetching configuration types from ConnectWise...'
                              : (cwConfigTypes.length === 0
                                ? 'Click "Load Types" to fetch available configuration types from ConnectWise'
                                : 'Type to search and select configuration types. Leave empty to sync all types.')}
                          </small>

                          {cwConfigTypes.length > 0 && (
                            <div className="cw-filter-container">
                              <div className="cw-filter-input-wrapper">
                                <input
                                  type="text"
                                  className="cw-filter-input"
                                  placeholder="Type to search configuration types..."
                                  value={cwTypeSearch}
                                  onChange={(e) => setCwTypeSearch(e.target.value)}
                                />
                                {cwTypeSearch && (
                                  <div className="cw-dropdown">
                                    {cwConfigTypes
                                      .filter(type => type.toLowerCase().includes(cwTypeSearch.toLowerCase()))
                                      .slice(0, 10)
                                      .map(type => (
                                        <div
                                          key={type}
                                          className={`cw-dropdown-item ${cwSelectedTypes.includes(type) ? 'selected' : ''}`}
                                          onMouseDown={() => {
                                            toggleCWConfigType(type);
                                            setCwTypeSearch('');
                                          }}
                                        >
                                          <span className="cw-dropdown-name">{type}</span>
                                          {cwSelectedTypes.includes(type) && <span className="checkmark"> ✓</span>}
                                        </div>
                                      ))}
                                    {cwConfigTypes.filter(type => type.toLowerCase().includes(cwTypeSearch.toLowerCase())).length === 0 && (
                                      <div className="cw-dropdown-item disabled">No matches found</div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {cwSelectedTypes.length > 0 && (
                                <div className="cw-selected-tags">
                                  {cwSelectedTypes.map(type => (
                                    <span key={type} className="cw-tag">
                                      {type}
                                      <button
                                        className="cw-tag-remove"
                                        onClick={() => toggleCWConfigType(type)}
                                        title={`Remove ${type}`}
                                      >
                                        ✕
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Company Types Filter */}
                        <div className="credential-field">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <label>Company Types to Sync {cwCompanyTypesLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                            <button
                              className="btn-test"
                              style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                              onClick={fetchCWCompanyTypes}
                              disabled={cwCompanyTypesLoading}
                            >
                              {cwCompanyTypesLoading ? 'Loading...' : (cwCompanyTypes.length > 0 ? 'Refresh Types' : 'Load Types')}
                            </button>
                          </div>
                          <small style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.5rem', display: 'block' }}>
                            {cwCompanyTypesLoading
                              ? 'Fetching company types from ConnectWise...'
                              : (cwCompanyTypes.length === 0
                                ? 'Click "Load Types" to fetch available company types from ConnectWise'
                                : 'Type to search and select company types. Leave empty to sync all types.')}
                          </small>

                          {cwCompanyTypes.length > 0 && (
                            <div className="cw-filter-container">
                              <div className="cw-filter-input-wrapper">
                                <input
                                  type="text"
                                  className="cw-filter-input"
                                  placeholder="Type to search company types..."
                                  value={cwCompanyTypeSearch}
                                  onChange={(e) => setCwCompanyTypeSearch(e.target.value)}
                                />
                                {cwCompanyTypeSearch && (
                                  <div className="cw-dropdown">
                                    {cwCompanyTypes
                                      .filter(type => type.toLowerCase().includes(cwCompanyTypeSearch.toLowerCase()))
                                      .slice(0, 10)
                                      .map(type => (
                                        <div
                                          key={type}
                                          className={`cw-dropdown-item ${cwSelectedCompanyTypes.includes(type) ? 'selected' : ''}`}
                                          onMouseDown={() => {
                                            toggleCWCompanyType(type);
                                            setCwCompanyTypeSearch('');
                                          }}
                                        >
                                          <span className="cw-dropdown-name">{type}</span>
                                          {cwSelectedCompanyTypes.includes(type) && <span className="checkmark"> ✓</span>}
                                        </div>
                                      ))}
                                    {cwCompanyTypes.filter(type => type.toLowerCase().includes(cwCompanyTypeSearch.toLowerCase())).length === 0 && (
                                      <div className="cw-dropdown-item disabled">No matches found</div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {cwSelectedCompanyTypes.length > 0 && (
                                <div className="cw-selected-tags">
                                  {cwSelectedCompanyTypes.map(type => (
                                    <span key={type} className="cw-tag">
                                      {type}
                                      <button
                                        className="cw-tag-remove"
                                        onClick={() => toggleCWCompanyType(type)}
                                        title={`Remove ${type}`}
                                      >
                                        ✕
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Ticketing Configuration Sub-Section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid rgba(76, 154, 255, 0.3)', paddingBottom: '0.5rem' }}>
                          <h4 style={{ margin: 0, color: '#4c9aff' }}>Ticketing Configuration</h4>
                          <button
                            onClick={refreshCwCache}
                            disabled={cwCacheRefreshing}
                            title="Clear cached ConnectWise data and re-fetch from API"
                            style={{
                              background: 'transparent',
                              border: '1px solid #555',
                              color: '#888',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              cursor: cwCacheRefreshing ? 'wait' : 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.35rem'
                            }}
                          >
                            <span style={{ display: 'inline-block', animation: cwCacheRefreshing ? 'spin 1s linear infinite' : 'none' }}>&#x21bb;</span>
                            {cwCacheRefreshing ? 'Refreshing...' : 'Refresh from CW'}
                          </button>
                        </div>
                        <small style={{ color: '#888', fontSize: '0.75rem', marginBottom: '1rem', display: 'block' }}>
                          Configure ConnectWise ticketing for device offboarding. Tickets will be created when devices are removed from ImmyBot/CloudRadial.
                        </small>

                        {/* Show Configure button if not configured and not in configure mode */}
                        {!ticketingSettings.board_id && !ticketingConfiguring ? (
                          <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <p style={{ color: '#888', marginBottom: '1rem' }}>Ticketing is not configured. Click below to set up ConnectWise ticketing.</p>
                            <button
                              className="btn-test"
                              onClick={startTicketingConfiguration}
                              disabled={cwBoardsLoading}
                            >
                              {cwBoardsLoading ? 'Loading...' : 'Configure Ticketing'}
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Service Board */}
                            <div className="credential-field">
                              <label>Service Board * {cwBoardsLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                              <select
                                value={ticketingSettings.board_id || ''}
                                onChange={(e) => {
                                  const selected = cwBoards.find(b => b.id === parseInt(e.target.value));
                                  handleBoardChange(e.target.value, selected?.name || '');
                                }}
                                disabled={cwBoardsLoading}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #444', background: '#2a2a2a', color: '#fff' }}
                              >
                                <option value="">{cwBoardsLoading ? 'Loading service boards...' : 'Select a service board...'}</option>
                                {cwBoards.map(board => (
                                  <option key={board.id} value={board.id}>{board.name}</option>
                                ))}
                              </select>
                            </div>

                            {/* Type / SubType / Item Row (Cascading) */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                              <div className="credential-field">
                                <label>Type * {cwTypesLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                                <select
                                  value={ticketingSettings.type_id || ''}
                                  onChange={(e) => {
                                    const selected = cwBoardTypes.find(t => t.id === parseInt(e.target.value));
                                    handleTypeChange(e.target.value, selected?.name);
                                  }}
                                  disabled={!ticketingSettings.board_id || cwTypesLoading}
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #444', background: '#2a2a2a', color: '#fff' }}
                                >
                                  <option value="">{cwTypesLoading ? 'Loading...' : 'Select type...'}</option>
                                  {cwBoardTypes.map(type => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="credential-field">
                                <label>Sub-Type * {cwSubtypesLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                                <select
                                  value={ticketingSettings.subtype_id || ''}
                                  onChange={(e) => {
                                    const selected = cwBoardSubtypes.find(s => s.id === parseInt(e.target.value));
                                    handleSubtypeChange(e.target.value, selected?.name);
                                  }}
                                  disabled={!ticketingSettings.type_id || cwSubtypesLoading}
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #444', background: '#2a2a2a', color: '#fff' }}
                                >
                                  <option value="">{cwSubtypesLoading ? 'Loading...' : 'Select sub-type...'}</option>
                                  {cwBoardSubtypes.map(subtype => (
                                    <option key={subtype.id} value={subtype.id}>{subtype.name}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="credential-field">
                                <label>Item * {cwItemsLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                                <select
                                  value={ticketingSettings.item_id || ''}
                                  onChange={(e) => {
                                    const selected = cwBoardItems.find(i => i.id === parseInt(e.target.value));
                                    handleTicketingChange('item_id', e.target.value, selected?.name);
                                  }}
                                  disabled={!ticketingSettings.subtype_id || cwItemsLoading}
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #444', background: '#2a2a2a', color: '#fff' }}
                                >
                                  <option value="">{cwItemsLoading ? 'Loading...' : 'Select item...'}</option>
                                  {cwBoardItems.map(item => (
                                    <option key={item.id} value={item.id}>{item.name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Priority */}
                            <div className="credential-field">
                              <label>Priority * {cwPrioritiesLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                              <select
                                value={ticketingSettings.priority_id || ''}
                                onChange={(e) => {
                                  const selected = cwPriorities.find(p => p.id === parseInt(e.target.value));
                                  handleTicketingChange('priority_id', e.target.value, selected?.name);
                                }}
                                disabled={cwPrioritiesLoading}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #444', background: '#2a2a2a', color: '#fff' }}
                              >
                                <option value="">{cwPrioritiesLoading ? 'Loading...' : 'Select priority...'}</option>
                                {cwPriorities.map(priority => (
                                  <option key={priority.id} value={priority.id}>{priority.name}</option>
                                ))}
                              </select>
                            </div>

                            {/* Status Row */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              <div className="credential-field">
                                <label>Status for Open Tickets * {cwStatusesLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                                <select
                                  value={ticketingSettings.open_status_id || ''}
                                  onChange={(e) => {
                                    const selected = cwBoardStatuses.find(s => s.id === parseInt(e.target.value));
                                    handleTicketingChange('open_status_id', e.target.value, selected?.name);
                                  }}
                                  disabled={!ticketingSettings.board_id || cwStatusesLoading}
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #444', background: '#2a2a2a', color: '#fff' }}
                                >
                                  <option value="">{cwStatusesLoading ? 'Loading...' : 'Select open status...'}</option>
                                  {cwBoardStatuses.map(status => (
                                    <option key={status.id} value={status.id}>{status.name}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="credential-field">
                                <label>Status for Completed Tickets * {cwStatusesLoading && <span style={{ color: '#888', fontSize: '0.75rem' }}>(Loading...)</span>}</label>
                                <select
                                  value={ticketingSettings.closed_status_id || ''}
                                  onChange={(e) => {
                                    const selected = cwBoardStatuses.find(s => s.id === parseInt(e.target.value));
                                    handleTicketingChange('closed_status_id', e.target.value, selected?.name);
                                  }}
                                  disabled={!ticketingSettings.board_id || cwStatusesLoading}
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #444', background: '#2a2a2a', color: '#fff' }}
                                >
                                  <option value="">{cwStatusesLoading ? 'Loading...' : 'Select closed status...'}</option>
                                  {cwBoardStatuses.map(status => (
                                    <option key={status.id} value={status.id}>{status.name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                              <button
                                className="btn-test"
                                onClick={saveTicketingSettings}
                                disabled={ticketingSaving || !ticketingSettings.board_id || !ticketingSettings.type_id || !ticketingSettings.subtype_id || !ticketingSettings.item_id || !ticketingSettings.priority_id || !ticketingSettings.open_status_id || !ticketingSettings.closed_status_id}
                              >
                                {ticketingSaving ? 'Saving...' : 'Save Settings'}
                              </button>

                              <button
                                className="btn-test"
                                onClick={testTicketCreation}
                            disabled={ticketingLoading || !ticketingSettings.board_id || !ticketingSettings.open_status_id}
                            style={{ background: '#4c9aff' }}
                          >
                            {ticketingLoading ? 'Creating...' : 'Test Ticket'}
                          </button>
                        </div>

                            {/* Test Result */}
                            {ticketingTestResult && (
                              <div className={`test-result ${ticketingTestResult.status === 'success' ? 'success' : 'error'}`} style={{ marginTop: '1rem' }}>
                                {ticketingTestResult.status === 'success' ? '✓' : '✗'} {ticketingTestResult.message}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* EDR SECTION */}
                {credentialsSubTab === 'edr' && (
                <div className="credential-group">
                  <div className="group-header" onClick={() => toggleSection('edr')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <h2 style={{ margin: 0 }}>🛡️ EDR</h2>
                      <span style={{ fontSize: '0.85rem', color: '#888' }}>Huntress</span>
                    </div>
                    <span className="collapse-icon">{collapsedSections.edr ? '▼' : '▲'}</span>
                  </div>

                  {!collapsedSections.edr && (
                    <div className="group-content">
                      {/* HUNTRESS */}
                      <div className="credential-card">
                        <div className="credential-header">
                          <h3>Huntress</h3>
                          <span className={`status-badge ${
                            testResults.huntress?.success ? 'connected' :
                            testResults.huntress?.error ? 'failed' :
                            credentialsStatus.huntress ? 'configured' : 'not-configured'
                          }`}>
                            {testResults.huntress?.success ? 'Connected' :
                             testResults.huntress?.error ? 'Failed' :
                             credentialsStatus.huntress ? 'Configured' : 'Not Configured'}
                          </span>
                        </div>

                        <div className="credential-field">
                          <label>API Key</label>
                          {maskedFields.HUNTRESS_API_KEY ? (
                            <div
                              style={{
                                padding: '0.75rem',
                                backgroundColor: 'rgba(76, 154, 255, 0.1)',
                                border: '1px solid rgba(76, 154, 255, 0.3)',
                                borderRadius: '6px',
                                color: '#4c9aff',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                              }}
                              onClick={() => handleUnmaskField('HUNTRESS_API_KEY')}
                            >
                              🔒 Click here to change
                            </div>
                          ) : (
                            <input
                              type="password"
                              value={credentials.HUNTRESS_API_KEY || ''}
                              onChange={(e) => handleCredentialChange('HUNTRESS_API_KEY', e.target.value)}
                              placeholder="Enter Huntress API key"
                            />
                          )}
                        </div>

                        <div className="credential-field">
                          <label>API Secret</label>
                          {maskedFields.HUNTRESS_API_SECRET ? (
                            <div
                              style={{
                                padding: '0.75rem',
                                backgroundColor: 'rgba(76, 154, 255, 0.1)',
                                border: '1px solid rgba(76, 154, 255, 0.3)',
                                borderRadius: '6px',
                                color: '#4c9aff',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                              }}
                              onClick={() => handleUnmaskField('HUNTRESS_API_SECRET')}
                            >
                              🔒 Click here to change
                            </div>
                          ) : (
                            <input
                              type="password"
                              value={credentials.HUNTRESS_API_SECRET || ''}
                              onChange={(e) => handleCredentialChange('HUNTRESS_API_SECRET', e.target.value)}
                              placeholder="Enter Huntress API secret"
                            />
                          )}
                        </div>

                        <button
                          className="btn-test"
                          onClick={() => handleTestConnection('huntress')}
                          disabled={testingSource === 'huntress'}
                        >
                          {testingSource === 'huntress' ? 'Testing...' : 'Test Connection'}
                        </button>

                        {testResults.huntress && (
                          <div className={`test-result ${testResults.huntress.success ? 'success' : 'error'} ${testResults.huntress.fading ? 'fading' : ''}`}>
                            {testResults.huntress.success ? '✓' : '✗'} {testResults.huntress.message || testResults.huntress.error}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* SERVICE DELIVERY SECTION */}
                {credentialsSubTab === 'service-delivery' && (
                <div className="credential-group">
                  <div className="group-header" onClick={() => toggleSection('service_delivery')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <h2 style={{ margin: 0 }}>📊 Service Delivery</h2>
                      <span style={{ fontSize: '0.85rem', color: '#888' }}>CloudRadial</span>
                    </div>
                    <span className="collapse-icon">{collapsedSections.service_delivery ? '▼' : '▲'}</span>
                  </div>

                  {!collapsedSections.service_delivery && (
                    <div className="group-content">
                      {/* CLOUDRADIAL */}
                      <div className="credential-card">
                        <div className="credential-header">
                          <h3>CloudRadial</h3>
                          <span className={`status-badge ${
                            testResults.cloudradial?.success ? 'connected' :
                            testResults.cloudradial?.error ? 'failed' :
                            credentialsStatus.cloudradial ? 'configured' : 'not-configured'
                          }`}>
                            {testResults.cloudradial?.success ? 'Connected' :
                             testResults.cloudradial?.error ? 'Failed' :
                             credentialsStatus.cloudradial ? 'Configured' : 'Not Configured'}
                          </span>
                        </div>

                        <div className="credential-field">
                          <label>Public Key</label>
                          <input
                            type="password"
                            value={credentials.CLOUDRADIAL_PUBLIC_KEY || ''}
                            onChange={(e) => handleCredentialChange('CLOUDRADIAL_PUBLIC_KEY', e.target.value)}
                            placeholder="Enter CloudRadial public key"
                          />
                        </div>

                        <div className="credential-field">
                          <label>Private Key</label>
                          <input
                            type="password"
                            value={credentials.CLOUDRADIAL_PRIVATE_KEY || ''}
                            onChange={(e) => handleCredentialChange('CLOUDRADIAL_PRIVATE_KEY', e.target.value)}
                            placeholder="Enter CloudRadial private key"
                          />
                        </div>

                        <div className="credential-field">
                          <label>Base URL (Optional)</label>
                          <input
                            type="text"
                            value={credentials.CLOUDRADIAL_BASE_URL || ''}
                            onChange={(e) => handleCredentialChange('CLOUDRADIAL_BASE_URL', e.target.value)}
                            placeholder="https://api.cloudradial.com"
                          />
                        </div>

                        <button
                          className="btn-test"
                          onClick={() => handleTestConnection('cloudradial')}
                          disabled={testingSource === 'cloudradial'}
                        >
                          {testingSource === 'cloudradial' ? 'Testing...' : 'Test Connection'}
                        </button>

                        {testResults.cloudradial && (
                          <div className={`test-result ${testResults.cloudradial.success ? 'success' : 'error'} ${testResults.cloudradial.fading ? 'fading' : ''}`}>
                            {testResults.cloudradial.success ? '✓' : '✗'} {testResults.cloudradial.message || testResults.cloudradial.error}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* MICROSOFT SECTION */}
                {credentialsSubTab === 'microsoft' && (
                <div className="credential-group">
                  <div className="group-header" onClick={() => toggleSection('microsoft')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <h2 style={{ margin: 0 }}>☁️ Microsoft Intune / Entra ID</h2>
                      <span style={{ fontSize: '0.85rem', color: '#888' }}>GDAP Integration</span>
                    </div>
                    <span className="collapse-icon">{collapsedSections.microsoft ? '▼' : '▲'}</span>
                  </div>

                  {!collapsedSections.microsoft && (
                    <div className="group-content">
                      {/* Microsoft Partner App Credentials */}
                      <div className="credential-card">
                        <div className="credential-header">
                          <h3>Partner App Registration</h3>
                          <span className={`status-badge ${
                            testResults.microsoft?.success ? 'connected' :
                            testResults.microsoft?.error ? 'failed' :
                            credentialsStatus.microsoft ? 'configured' : 'not-configured'
                          }`}>
                            {testResults.microsoft?.success ? 'Connected' :
                             testResults.microsoft?.error ? 'Failed' :
                             credentialsStatus.microsoft ? 'Configured' : 'Not Configured'}
                          </span>
                        </div>

                        <p className="credential-help" style={{ color: '#9ca3af', marginBottom: '1rem', fontSize: '0.9rem' }}>
                          Create a multi-tenant Azure AD app in your MSP tenant. Each customer will need to grant admin consent via the Organizations tab.
                        </p>

                        <div className="credential-field">
                          <label>Application (Client) ID</label>
                          <input
                            type="text"
                            value={credentials.MICROSOFT_CLIENT_ID || ''}
                            onChange={(e) => handleCredentialChange('MICROSOFT_CLIENT_ID', e.target.value)}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          />
                        </div>

                        <div className="credential-field">
                          <label>Client Secret</label>
                          {maskedFields.MICROSOFT_CLIENT_SECRET ? (
                            <div
                              style={{
                                padding: '0.75rem',
                                backgroundColor: 'rgba(76, 154, 255, 0.1)',
                                border: '1px solid rgba(76, 154, 255, 0.3)',
                                borderRadius: '4px',
                                color: '#4c9aff',
                                cursor: 'pointer',
                                textAlign: 'center'
                              }}
                              onClick={() => handleUnmaskField('MICROSOFT_CLIENT_SECRET')}
                            >
                              🔒 Click here to change
                            </div>
                          ) : (
                            <input
                              type="password"
                              value={credentials.MICROSOFT_CLIENT_SECRET || ''}
                              onChange={(e) => handleCredentialChange('MICROSOFT_CLIENT_SECRET', e.target.value)}
                              placeholder="Enter client secret"
                            />
                          )}
                        </div>

                        <div className="credential-field">
                          <label>MSP Tenant ID</label>
                          <input
                            type="text"
                            value={credentials.MICROSOFT_TENANT_ID || ''}
                            onChange={(e) => handleCredentialChange('MICROSOFT_TENANT_ID', e.target.value)}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          />
                        </div>

                        <div className="credential-field">
                          <label>Redirect URI (Auto-generated if empty)</label>
                          <input
                            type="text"
                            value={credentials.MICROSOFT_REDIRECT_URI || ''}
                            onChange={(e) => handleCredentialChange('MICROSOFT_REDIRECT_URI', e.target.value)}
                            placeholder="https://console.assetmesh.io/api/microsoft/callback"
                          />
                          <span className="field-hint" style={{ fontSize: '0.8rem', color: '#888' }}>
                            Add this as a redirect URI in your Azure AD app registration
                          </span>
                        </div>

                        <button
                          className="btn-test"
                          onClick={() => handleTestConnection('microsoft')}
                          disabled={testingSource === 'microsoft'}
                        >
                          {testingSource === 'microsoft' ? 'Testing...' : 'Test Connection'}
                        </button>

                        {testResults.microsoft && (
                          <div className={`test-result ${testResults.microsoft.success ? 'success' : 'error'} ${testResults.microsoft.fading ? 'fading' : ''}`}>
                            {testResults.microsoft.success ? '✓' : '✗'} {testResults.microsoft.message || testResults.microsoft.error}
                            {testResults.microsoft.organization_count !== undefined && (
                              <span> ({testResults.microsoft.organization_count} tenants with consent)</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Azure App Permissions Info */}
                      <div className="credential-card" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                        <h3 style={{ color: '#60a5fa' }}>Required Azure App Permissions</h3>
                        <p style={{ color: '#9ca3af', marginBottom: '0.5rem' }}>
                          Add these <strong>Application</strong> permissions in your Azure AD app registration:
                        </p>
                        <ul style={{ color: '#d1d5db', margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                          <li><code>DeviceManagementManagedDevices.Read.All</code> - Read Intune managed devices</li>
                          <li><code>Device.Read.All</code> - Read Azure AD devices</li>
                          <li><code>Organization.Read.All</code> - Read tenant info</li>
                        </ul>
                        <p style={{ color: '#9ca3af', marginTop: '1rem' }}>
                          After adding permissions, click "Grant admin consent" for your MSP tenant.
                          Then, for each customer, use the consent button in the Organizations tab.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>

              <div className="credentials-info">
                <h4>Security Information</h4>
                <div style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  marginBottom: '0.75rem',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  backgroundColor: credentialsEnvironment === 'local' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(52, 211, 153, 0.2)',
                  color: credentialsEnvironment === 'local' ? '#fbbf24' : '#34d399',
                  border: `1px solid ${credentialsEnvironment === 'local' ? 'rgba(251, 191, 36, 0.4)' : 'rgba(52, 211, 153, 0.4)'}`
                }}>
                  {credentialsEnvironment === 'local' ? '📁 Local Mode (.env file)' : '🔐 Production Mode (Secrets Manager)'}
                </div>
                <p>Credentials are stored securely:</p>
                <ul>
                  <li><strong>Development:</strong> Stored in .env file (never committed to git)</li>
                  <li><strong>Production:</strong> Stored in Azure Key Vault with managed identity</li>
                  <li>All API calls use HTTPS encryption</li>
                  <li>Changes are logged in the event log</li>
                </ul>
              </div>
              </>
            )}
          </div>
        )}

        {/* GENERAL TAB */}
        {activeTab === 'general' && (
          <div className="general-settings-section">
            <div className="section-header">
              <div>
                <h2>General Settings</h2>
                <p>Configure application-wide settings for device status thresholds and display preferences</p>
              </div>
            </div>

            {generalSettingsLoading ? (
              <div className="loading">Loading settings...</div>
            ) : (
              <div className="settings-form">
                <div className="settings-group">
                  <h3>Device Status Thresholds</h3>
                  <p className="settings-description">
                    Configure how many days without sync before a device is flagged with different status levels.
                  </p>

                  <div className="setting-row">
                    <label htmlFor="stale-threshold">
                      <strong>Stale Threshold (days)</strong>
                      <span className="setting-help">Devices not seen for this many days are marked as "Stale"</span>
                    </label>
                    <input
                      id="stale-threshold"
                      type="number"
                      min="1"
                      max="365"
                      value={generalSettings.stale_threshold_days}
                      onChange={(e) => setGeneralSettings({
                        ...generalSettings,
                        stale_threshold_days: parseInt(e.target.value) || 30
                      })}
                      className="setting-input"
                    />
                  </div>

                  <div className="setting-row">
                    <label htmlFor="warning-threshold">
                      <strong>Warning Threshold (days)</strong>
                      <span className="setting-help">Devices not seen for this many days show a warning indicator</span>
                    </label>
                    <input
                      id="warning-threshold"
                      type="number"
                      min="1"
                      max="365"
                      value={generalSettings.warning_threshold_days}
                      onChange={(e) => setGeneralSettings({
                        ...generalSettings,
                        warning_threshold_days: parseInt(e.target.value) || 7
                      })}
                      className="setting-input"
                    />
                  </div>

                  <div className="setting-row">
                    <label htmlFor="drift-threshold">
                      <strong>Sync Drift Threshold (days)</strong>
                      <span className="setting-help">If sync times between sources differ by more than this, show "Sync Drift" warning</span>
                    </label>
                    <input
                      id="drift-threshold"
                      type="number"
                      min="1"
                      max="30"
                      value={generalSettings.sync_drift_threshold_days}
                      onChange={(e) => setGeneralSettings({
                        ...generalSettings,
                        sync_drift_threshold_days: parseInt(e.target.value) || 3
                      })}
                      className="setting-input"
                    />
                  </div>
                </div>

                <div className="settings-group">
                  <h3>Display Settings</h3>

                  <div className="setting-row">
                    <label htmlFor="timezone">
                      <strong>Timezone</strong>
                      <span className="setting-help">Timezone for displaying dates and times</span>
                    </label>
                    <select
                      id="timezone"
                      value={generalSettings.timezone}
                      onChange={(e) => setGeneralSettings({
                        ...generalSettings,
                        timezone: e.target.value
                      })}
                      className="setting-input"
                    >
                      <option value="America/Vancouver">Pacific Time (America/Vancouver)</option>
                      <option value="America/Edmonton">Mountain Time (America/Edmonton)</option>
                      <option value="America/Winnipeg">Central Time (America/Winnipeg)</option>
                      <option value="America/Toronto">Eastern Time (America/Toronto)</option>
                      <option value="America/Halifax">Atlantic Time (America/Halifax)</option>
                      <option value="America/St_Johns">Newfoundland Time (America/St_Johns)</option>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time (America/New_York)</option>
                      <option value="America/Chicago">Central Time (America/Chicago)</option>
                      <option value="America/Denver">Mountain Time (America/Denver)</option>
                      <option value="America/Los_Angeles">Pacific Time (America/Los_Angeles)</option>
                    </select>
                  </div>
                </div>

                <div className="settings-group">
                  <h3>Diagnostic Settings</h3>
                  <p className="settings-description">
                    Configure diagnostic logging for troubleshooting integration syncs and application issues.
                  </p>

                  <div className="setting-row">
                    <label htmlFor="log-level">
                      <strong>Log Level</strong>
                      <span className="setting-help">Controls the verbosity of application logging. Set to "Comprehensive" to enable detailed diagnostic output for troubleshooting. "Debug" mode includes full request/response data for API calls.</span>
                    </label>
                    <select
                      id="log-level"
                      value={generalSettings.log_level || 'minimal'}
                      onChange={(e) => setGeneralSettings({
                        ...generalSettings,
                        log_level: e.target.value
                      })}
                      className="setting-input"
                    >
                      <option value="minimal">Minimal (Default)</option>
                      <option value="comprehensive">Comprehensive</option>
                      <option value="debug">Debug (Full Diagnostics)</option>
                    </select>
                  </div>
                </div>

                <div className="settings-actions">
                  <button
                    onClick={saveGeneralSettings}
                    disabled={generalSettingsSaving}
                    className="btn-save"
                  >
                    {generalSettingsSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PRODUCTION TAB (v2.5+) */}
        {activeTab === 'production' && (
          <div className="production-settings-section">
            <div className="section-header">
              <div>
                <h2>Production Settings</h2>
                <p>Configure CORS, rate limiting, and other production deployment settings</p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  onClick={() => setShowProductionHelp(!showProductionHelp)}
                  className="btn-action-small"
                  style={{ padding: '0.5rem 1rem' }}
                >
                  {showProductionHelp ? 'Hide Help' : 'Show Help'}
                </button>
                {productionVersion && (
                  <div className="version-badge" style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'var(--bg-tertiary)',
                    borderRadius: '8px',
                    fontSize: '0.85rem'
                  }}>
                    <strong>v{productionVersion.version}</strong> "{productionVersion.codename}"
                  </div>
                )}
              </div>
            </div>

            {/* Help Section */}
            {showProductionHelp && (
              <div className="settings-group" style={{
                backgroundColor: 'rgba(52, 152, 219, 0.1)',
                border: '1px solid #3498db',
                padding: '1.5rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ marginTop: 0, color: '#3498db' }}>Production Settings Help</h3>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ marginBottom: '0.5rem' }}>CORS (Cross-Origin Resource Sharing)</h4>
                  <p style={{ margin: '0 0 0.5rem 0', color: 'var(--text-secondary)' }}>
                    Controls which domains can access your API. This is a critical security setting.
                  </p>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Value</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Use Case</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Security</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>*</td>
                        <td style={{ padding: '0.5rem' }}>Development only</td>
                        <td style={{ padding: '0.5rem', color: '#e74c3c' }}>Low - allows any origin</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>https://app.example.com</td>
                        <td style={{ padding: '0.5rem' }}>Production (recommended)</td>
                        <td style={{ padding: '0.5rem', color: '#27ae60' }}>High - specific origin only</td>
                      </tr>
                    </tbody>
                  </table>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <strong>Recommendation:</strong> In production, set to your frontend's exact URL (e.g., <code>https://assets.yourcompany.com</code>)
                  </p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ marginBottom: '0.5rem' }}>Rate Limiting</h4>
                  <p style={{ margin: '0 0 0.5rem 0', color: 'var(--text-secondary)' }}>
                    Protects against API abuse and denial-of-service attacks. Delete operations have a stricter limit of 30/minute.
                  </p>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Setting</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Default</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Recommendation</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.5rem' }}>Default Rate</td>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>200 per minute</td>
                        <td style={{ padding: '0.5rem' }}>Good for most deployments</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.5rem' }}>Delete Operations</td>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>30 per minute</td>
                        <td style={{ padding: '0.5rem' }}>Fixed - prevents mass deletion</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.5rem' }}>Storage Backend</td>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>memory://</td>
                        <td style={{ padding: '0.5rem' }}>Use Redis for multi-instance deployments</td>
                      </tr>
                    </tbody>
                  </table>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <strong>Format:</strong> <code>{'{number}'} per {'{second|minute|hour|day}'}</code> (e.g., "100 per minute", "5000 per hour")
                  </p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ marginBottom: '0.5rem' }}>Role-Based Access Control</h4>
                  <p style={{ margin: '0 0 0.5rem 0', color: 'var(--text-secondary)' }}>
                    Asset Mesh uses fixed role names to prevent lockouts. Roles must be defined in your Azure AD App Registration.
                  </p>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Role</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Access Level</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem' }}>Permissions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.5rem' }}><strong style={{ color: '#9b59b6' }}>{productionRoles.admin}</strong></td>
                        <td style={{ padding: '0.5rem' }}>Full Access</td>
                        <td style={{ padding: '0.5rem' }}>All features + Settings pages + Production settings</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.5rem' }}><strong style={{ color: '#3498db' }}>{productionRoles.technician}</strong></td>
                        <td style={{ padding: '0.5rem' }}>Operational</td>
                        <td style={{ padding: '0.5rem' }}>View, Remediation, Deployments, Delete devices</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.5rem' }}><strong style={{ color: '#27ae60' }}>{productionRoles.viewer}</strong></td>
                        <td style={{ padding: '0.5rem' }}>Read-Only</td>
                        <td style={{ padding: '0.5rem' }}>View dashboard and device details only</td>
                      </tr>
                    </tbody>
                  </table>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <strong>Note:</strong> Role names are fixed and cannot be changed to prevent accidental lockouts.
                  </p>
                </div>

                <div>
                  <h4 style={{ marginBottom: '0.5rem' }}>Security Notes</h4>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--text-secondary)' }}>
                    <li>Only users with the <strong>{productionRequiredRole || 'Admin'}</strong> role can access these settings</li>
                    <li>All changes require explicit confirmation before saving</li>
                    <li>Changes are logged with the user's email for audit purposes</li>
                    <li>Settings are stored encrypted in your secrets backend</li>
                    <li><strong>App restart required</strong> for changes to take effect</li>
                  </ul>
                </div>
              </div>
            )}

            {productionSettingsLoading ? (
              <div className="loading">Loading production settings...</div>
            ) : productionAccessDenied ? (
              <div className="settings-group" style={{
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                border: '1px solid #e74c3c',
                padding: '2rem',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#e74c3c', marginTop: 0 }}>Access Denied</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  You do not have permission to view or modify production settings.
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  This page requires the <strong>{productionRequiredRole || 'Admin'}</strong> role in Azure AD.
                  Contact your administrator to request access.
                </p>
              </div>
            ) : (
              <div className="settings-form">
                {/* Admin Role Badge */}
                {productionRequiredRole && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: 'rgba(155, 89, 182, 0.1)',
                    border: '1px solid #9b59b6',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontSize: '0.85rem'
                  }}>
                    <span style={{ color: '#9b59b6' }}>Required Role:</span>
                    <strong style={{ color: '#9b59b6' }}>{productionRequiredRole}</strong>
                  </div>
                )}

                {/* Backend Info */}
                {productionBackend && (
                  <div className="settings-group" style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                  }}>
                    <h3 style={{ marginTop: 0 }}>Secrets Backend</h3>
                    <div className="setting-row" style={{ marginBottom: '0.5rem' }}>
                      <span className="detail-label">Backend Type:</span>
                      <span className="detail-value" style={{
                        fontFamily: 'monospace',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: '4px'
                      }}>
                        {productionBackend.backend}
                      </span>
                    </div>
                    <div className="setting-row" style={{ marginBottom: '0.5rem' }}>
                      <span className="detail-label">Tenant ID:</span>
                      <span className="detail-value" style={{ fontFamily: 'monospace' }}>
                        {productionBackend.tenant_id}
                      </span>
                    </div>
                    {productionBackend.encryption_configured !== 'n/a' && (
                      <div className="setting-row">
                        <span className="detail-label">Encryption:</span>
                        <span className="detail-value" style={{
                          color: productionBackend.encryption_configured ? 'var(--green)' : 'var(--red)'
                        }}>
                          {productionBackend.encryption_configured ? 'Configured' : 'Not Configured'}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Restart Warning */}
                <div className="settings-group" style={{
                  backgroundColor: 'rgba(255, 159, 67, 0.1)',
                  border: '1px solid #ff9f43',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ margin: 0, color: '#ff9f43', fontWeight: '500' }}>
                    ⚠️ Changes to these settings require an application restart to take effect.
                  </p>
                </div>

                {/* CORS Settings */}
                <div className="settings-group">
                  <h3>CORS Configuration</h3>
                  <p className="settings-description">
                    Control which origins (domains) can access the API. Use <code>*</code> for development or specify allowed origins for production.
                  </p>

                  <div className="setting-row">
                    <label htmlFor="cors-origins">
                      <strong>Allowed Origins</strong>
                      <span className="setting-help">
                        Use <code>*</code> for all origins (development) or comma-separated URLs for production (e.g., https://app.example.com)
                      </span>
                    </label>
                    <input
                      id="cors-origins"
                      type="text"
                      value={productionSettings.cors_origins}
                      onChange={(e) => setProductionSettings({
                        ...productionSettings,
                        cors_origins: e.target.value
                      })}
                      placeholder="* or https://app.example.com"
                      className="setting-input"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                </div>

                {/* Rate Limiting Settings */}
                <div className="settings-group">
                  <h3>Rate Limiting</h3>
                  <p className="settings-description">
                    Protect the API from abuse by limiting request rates. Delete operations have stricter limits (30/min).
                  </p>

                  <div className="setting-row">
                    <label htmlFor="rate-limit-default">
                      <strong>Default Rate Limit</strong>
                      <span className="setting-help">
                        Format: "{'{number}'} per {'{second|minute|hour|day}'}" (e.g., "200 per minute")
                      </span>
                    </label>
                    <input
                      id="rate-limit-default"
                      type="text"
                      value={productionSettings.rate_limit_default}
                      onChange={(e) => setProductionSettings({
                        ...productionSettings,
                        rate_limit_default: e.target.value
                      })}
                      placeholder="200 per minute"
                      className="setting-input"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>

                  <div className="setting-row">
                    <label htmlFor="rate-limit-storage">
                      <strong>Storage Backend</strong>
                      <span className="setting-help">
                        Use <code>memory://</code> for single instance or <code>redis://host:port</code> for distributed deployments
                      </span>
                    </label>
                    <select
                      id="rate-limit-storage"
                      value={productionSettings.rate_limit_storage.startsWith('redis://') ? 'redis' : 'memory'}
                      onChange={(e) => setProductionSettings({
                        ...productionSettings,
                        rate_limit_storage: e.target.value === 'redis' ? 'redis://localhost:6379' : 'memory://'
                      })}
                      className="setting-input"
                    >
                      <option value="memory">In-Memory (single instance)</option>
                      <option value="redis">Redis (distributed)</option>
                    </select>
                  </div>

                  {productionSettings.rate_limit_storage.startsWith('redis://') && (
                    <div className="setting-row">
                      <label htmlFor="rate-limit-redis-url">
                        <strong>Redis URL</strong>
                        <span className="setting-help">
                          Full Redis connection URL (e.g., redis://localhost:6379 or redis://user:pass@host:port/db)
                        </span>
                      </label>
                      <input
                        id="rate-limit-redis-url"
                        type="text"
                        value={productionSettings.rate_limit_storage}
                        onChange={(e) => setProductionSettings({
                          ...productionSettings,
                          rate_limit_storage: e.target.value
                        })}
                        placeholder="redis://localhost:6379"
                        className="setting-input"
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                  )}
                </div>

                <div className="settings-actions">
                  <button
                    onClick={saveProductionSettings}
                    disabled={productionSettingsSaving}
                    className="btn-save"
                  >
                    {productionSettingsSaving ? 'Saving...' : 'Save Production Settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* DUPLICATES TAB - Microsoft Duplicate Device Cleanup */}
        {activeTab === 'duplicates' && (
          <div className="duplicates-settings-section">
            <div className="section-header">
              <div>
                <h2>Microsoft Duplicate Cleanup</h2>
                <p>Find and remove duplicate Microsoft device records</p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select
                  value={duplicatesOrgFilter}
                  onChange={(e) => setDuplicatesOrgFilter(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9rem',
                    minWidth: '200px'
                  }}
                >
                  <option value="">All Organizations</option>
                  {/* Get unique master org names from mappings */}
                  {[...new Set(organizations.map(org => org.master_org_name))]
                    .filter(name => name && name !== 'Unknown')
                    .sort((a, b) => a.localeCompare(b))
                    .map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))
                  }
                </select>
                <button
                  onClick={() => fetchDuplicatesScan(duplicatesOrgFilter)}
                  disabled={duplicatesScanLoading}
                  className="btn-action"
                  style={{ padding: '0.5rem 1rem' }}
                >
                  {duplicatesScanLoading ? 'Scanning...' : 'Scan for Duplicates'}
                </button>
                {duplicatesScanData && (queueTotals.queued > 0 || queueTotals.processing > 0 || queueTotals.verified > 0) && (
                  <div style={{
                    padding: '0.4rem 0.8rem',
                    background: 'var(--bg-secondary, #1e293b)',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    color: '#94a3b8'
                  }}>
                    <span style={{ fontWeight: 500 }}>Verification Queue:</span>
                    {queueTotals.queued > 0 && <span style={{ color: '#3b82f6' }}>{queueTotals.queued} queued</span>}
                    {queueTotals.processing > 0 && <span style={{ color: '#f59e0b' }}>{queueTotals.processing} processing</span>}
                    {queueTotals.verified > 0 && <span style={{ color: '#22c55e' }}>{queueTotals.verified} verified</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Global Verification Status Tray - always visible regardless of scan state */}
            {(verificationQueueCount > 0 || Object.values(verificationSessions).some(s => s.status === 'scheduled' || s.status === 'completed') || scheduledVerifications.length > 0) && (
              <div style={{
                background: verificationQueueCount > 0 ? 'rgba(245, 158, 11, 0.15)' : Object.values(verificationSessions).some(s => s.status === 'scheduled') || scheduledVerifications.length > 0 ? 'rgba(139, 92, 246, 0.15)' : 'rgba(34, 197, 94, 0.1)',
                border: `1px solid ${verificationQueueCount > 0 ? '#f59e0b' : Object.values(verificationSessions).some(s => s.status === 'scheduled') || scheduledVerifications.length > 0 ? '#8b5cf6' : '#22c55e'}`,
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                {/* Top: On-demand verification progress */}
                {verificationQueueCount > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: '#f59e0b' }}>
                        Verification in progress: {verificationQueueCount} device{verificationQueueCount > 1 ? 's' : ''} in queue
                      </strong>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                        Polling ImmyBot (up to 5 min each). Results will update automatically.
                      </div>
                    </div>
                    {Object.values(verificationSessions).filter(s => s.status === 'completed').length > 0 && (
                      <div style={{ color: '#22c55e', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                        ✅ {Object.values(verificationSessions).filter(s => s.status === 'completed').length} completed
                      </div>
                    )}
                  </div>
                )}
                {verificationQueueCount === 0 && Object.values(verificationSessions).some(s => s.status === 'completed') && scheduledVerifications.length === 0 && !Object.values(verificationSessions).some(s => s.status === 'scheduled') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ color: '#22c55e', fontSize: '0.9rem' }}>
                      ✅ {Object.values(verificationSessions).filter(s => s.status === 'completed').length} verification{Object.values(verificationSessions).filter(s => s.status === 'completed').length > 1 ? 's' : ''} completed
                    </div>
                  </div>
                )}
                {/* Bottom: Background/scheduled checks with timing */}
                {(Object.values(verificationSessions).some(s => s.status === 'scheduled') || scheduledVerifications.length > 0) && (() => {
                  const scheduledCount = scheduledVerifications.filter(sv => sv.verification_status === 'ScheduledPending').length;
                  const pendingCount = scheduledVerifications.filter(sv => sv.verification_status === 'VerificationPending').length;
                  const totalBg = scheduledCount + pendingCount;
                  // Find the soonest next check time
                  const nextChecks = scheduledVerifications
                    .filter(sv => sv.next_scheduled_check)
                    .map(sv => new Date(sv.next_scheduled_check))
                    .filter(d => !isNaN(d.getTime()))
                    .sort((a, b) => a - b);
                  const soonestCheck = nextChecks.length > 0 ? nextChecks[0] : null;
                  // Find the most recent update (last check ran)
                  const lastUpdates = scheduledVerifications
                    .filter(sv => sv.updated_at)
                    .map(sv => new Date(sv.updated_at))
                    .filter(d => !isNaN(d.getTime()))
                    .sort((a, b) => b - a);
                  const lastRun = lastUpdates.length > 0 ? lastUpdates[0] : null;

                  const formatRelative = (date) => {
                    if (!date) return 'N/A';
                    const now = new Date();
                    const diffMs = date - now;
                    const absDiffMs = Math.abs(diffMs);
                    const minutes = Math.floor(absDiffMs / 60000);
                    const hours = Math.floor(minutes / 60);
                    const remainingMins = minutes % 60;
                    if (diffMs > 0) {
                      // Future
                      if (hours > 0) return `in ${hours}h ${remainingMins}m`;
                      return `in ${minutes}m`;
                    } else {
                      // Past
                      if (hours > 0) return `${hours}h ${remainingMins}m ago`;
                      if (minutes > 0) return `${minutes}m ago`;
                      return 'just now';
                    }
                  };

                  return (
                    <div style={{
                      borderTop: verificationQueueCount > 0 || Object.values(verificationSessions).some(s => s.status === 'completed') ? '1px solid rgba(139, 92, 246, 0.3)' : 'none',
                      paddingTop: verificationQueueCount > 0 || Object.values(verificationSessions).some(s => s.status === 'completed') ? '0.75rem' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <strong style={{ color: '#8b5cf6', fontSize: '0.9rem' }}>
                          📅 {totalBg || Object.values(verificationSessions).filter(s => s.status === 'scheduled').length} background verification{(totalBg || Object.values(verificationSessions).filter(s => s.status === 'scheduled').length) > 1 ? 's' : ''} scheduled
                        </strong>
                      </div>
                      <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {lastRun && (
                          <span>Last checked: <strong style={{ color: 'var(--text-primary)' }}>{formatRelative(lastRun)}</strong></span>
                        )}
                        {soonestCheck && (
                          <span>Next check: <strong style={{ color: '#8b5cf6' }}>{formatRelative(soonestCheck)}</strong></span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {duplicatesScanLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner"></div>
                <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Scanning for duplicate devices...</p>
              </div>
            ) : duplicatesScanData ? (
              <>
                {/* Summary Stats */}
                <div className="duplicates-summary" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div className="stat-card" style={{
                    background: 'var(--bg-secondary)',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3b82f6' }}>
                      {duplicatesScanData.summary?.total_organizations || 0}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Organizations with Duplicates</div>
                  </div>
                  <div className="stat-card" style={{
                    background: 'var(--bg-secondary)',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#f59e0b' }}>
                      {duplicatesScanData.summary?.total_duplicate_groups || 0}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Duplicate Groups</div>
                  </div>
                  <div className="stat-card" style={{
                    background: 'var(--bg-secondary)',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }}>
                      {duplicatesScanData.summary?.total_stale_devices || 0}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                      Stale Records (&gt;{duplicatesScanData.summary?.stale_threshold_days || 30} days)
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#3b82f6' }}>How Duplicate Detection Works</h4>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    <li>Devices are grouped by <strong>hostname + organization</strong></li>
                    <li>Groups with different serial numbers are excluded (likely different physical devices)</li>
                    <li><strong>Sorted by Last Sign-In</strong> (ApproximateLastSignInDateTime) — the most recently active device is shown first</li>
                    <li>Devices with no sign-in activity fall back to registration date</li>
                    <li>Stale devices (no activity for {duplicatesScanData.summary?.stale_threshold_days || 30}+ days) are marked for deletion</li>
                  </ul>
                </div>

                {/* Filter indicator */}
                {duplicatesOrgFilter && (
                  <div style={{
                    background: 'rgba(168, 85, 247, 0.1)',
                    border: '1px solid #a855f7',
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ color: '#a855f7' }}>
                      <strong>Filtered:</strong> Showing duplicates for <strong>{duplicatesOrgFilter}</strong> only
                    </span>
                    <button
                      onClick={() => {
                        setDuplicatesOrgFilter('');
                        fetchDuplicatesScan('');
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid #a855f7',
                        color: '#a855f7',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Clear Filter
                    </button>
                  </div>
                )}

                {/* Device Type Sub-Tabs */}
                <div className="sub-tabs" style={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginBottom: '1.5rem',
                  borderBottom: '2px solid rgba(76, 154, 255, 0.2)',
                  paddingBottom: '0.5rem'
                }}>
                  <button
                    onClick={() => setDuplicatesSubTab('desktop')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: duplicatesSubTab === 'desktop' ? 'rgba(76, 154, 255, 0.2)' : 'transparent',
                      border: 'none',
                      borderRadius: '6px 6px 0 0',
                      color: duplicatesSubTab === 'desktop' ? '#4c9aff' : '#888',
                      cursor: 'pointer',
                      fontWeight: duplicatesSubTab === 'desktop' ? '600' : '400',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    Desktops / Laptops / Servers
                    {(() => {
                      const count = duplicatesScanData.organizations?.reduce((sum, org) =>
                        sum + (org.groups?.filter(g => !g.is_mobile)?.length || 0), 0) || 0;
                      return count > 0 ? <span style={{ background: 'rgba(59, 130, 246, 0.3)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.75rem' }}>{count}</span> : null;
                    })()}
                  </button>
                  <button
                    onClick={() => setDuplicatesSubTab('mobile')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: duplicatesSubTab === 'mobile' ? 'rgba(168, 85, 247, 0.2)' : 'transparent',
                      border: 'none',
                      borderRadius: '6px 6px 0 0',
                      color: duplicatesSubTab === 'mobile' ? '#a855f7' : '#888',
                      cursor: 'pointer',
                      fontWeight: duplicatesSubTab === 'mobile' ? '600' : '400',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    Mobile Devices
                    {(() => {
                      const count = duplicatesScanData.organizations?.reduce((sum, org) =>
                        sum + (org.groups?.filter(g => g.is_mobile)?.length || 0), 0) || 0;
                      return count > 0 ? <span style={{ background: 'rgba(168, 85, 247, 0.3)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.75rem' }}>{count}</span> : null;
                    })()}
                  </button>
                </div>

                {/* Mobile tab info */}
                {duplicatesSubTab === 'mobile' && (
                  <div style={{
                    background: 'rgba(168, 85, 247, 0.1)',
                    border: '1px solid #a855f7',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1.5rem',
                    fontSize: '0.9rem'
                  }}>
                    <strong>Mobile Devices</strong> — Duplicates are detected by hostname, but mobile devices
                    often share generic names (e.g. "IPHONE"). Use IMEI and serial number to confirm true duplicates.
                    Stale mobile devices are typically devices no longer in use that can be safely removed.
                  </div>
                )}

                {/* Organizations List - filtered by sub-tab */}
                {(() => {
                  const isMobileTab = duplicatesSubTab === 'mobile';
                  // Filter organizations and their groups by the selected sub-tab
                  const filteredOrgs = duplicatesScanData.organizations?.map(org => ({
                    ...org,
                    groups: org.groups?.filter(g => isMobileTab ? g.is_mobile : !g.is_mobile) || []
                  })).filter(org => org.groups.length > 0) || [];

                  return filteredOrgs.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✨</div>
                    <h3 style={{ margin: '0 0 0.5rem 0' }}>No Duplicates Found</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {duplicatesOrgFilter
                        ? `No duplicate devices found in ${duplicatesOrgFilter}.`
                        : 'All Microsoft devices are unique. No cleanup needed.'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="duplicates-orgs-list">
                    {filteredOrgs.map(org => {
                      // Compute verification counts for the org (filtered by current sub-tab)
                      const orgVerifiedCount = org.groups.reduce((sum, g) => {
                        return sum + g.devices.filter(d => {
                          const vs = getVerificationStatus(d);
                          return vs.status === 'verified';
                        }).length;
                      }, 0);
                      const orgTotalDevices = org.groups.reduce((sum, g) => sum + g.devices.length, 0);
                      const orgAllVerified = orgTotalDevices > 0 && orgVerifiedCount === orgTotalDevices;

                      return (
                      <div key={org.organization_name} className="duplicates-org-card" style={{
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: orgAllVerified ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid var(--border-color)',
                        marginBottom: '1rem',
                        overflow: 'hidden'
                      }}>
                        {/* Org Header */}
                        <div
                          onClick={() => setDuplicatesExpandedOrg(
                            duplicatesExpandedOrg === org.organization_name ? null : org.organization_name
                          )}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem',
                            cursor: 'pointer',
                            borderBottom: duplicatesExpandedOrg === org.organization_name ? '1px solid var(--border-color)' : 'none'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.2rem' }}>
                              {duplicatesExpandedOrg === org.organization_name ? '▼' : '▶'}
                            </span>
                            <strong>{org.organization_name}</strong>
                            <span style={{
                              background: 'rgba(245, 158, 11, 0.2)',
                              color: '#f59e0b',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8rem'
                            }}>
                              {org.total_groups} group{org.total_groups !== 1 ? 's' : ''}
                            </span>
                            {org.total_stale_devices > 0 && (
                              <span style={{
                                background: 'rgba(239, 68, 68, 0.2)',
                                color: '#ef4444',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '0.8rem'
                              }}>
                                {org.total_stale_devices} stale
                              </span>
                            )}
                            <span style={{
                              background: orgVerifiedCount > 0
                                ? orgAllVerified
                                  ? 'rgba(34, 197, 94, 0.2)'
                                  : 'rgba(34, 197, 94, 0.15)'
                                : 'rgba(107, 114, 128, 0.15)',
                              color: orgVerifiedCount > 0 ? '#22c55e' : '#6b7280',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: orgAllVerified ? '600' : '400'
                            }}>
                              {orgAllVerified ? '✓ ' : ''}{orgVerifiedCount}/{orgTotalDevices} verified
                            </span>
                          </div>
                        </div>

                        {/* Org Expanded Content */}
                        {duplicatesExpandedOrg === org.organization_name && (
                          <div style={{ padding: '1rem' }}>
                            {org.groups.map((group, idx) => {
                              const groupVerifiedCount = group.devices.filter(d => {
                                const vs = getVerificationStatus(d);
                                return vs.status === 'verified';
                              }).length;
                              const groupAllVerified = group.devices.length > 0 && groupVerifiedCount === group.devices.length;

                              return (
                              <div key={`${group.hostname}-${idx}`} style={{
                                background: groupAllVerified ? 'rgba(34, 197, 94, 0.05)' : 'var(--bg-tertiary)',
                                borderRadius: '6px',
                                marginBottom: '0.75rem',
                                overflow: 'hidden',
                                border: groupAllVerified ? '1px solid rgba(34, 197, 94, 0.25)' : 'none'
                              }}>
                                {/* Group Header */}
                                <div
                                  onClick={() => setDuplicatesExpandedGroup(
                                    duplicatesExpandedGroup === `${org.organization_name}-${group.hostname}`
                                      ? null
                                      : `${org.organization_name}-${group.hostname}`
                                  )}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '0.75rem 1rem',
                                    cursor: 'pointer',
                                    background: groupAllVerified ? 'rgba(34, 197, 94, 0.05)' : 'var(--bg-tertiary)'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ color: 'var(--text-secondary)' }}>
                                      {duplicatesExpandedGroup === `${org.organization_name}-${group.hostname}` ? '▾' : '▸'}
                                    </span>
                                    <code style={{ background: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: '4px' }}>
                                      {group.hostname}
                                    </code>
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                      ({group.device_count} records)
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{
                                      color: groupVerifiedCount > 0 ? '#22c55e' : '#6b7280',
                                      fontSize: '0.8rem',
                                      fontWeight: groupAllVerified ? '600' : '400'
                                    }}>
                                      {groupAllVerified ? '✓ ' : ''}{groupVerifiedCount}/{group.devices.length} verified
                                    </span>
                                    {group.stale_count > 0 && (
                                      <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>
                                        {group.stale_count} stale
                                      </span>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Reset per-modal state when opening a new modal
                                        setDuplicatesSelectedDevices(new Set());
                                        const modalDeviceIds = new Set(group.devices.map(d => d.unique_id));
                                        // Filter verification sessions to only include this modal's devices
                                        setVerificationSessions(prev => {
                                          const filtered = {};
                                          Object.entries(prev).forEach(([key, val]) => {
                                            if (modalDeviceIds.has(key)) filtered[key] = val;
                                          });
                                          return filtered;
                                        });
                                        setDuplicatesManageModal({
                                          hostname: group.hostname,
                                          org: org.organization_name,
                                          devices: group.devices,
                                          staleThreshold: duplicatesScanData.summary?.stale_threshold_days || 30
                                        });
                                      }}
                                      className="btn-action-small"
                                      style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                                    >
                                      Manage
                                    </button>
                                  </div>
                                </div>

                                {/* Group Expanded - Device List */}
                                {duplicatesExpandedGroup === `${org.organization_name}-${group.hostname}` && (
                                  <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border-color)' }}>
                                    <table style={{ width: '100%', fontSize: '0.85rem' }}>
                                      <thead>
                                        <tr style={{ color: 'var(--text-secondary)' }}>
                                          <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}>Type</th>
                                          <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}>Serial</th>
                                          {group.is_mobile && <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}>IMEI</th>}
                                          {group.is_mobile && <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}>Phone</th>}
                                          <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }} title="ApproximateLastSignInDateTime">Last Sign-In ↓</th>
                                          <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}>Verification</th>
                                          <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}>Recommendation</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {group.devices.map((device, dIdx) => {
                                          const vStatus = getVerificationStatus(device);
                                          const isVerified = vStatus.status === 'verified';
                                          return (
                                          <tr key={device.unique_id} style={{
                                            background: isVerified ? 'rgba(34, 197, 94, 0.08)' : dIdx === 0 ? 'rgba(74, 222, 128, 0.1)' : device.is_stale ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
                                            borderLeft: isVerified ? '3px solid #22c55e' : '3px solid transparent'
                                          }}>
                                            <td style={{ padding: '0.5rem' }}>
                                              <span style={{
                                                background: device.device_type === 'Intune' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(168, 85, 247, 0.2)',
                                                color: device.device_type === 'Intune' ? '#3b82f6' : '#a855f7',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem'
                                              }}>
                                                {device.device_type}
                                              </span>
                                            </td>
                                            <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                              {device.serial_number || 'N/A'}
                                            </td>
                                            {group.is_mobile && (
                                              <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                {device.imei || 'N/A'}
                                              </td>
                                            )}
                                            {group.is_mobile && (
                                              <td style={{ padding: '0.5rem', fontSize: '0.8rem' }}>
                                                {device.phone_number || 'N/A'}
                                              </td>
                                            )}
                                            <td style={{ padding: '0.5rem' }}>
                                              {formatDeviceDate(device)}
                                            </td>
                                            <td style={{ padding: '0.5rem' }}>
                                              <span style={{ color: vStatus.color, fontSize: '0.8rem', fontWeight: isVerified ? '600' : '400' }}>
                                                {vStatus.label}
                                              </span>
                                            </td>
                                            <td style={{ padding: '0.5rem' }}>
                                              {dIdx === 0 ? (
                                                <span style={{ color: '#4ade80' }}>✓ Keep (most active)</span>
                                              ) : device.is_stale ? (
                                                <span style={{ color: '#ef4444' }}>🗑 Delete (stale)</span>
                                              ) : (
                                                <span style={{ color: '#f59e0b' }}>⚠ Less Active</span>
                                              )}
                                            </td>
                                          </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                );
                })()}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                Select an organization or choose "All Organizations" and click "Scan for Duplicates" to find duplicate Microsoft devices.
              </div>
            )}
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="branding-section">
            <h2 style={{ marginBottom: '0.5rem' }}>Branding & Whitelabel</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Customize the application appearance with your organization's branding. Upload logos, set your company name, and choose accent colors.
            </p>

            {brandingLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading branding settings...</div>
            ) : (
              <>
                {/* App Name */}
                <div className="branding-group">
                  <h3>Application Name</h3>
                  <p className="branding-hint">Replaces "ASSET MESH" in the header. Leave blank for default.</p>
                  <input
                    type="text"
                    className="setting-input"
                    value={brandingSettings.app_name}
                    onChange={(e) => setBrandingSettings(prev => ({ ...prev, app_name: e.target.value }))}
                    placeholder="ASSET MESH"
                    maxLength={40}
                    style={{ maxWidth: '400px' }}
                  />
                </div>

                {/* Logo Uploads - Side by Side */}
                <div className="branding-group">
                  <h3>Logo</h3>
                  <p className="branding-hint">Upload your logo for each theme. Recommended: PNG or SVG with transparent background, max 500KB. Square aspect ratio works best.</p>
                  <div className="branding-logo-grid">
                    {/* Dark Mode Logo */}
                    <div className="branding-logo-card" data-theme-preview="dark">
                      <div className="branding-logo-label">Dark Mode</div>
                      <div className="branding-logo-preview" style={{ background: '#0f1419' }}>
                        {brandingSettings.logo_dark ? (
                          <img src={brandingSettings.logo_dark} alt="Logo (dark)" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <div style={{ color: '#555', fontSize: '0.85rem', textAlign: 'center' }}>No logo uploaded</div>
                        )}
                      </div>
                      <div className="branding-logo-actions">
                        <label className="branding-upload-btn">
                          Upload
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleLogoUpload('logo_dark', e)} />
                        </label>
                        {brandingSettings.logo_dark && (
                          <button className="branding-clear-btn" onClick={() => clearBrandingField('logo_dark')}>Remove</button>
                        )}
                      </div>
                    </div>

                    {/* Light Mode Logo */}
                    <div className="branding-logo-card" data-theme-preview="light">
                      <div className="branding-logo-label">Light Mode</div>
                      <div className="branding-logo-preview" style={{ background: '#f5f7fa' }}>
                        {brandingSettings.logo_light ? (
                          <img src={brandingSettings.logo_light} alt="Logo (light)" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <div style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center' }}>No logo uploaded</div>
                        )}
                      </div>
                      <div className="branding-logo-actions">
                        <label className="branding-upload-btn">
                          Upload
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleLogoUpload('logo_light', e)} />
                        </label>
                        {brandingSettings.logo_light && (
                          <button className="branding-clear-btn" onClick={() => clearBrandingField('logo_light')}>Remove</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Favicon */}
                <div className="branding-group">
                  <h3>Favicon</h3>
                  <p className="branding-hint">Browser tab icon. Recommended: square PNG or ICO, 64x64px or larger, max 500KB.</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div className="branding-favicon-preview">
                      {brandingSettings.favicon ? (
                        <img src={brandingSettings.favicon} alt="Favicon" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                      ) : (
                        <div style={{ width: '32px', height: '32px', background: 'var(--bg-hover)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.7rem' }}>ico</div>
                      )}
                    </div>
                    <label className="branding-upload-btn">
                      Upload Favicon
                      <input type="file" accept="image/*,.ico" style={{ display: 'none' }} onChange={(e) => handleLogoUpload('favicon', e)} />
                    </label>
                    {brandingSettings.favicon && (
                      <button className="branding-clear-btn" onClick={() => clearBrandingField('favicon')}>Remove</button>
                    )}
                  </div>
                </div>

                {/* Colors */}
                <div className="branding-group">
                  <h3>Accent Colors</h3>
                  <p className="branding-hint">Customize the primary and accent colors used throughout the UI. Leave blank for defaults.</p>
                  <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Primary Color</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="color"
                          value={brandingSettings.primary_color || '#00d9ff'}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                          style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: 'none' }}
                        />
                        <input
                          type="text"
                          className="setting-input"
                          value={brandingSettings.primary_color}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                          placeholder="#00d9ff"
                          maxLength={7}
                          style={{ width: '100px', fontFamily: 'monospace' }}
                        />
                        {brandingSettings.primary_color && (
                          <button className="branding-clear-btn" onClick={() => clearBrandingField('primary_color')} style={{ padding: '4px 8px', fontSize: '0.75rem' }}>Reset</button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Accent Color</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="color"
                          value={brandingSettings.accent_color || '#a55eea'}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                          style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: 'none' }}
                        />
                        <input
                          type="text"
                          className="setting-input"
                          value={brandingSettings.accent_color}
                          onChange={(e) => setBrandingSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                          placeholder="#a55eea"
                          maxLength={7}
                          style={{ width: '100px', fontFamily: 'monospace' }}
                        />
                        {brandingSettings.accent_color && (
                          <button className="branding-clear-btn" onClick={() => clearBrandingField('accent_color')} style={{ padding: '4px 8px', fontSize: '0.75rem' }}>Reset</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div className="branding-group">
                  <h3>Preview</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    <button
                      className={`branding-preview-toggle ${brandingPreviewTheme === 'dark' ? 'active' : ''}`}
                      onClick={() => setBrandingPreviewTheme('dark')}
                    >
                      Dark
                    </button>
                    <button
                      className={`branding-preview-toggle ${brandingPreviewTheme === 'light' ? 'active' : ''}`}
                      onClick={() => setBrandingPreviewTheme('light')}
                    >
                      Light
                    </button>
                  </div>
                  <div
                    className="branding-preview-header"
                    style={{
                      background: brandingPreviewTheme === 'dark' ? '#0f1419' : '#f5f7fa',
                      color: brandingPreviewTheme === 'dark' ? '#fff' : '#2c3e50',
                      padding: '1rem 1.5rem',
                      borderRadius: '10px',
                      border: '1px solid var(--border-color)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    {(brandingPreviewTheme === 'dark' ? brandingSettings.logo_dark : brandingSettings.logo_light) ? (
                      <img
                        src={brandingPreviewTheme === 'dark' ? brandingSettings.logo_dark : brandingSettings.logo_light}
                        alt="Preview logo"
                        style={{ width: '38px', height: '38px', objectFit: 'contain' }}
                      />
                    ) : (
                      <div style={{ width: '38px', height: '38px', background: brandingPreviewTheme === 'dark' ? '#1e2433' : '#e8ecf1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: brandingPreviewTheme === 'dark' ? '#555' : '#aaa' }}>LOGO</div>
                    )}
                    <span style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      letterSpacing: '1px',
                      color: brandingSettings.primary_color || (brandingPreviewTheme === 'dark' ? '#00d9ff' : '#3498db')
                    }}>
                      {brandingSettings.app_name || 'ASSET MESH'}
                    </span>
                    <span style={{
                      background: `linear-gradient(135deg, ${brandingSettings.primary_color || '#3b82f6'}, ${brandingSettings.accent_color || '#8b5cf6'})`,
                      borderRadius: '12px',
                      padding: '3px 8px',
                      fontSize: '0.65rem',
                      fontWeight: '600',
                      color: 'white',
                      fontFamily: 'monospace'
                    }}>
                      v5.4.0
                    </span>
                  </div>
                </div>

                {/* Save / Reset */}
                <div className="settings-actions" style={{ marginTop: '1.5rem' }}>
                  <button
                    className="btn-save"
                    onClick={saveBrandingSettings}
                    disabled={brandingSaving}
                  >
                    {brandingSaving ? 'Saving...' : 'Save Branding'}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={resetBrandingSettings}
                    style={{ marginLeft: '0.75rem' }}
                  >
                    Reset to Defaults
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Duplicates Manage Modal */}
      {duplicatesManageModal && (
        <div className="modal-overlay" onClick={() => {
          setDuplicatesManageModal(null);
          setDuplicatesSelectedDevices(new Set());
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '850px' }}>
            <div className="modal-header">
              <h3>Manage Duplicates: {duplicatesManageModal.hostname}</h3>
              <button className="modal-close" onClick={() => {
                setDuplicatesManageModal(null);
                setDuplicatesSelectedDevices(new Set());
              }}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Found {duplicatesManageModal.devices.length} Microsoft records for this device in <strong>{duplicatesManageModal.org}</strong>.
                Devices are sorted by <strong>Last Sign-In</strong> (most recent activity first). The most recently active device is recommended to keep.
              </p>

              {/* Verification Queue Status — server-driven, no Verify All button needed */}
              {(() => {
                const modalIds = new Set(duplicatesManageModal.devices.map(d => d.unique_id));
                const modalQueued = Object.entries(verificationSessions)
                  .filter(([key, s]) => modalIds.has(key) && s.status === 'queued').length;
                const modalProcessing = Object.entries(verificationSessions)
                  .filter(([key, s]) => modalIds.has(key) && (s.status === 'triggering' || s.status === 'running')).length;
                const modalScheduled = Object.entries(verificationSessions)
                  .filter(([key, s]) => modalIds.has(key) && s.status === 'scheduled').length;
                const modalVerified = Object.entries(verificationSessions)
                  .filter(([key, s]) => modalIds.has(key) && s.status === 'verified').length;

                const hasActivity = modalQueued > 0 || modalProcessing > 0 || modalScheduled > 0;

                return hasActivity ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    background: 'var(--bg-tertiary)',
                    borderRadius: '6px',
                    fontSize: '0.85rem'
                  }}>
                    <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Verification:</span>
                    {modalQueued > 0 && <span style={{ color: '#3b82f6' }}>🕐 {modalQueued} queued</span>}
                    {modalProcessing > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#f59e0b' }}>
                        <span className="spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                        {modalProcessing} processing
                      </span>
                    )}
                    {modalScheduled > 0 && <span style={{ color: '#8b5cf6' }}>📅 {modalScheduled} scheduled</span>}
                    {modalVerified > 0 && <span style={{ color: '#22c55e' }}>✅ {modalVerified} verified</span>}
                  </div>
                ) : null;
              })()}

              {/* Verification Info Box */}
              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                <strong>🔍 Device Verification:</strong> Devices are automatically queued for verification after import. Click "Verify Now" to prioritize a specific device.
                This confirms which Azure AD record matches the actual device by comparing hardware Device IDs.
                {(() => {
                  // Derive match results from verificationSessions (populated by queue-status endpoint)
                  const activeDevices = [];
                  const staleDevices = [];

                  duplicatesManageModal.devices.forEach(device => {
                    const session = verificationSessions[device.unique_id];
                    if (session?.match_result) {
                      if (session.match_result.is_active) {
                        activeDevices.push({ ...device, match_method: session.match_result.match_method });
                      } else {
                        staleDevices.push(device);
                      }
                    }
                  });

                  if (activeDevices.length === 0 && staleDevices.length === 0) return null;

                  return (
                  <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '4px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>📋 Verification Results</strong>
                    </div>

                    {activeDevices.length > 0 && (
                      <div style={{ fontSize: '0.85rem', color: '#22c55e', marginBottom: '0.25rem' }}>
                        ✅ <strong>{activeDevices.length} Active record{activeDevices.length > 1 ? 's' : ''}</strong> matched
                        {activeDevices.map(d => (
                          <span key={d.unique_id} style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            ({d.display_name || d.hostname} via {d.match_method === 'thumbprint' ? 'Thumbprint' : d.match_method === 'activity' ? 'Activity' : 'Device ID'})
                          </span>
                        ))}
                      </div>
                    )}

                    {staleDevices.length > 0 && (
                      <div style={{ fontSize: '0.85rem', color: '#ef4444', marginTop: '0.25rem' }}>
                        🗑️ <strong>{staleDevices.length} Stale record{staleDevices.length > 1 ? 's' : ''}</strong> — safe to delete
                      </div>
                    )}
                  </div>
                  );
                })()}
              </div>

              {/* No verification method warning */}
              {!duplicatesManageModal.devices.some(d => d.computer_id) && (
                <div style={{
                  background: 'rgba(249, 115, 22, 0.15)',
                  border: '1px solid #f97316',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  fontSize: '0.9rem'
                }}>
                  <strong>⚠️ No verification method available:</strong> None of these devices have an ImmyBot agent.
                  Verification cannot confirm which record is active. Deleting is at your own risk.
                </div>
              )}

              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.4)',
                borderRadius: '6px',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                <strong>ℹ️ Note:</strong> Stale Intune records will be deleted. Entra device objects will be archived (disabled) instead of permanently deleted, and can be re-enabled if needed.
                <br />
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  A PSA ticket will be created automatically. Safety checks (Autopilot, Hybrid Join, shared Entra) are performed before archiving.
                </span>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={
                          duplicatesManageModal.devices.slice(1).length > 0 &&
                          duplicatesManageModal.devices.slice(1).every(d => duplicatesSelectedDevices.has(d.unique_id))
                        }
                        onChange={(e) => {
                          const newSelected = new Set(duplicatesSelectedDevices);
                          duplicatesManageModal.devices.slice(1).forEach(d => {
                            if (e.target.checked) {
                              newSelected.add(d.unique_id);
                            } else {
                              newSelected.delete(d.unique_id);
                            }
                          });
                          setDuplicatesSelectedDevices(newSelected);
                        }}
                      />
                    </th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Type</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Serial</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>
                      <span title="ApproximateLastSignInDateTime from Microsoft Graph API - sorted by most recent">
                        Last Sign-In ↓
                      </span>
                    </th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Verified</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicatesManageModal.devices.map((device, idx) => (
                    <tr
                      key={device.unique_id}
                      style={{
                        borderBottom: '1px solid var(--border-color)',
                        background: idx === 0 ? 'rgba(74, 222, 128, 0.1)' : duplicatesSelectedDevices.has(device.unique_id) ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '0.5rem' }}>
                        {idx === 0 ? (
                          <span title="This is the newest record - recommended to keep">🔒</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={duplicatesSelectedDevices.has(device.unique_id)}
                            onChange={(e) => {
                              const newSelected = new Set(duplicatesSelectedDevices);
                              if (e.target.checked) {
                                newSelected.add(device.unique_id);
                              } else {
                                newSelected.delete(device.unique_id);
                              }
                              setDuplicatesSelectedDevices(newSelected);
                            }}
                          />
                        )}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <span style={{
                          background: device.device_type === 'Intune' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(168, 85, 247, 0.2)',
                          color: device.device_type === 'Intune' ? '#3b82f6' : '#a855f7',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem'
                        }}>
                          {device.device_type}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {device.serial_number || 'N/A'}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {formatDeviceDate(device)}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {(() => {
                          const vStatus = getVerificationStatus(device);
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ color: vStatus.color, fontSize: '0.85rem' }}>
                                {vStatus.label}
                              </span>
                              {(vStatus.status === 'unverified' || vStatus.status === 'expired' || vStatus.status === 'reverify') && device.computer_id && (
                                <button
                                  onClick={() => triggerVerifyNow(device)}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '0.75rem',
                                    background: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                  }}
                                  title="Verify Now — pushes to front of queue and triggers immediately"
                                >
                                  Verify Now
                                </button>
                              )}
                              {vStatus.status === 'unverified' && !device.computer_id && (
                                <span
                                  style={{ fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic' }}
                                  title="This device must have an ImmyBot agent to run verification script"
                                >
                                  (no ImmyBot)
                                </span>
                              )}
                              {vStatus.status === 'verifying' && (
                                <span style={{ fontSize: '0.75rem', color: '#f59e0b' }}>
                                  (polling...)
                                </span>
                              )}
                              {(vStatus.status === 'scheduled' || vStatus.status === 'pending') && (
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Cancel this scheduled verification?')) {
                                      try {
                                        const response = await apiFetch(`${API_URL}/microsoft/verification/cancel`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            device_unique_id: device.unique_id,
                                            reason: 'Cancelled by user'
                                          })
                                        });
                                        const data = await response.json();
                                        if (data.status === 'success') {
                                          // Clear local session state for this device
                                          setVerificationSessions(prev => {
                                            const updated = { ...prev };
                                            delete updated[device.unique_id];
                                            return updated;
                                          });
                                          // Refresh duplicates scan to get updated status
                                          fetchDuplicatesScan();
                                        }
                                      } catch (error) {
                                        console.error('Failed to cancel verification:', error);
                                      }
                                    }
                                  }}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '0.7rem',
                                    background: 'transparent',
                                    color: '#ef4444',
                                    border: '1px solid #ef4444',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                  }}
                                  title="Cancel scheduled verification"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {idx === 0 ? (
                          <span style={{ color: '#4ade80', fontWeight: '500' }}>✓ Keep (most active)</span>
                        ) : device.is_stale ? (
                          <span style={{ color: '#ef4444' }}>🗑 Stale ({device.days_since_checkin}+ days)</span>
                        ) : (
                          <span style={{ color: '#f59e0b' }}>⚠ Less Active</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    const staleIds = duplicatesManageModal.devices.slice(1).filter(d => d.is_stale).map(d => d.unique_id);
                    setDuplicatesSelectedDevices(new Set(staleIds));
                  }}
                  className="btn-action-small"
                  style={{ fontSize: '0.85rem' }}
                >
                  Select All Stale
                </button>
                <button
                  onClick={() => {
                    const allIds = duplicatesManageModal.devices.slice(1).map(d => d.unique_id);
                    setDuplicatesSelectedDevices(new Set(allIds));
                  }}
                  className="btn-action-small"
                  style={{ fontSize: '0.85rem' }}
                >
                  Select All (except newest)
                </button>
                <button
                  onClick={() => setDuplicatesSelectedDevices(new Set())}
                  className="btn-action-small"
                  style={{ fontSize: '0.85rem' }}
                >
                  Clear Selection
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setDuplicatesManageModal(null);
                setDuplicatesSelectedDevices(new Set());
                // Keep verificationSessions running in background
              }}>
                Cancel
              </button>
              <button
                className="btn-danger"
                disabled={
                  // Only count selected devices that belong to THIS modal
                  duplicatesManageModal.devices.filter(d => duplicatesSelectedDevices.has(d.unique_id)).length === 0
                  || duplicatesDeleting
                }
                onClick={async () => {
                  // Only include device IDs that are in the current modal
                  const modalDeviceIdSet = new Set(duplicatesManageModal.devices.map(d => d.unique_id));
                  const deviceIds = Array.from(duplicatesSelectedDevices).filter(id => modalDeviceIdSet.has(id));
                  const selectedDevices = duplicatesManageModal.devices.filter(d => deviceIds.includes(d.unique_id));

                  // Check if any selected devices are unverified or still in queue
                  const pendingStatuses = ['unverified', 'reverify', 'queued', 'verifying'];
                  const pendingCount = selectedDevices.filter(d => {
                    const vStatus = getVerificationStatus(d);
                    return pendingStatuses.includes(vStatus.status);
                  }).length;

                  // Check if ImmyBot verification is available for this group
                  const hasImmyBot = duplicatesManageModal.devices.some(d => d.computer_id);

                  let confirmMessage = `Clean up ${deviceIds.length} duplicate device(s)?\n\nStale Intune records will be permanently deleted.\nEntra device objects will be archived (disabled), not deleted.\nArchived devices can be re-enabled later if needed.\n\nSafety checks will be performed automatically.`;

                  if (pendingCount > 0) {
                    confirmMessage = `⚠️ WARNING: ${pendingCount} of ${deviceIds.length} device(s) are unverified or still in queue.\n\n`;
                    if (!hasImmyBot) {
                      confirmMessage += `No verification method is enabled for this organization. Cleanup is at your own risk.\n\n`;
                    } else {
                      confirmMessage += `Verification is queued or in progress. You can wait for it to complete, or proceed now.\n\n`;
                    }
                    confirmMessage += `Proceed with cleanup?\n\nIntune records will be deleted. Entra objects will be archived (can be re-enabled).`;
                  }

                  // Add PSA ticket info
                  confirmMessage += `\n\nA PSA ticket will be created automatically.`;

                  if (window.confirm(confirmMessage)) {
                    await deleteDuplicates(deviceIds);
                  }
                }}
                style={{ background: '#d97706' }}
              >
                {duplicatesDeleting ? 'Processing...' : `Clean Up ${duplicatesManageModal.devices.filter(d => duplicatesSelectedDevices.has(d.unique_id)).length} Selected`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Settings Modal */}
      {editingOrgSettings && (
        <div className="modal-overlay" onClick={() => setEditingOrgSettings(null)}>
          <div className="modal-content org-settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Organization Settings</h3>
              <button className="modal-close" onClick={() => setEditingOrgSettings(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="org-settings-name">{editingOrgSettings}</div>

              {/* Tab Navigation */}
              <div className="org-settings-tabs">
                <button
                  className={`org-settings-tab ${orgSettingsTab === 'general' ? 'active' : ''}`}
                  onClick={() => setOrgSettingsTab('general')}
                >
                  General
                </button>
                <button
                  className={`org-settings-tab ${orgSettingsTab === 'integrations' ? 'active' : ''}`}
                  onClick={() => setOrgSettingsTab('integrations')}
                >
                  Integrations
                </button>
                <button
                  className={`org-settings-tab ${orgSettingsTab === 'macos' ? 'active' : ''}`}
                  onClick={() => setOrgSettingsTab('macos')}
                >
                  macOS
                </button>
                {orgSettingsForm.microsoft_enabled && (
                  <button
                    className={`org-settings-tab ${orgSettingsTab === 'microsoft' ? 'active' : ''}`}
                    onClick={() => setOrgSettingsTab('microsoft')}
                  >
                    Microsoft
                  </button>
                )}
              </div>

              {/* Tab Content */}
              <div className="org-settings-tab-content">
                {/* General Tab */}
                {orgSettingsTab === 'general' && (
                  <>
                    <div className="org-settings-section">
                      <h4>Visibility</h4>
                      <label className="visibility-option">
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.is_hidden}
                          onChange={(e) => setOrgSettingsForm({...orgSettingsForm, is_hidden: e.target.checked})}
                        />
                        <div className="visibility-option-content">
                          <span className="visibility-option-label">Hide from dashboard</span>
                          <span className="visibility-option-hint">Organization will still sync but won't appear in device list</span>
                        </div>
                      </label>
                    </div>

                    <div className="org-settings-section">
                      <h4>Primary RMM</h4>
                      <p className="section-description">
                        Override the global primary RMM setting for this organization. This affects health calculations and PSA comparison.
                      </p>
                      <select
                        className="primary-rmm-select"
                        value={orgSettingsForm.primary_rmm || 'global'}
                        onChange={(e) => setOrgSettingsForm({...orgSettingsForm, primary_rmm: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          backgroundColor: 'var(--bg-secondary)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="global">Use Global Setting (N-Central)</option>
                        <option value="ncentral">N-Central</option>
                        <option value="datto">Datto RMM</option>
                        <option value="microsoft">Microsoft Intune</option>
                      </select>
                    </div>

                    <div className="org-settings-section">
                      <h4>Notes</h4>
                      <textarea
                        className="org-notes"
                        placeholder="Optional notes about this organization..."
                        value={orgSettingsForm.notes || ''}
                        onChange={(e) => setOrgSettingsForm({...orgSettingsForm, notes: e.target.value})}
                      />
                    </div>
                  </>
                )}

                {/* Integrations Tab */}
                {orgSettingsTab === 'integrations' && (
                  <div className="org-settings-section">
                    <h4>Expected Integrations</h4>
                    <p className="section-description">
                      Only flag "Missing from X" if the integration is enabled. Disable integrations this organization doesn't use.
                    </p>

                    <div className="integration-toggles">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.ncentral_enabled}
                          onChange={(e) => setOrgSettingsForm({...orgSettingsForm, ncentral_enabled: e.target.checked})}
                        />
                        <span>N-Central</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.connectwise_enabled}
                          onChange={(e) => setOrgSettingsForm({...orgSettingsForm, connectwise_enabled: e.target.checked})}
                        />
                        <span>ConnectWise</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.huntress_enabled}
                          onChange={(e) => setOrgSettingsForm({...orgSettingsForm, huntress_enabled: e.target.checked})}
                        />
                        <span>Huntress</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.immybot_enabled}
                          onChange={(e) => setOrgSettingsForm({...orgSettingsForm, immybot_enabled: e.target.checked})}
                        />
                        <span>ImmyBot</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.cloudradial_enabled}
                          onChange={(e) => setOrgSettingsForm({...orgSettingsForm, cloudradial_enabled: e.target.checked})}
                        />
                        <span>CloudRadial</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.datto_rmm_enabled}
                          onChange={(e) => handleDattoRmmToggle(e.target.checked, editingOrgSettings)}
                        />
                        <span>Datto RMM</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.microsoft_enabled}
                          onChange={(e) => {
                            setOrgSettingsForm({...orgSettingsForm, microsoft_enabled: e.target.checked});
                            // If disabling Microsoft and on Microsoft tab, switch to general
                            if (!e.target.checked && orgSettingsTab === 'microsoft') {
                              setOrgSettingsTab('general');
                            }
                          }}
                        />
                        <span>Microsoft</span>
                      </label>
                    </div>
                    {dattoRmmFilterWarning && (
                      <div className="filter-warning" style={{
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#fef3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '4px',
                        color: '#856404',
                        fontSize: '13px'
                      }}>
                        <strong>Warning:</strong> {dattoRmmFilterWarning}
                      </div>
                    )}
                  </div>
                )}

                {/* macOS Tab */}
                {orgSettingsTab === 'macos' && (
                  <div className="org-settings-section">
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ backgroundColor: '#333', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>macOS</span>
                      Requirements
                    </h4>
                    <p className="section-description" style={{ marginBottom: '12px' }}>
                      Configure which integrations are required for macOS devices in this organization.
                      Windows devices use the standard integration settings from the Integrations tab.
                    </p>

                    <label className="integration-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={orgSettingsForm.mac_health_checks_enabled}
                        onChange={(e) => setOrgSettingsForm({...orgSettingsForm, mac_health_checks_enabled: e.target.checked})}
                      />
                      <span style={{ fontWeight: '500' }}>Enable macOS health checks</span>
                    </label>

                    {orgSettingsForm.mac_health_checks_enabled ? (
                      <div style={{ marginLeft: '24px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        <label className="integration-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={orgSettingsForm.mac_ncentral_required}
                            onChange={(e) => setOrgSettingsForm({...orgSettingsForm, mac_ncentral_required: e.target.checked})}
                          />
                          <span>N-Central</span>
                        </label>
                        <label className="integration-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={orgSettingsForm.mac_datto_rmm_required}
                            onChange={(e) => setOrgSettingsForm({...orgSettingsForm, mac_datto_rmm_required: e.target.checked})}
                          />
                          <span>Datto RMM</span>
                        </label>
                        <label className="integration-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={orgSettingsForm.mac_cloudradial_required}
                            onChange={(e) => setOrgSettingsForm({...orgSettingsForm, mac_cloudradial_required: e.target.checked})}
                          />
                          <span>CloudRadial</span>
                        </label>
                        <label className="integration-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={orgSettingsForm.mac_huntress_required}
                            onChange={(e) => setOrgSettingsForm({...orgSettingsForm, mac_huntress_required: e.target.checked})}
                          />
                          <span>Huntress</span>
                        </label>
                        <label className="integration-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={orgSettingsForm.mac_connectwise_required}
                            onChange={(e) => setOrgSettingsForm({...orgSettingsForm, mac_connectwise_required: e.target.checked})}
                          />
                          <span>ConnectWise</span>
                        </label>
                        <label className="integration-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={orgSettingsForm.mac_microsoft_required}
                            onChange={(e) => setOrgSettingsForm({...orgSettingsForm, mac_microsoft_required: e.target.checked})}
                          />
                          <span>Microsoft</span>
                        </label>
                      </div>
                    ) : (
                      <p style={{ fontSize: '0.85rem', color: '#888', marginLeft: '24px' }}>
                        Enable macOS health checks to require specific integrations for macOS devices.
                        Without this, macOS devices will always show as "Healthy".
                      </p>
                    )}
                  </div>
                )}

                {/* Microsoft Tab */}
                {orgSettingsTab === 'microsoft' && orgSettingsForm.microsoft_enabled && (
                  <div className="org-settings-section">
                    <h4 style={{ color: '#60a5fa', marginBottom: '12px' }}>GDAP Consent</h4>
                    <p className="section-description" style={{ marginBottom: '12px' }}>
                      Grant admin consent to pull Intune and Azure AD devices from this customer's tenant.
                    </p>

                    {microsoftConsents[editingOrgSettings]?.consent_granted ? (
                      <div style={{ background: 'rgba(74, 222, 128, 0.1)', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{ color: '#4ade80', fontSize: '1.2rem' }}>✓</span>
                          <strong style={{ color: '#4ade80' }}>Consent Granted</strong>
                        </div>
                        <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                          <div>Tenant ID: <code>{microsoftConsents[editingOrgSettings]?.customer_tenant_id?.substring(0, 8)}...</code></div>
                          {microsoftConsents[editingOrgSettings]?.customer_tenant_name && (
                            <div>Tenant Name: {microsoftConsents[editingOrgSettings]?.customer_tenant_name}</div>
                          )}
                          {microsoftConsents[editingOrgSettings]?.last_sync_at && (
                            <div>Last Sync: {microsoftConsents[editingOrgSettings]?.last_sync_at} ({microsoftConsents[editingOrgSettings]?.last_sync_device_count || 0} devices)</div>
                          )}
                        </div>
                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                          <button
                            className="btn-test"
                            onClick={async () => {
                              setMicrosoftTestingTenant(editingOrgSettings);
                              try {
                                const response = await apiFetch(`${API_URL}/microsoft/test-tenant`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ org_name: editingOrgSettings })
                                });
                                const data = await response.json();
                                showToast(data.success
                                  ? `Connected! Found ${data.intune_device_count} Intune devices, ${data.azure_ad_device_count} Azure AD devices.`
                                  : `Test failed: ${data.error}`, { type: data.success ? 'success' : 'error' });
                              } catch (err) {
                                showToast(`Error: ${err.message}`, { type: 'error' });
                              }
                              setMicrosoftTestingTenant(null);
                            }}
                            disabled={microsoftTestingTenant === editingOrgSettings}
                            style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                          >
                            {microsoftTestingTenant === editingOrgSettings ? 'Testing...' : 'Test Connection'}
                          </button>
                          <button
                            className="btn-danger"
                            onClick={async () => {
                              if (!window.confirm(`Revoke Microsoft consent for ${editingOrgSettings}? Device sync will stop for this organization.`)) return;
                              try {
                                const response = await apiFetch(`${API_URL}/microsoft/revoke-consent`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ org_name: editingOrgSettings })
                                });
                                const data = await response.json();
                                if (data.status === 'success') {
                                  setMicrosoftConsents(prev => ({ ...prev, [editingOrgSettings]: { ...prev[editingOrgSettings], consent_granted: false } }));
                                  showToast('Consent revoked successfully', { type: 'success' });
                                } else {
                                  showToast(`Failed: ${data.message}`, { type: 'error' });
                                }
                              } catch (err) {
                                showToast(`Error: ${err.message}`, { type: 'error' });
                              }
                            }}
                            style={{ fontSize: '0.85rem', padding: '6px 12px', background: '#dc2626' }}
                          >
                            Revoke Consent
                          </button>
                        </div>
                        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                          <label style={{ display: 'block', marginBottom: '8px', color: '#9ca3af', fontSize: '0.85rem' }}>
                            Device Types to Sync:
                          </label>
                          <select
                            value={microsoftConsents[editingOrgSettings]?.device_type_filter || 'both'}
                            onChange={async (e) => {
                              const newFilter = e.target.value;
                              try {
                                const response = await apiFetch(`${API_URL}/microsoft/update-device-filter`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ org_name: editingOrgSettings, device_type_filter: newFilter })
                                });
                                const data = await response.json();
                                if (data.status === 'success') {
                                  setMicrosoftConsents(prev => ({
                                    ...prev,
                                    [editingOrgSettings]: { ...prev[editingOrgSettings], device_type_filter: newFilter }
                                  }));
                                } else {
                                  showToast(`Failed to update filter: ${data.message}`, { type: 'error' });
                                }
                              } catch (err) {
                                showToast(`Error: ${err.message}`, { type: 'error' });
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color)',
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              fontSize: '0.9rem'
                            }}
                          >
                            <option value="both">Both Intune + Azure AD Devices</option>
                            <option value="intune_only">Intune Managed Devices Only</option>
                            <option value="azure_ad_only">Azure AD Registered/Joined Only</option>
                          </select>
                          <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '6px' }}>
                            Intune = MDM-managed devices. Azure AD = domain-joined or registered devices (may include more devices but less detail).
                          </p>
                        </div>

                        {/* Archive Setup Section */}
                        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                          <label style={{ display: 'block', marginBottom: '8px', color: '#9ca3af', fontSize: '0.85rem', fontWeight: '600' }}>
                            Device Archiving (Best Practice)
                          </label>
                          <p style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '12px' }}>
                            Archive devices instead of deleting them. Archived devices are disabled in Entra ID and excluded from health checks.
                            A dynamic group will be created in Entra to track archived devices.
                          </p>

                          {microsoftArchivingStatus[editingOrgSettings]?.archiving_enabled ? (
                            <div style={{ background: 'rgba(74, 222, 128, 0.1)', padding: '10px', borderRadius: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                <span style={{ color: '#4ade80' }}>✓</span>
                                <strong style={{ color: '#4ade80', fontSize: '0.85rem' }}>Archiving Enabled</strong>
                              </div>
                              <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                                <div>Dynamic Group ID: <code>{microsoftArchivingStatus[editingOrgSettings]?.archive_group_id?.substring(0, 8)}...</code></div>
                                <div style={{ marginTop: '4px' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={microsoftArchivingStatus[editingOrgSettings]?.intune_retire_enabled || false}
                                      onChange={async (e) => {
                                        const newValue = e.target.checked;
                                        try {
                                          const response = await apiFetch(`${API_URL}/microsoft/update-archiving-settings`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              org_name: editingOrgSettings,
                                              intune_retire_enabled: newValue
                                            })
                                          });
                                          const data = await response.json();
                                          if (data.status === 'success') {
                                            setMicrosoftArchivingStatus(prev => ({
                                              ...prev,
                                              [editingOrgSettings]: { ...prev[editingOrgSettings], intune_retire_enabled: newValue }
                                            }));
                                          } else {
                                            showToast(`Failed: ${data.message}`, { type: 'error' });
                                          }
                                        } catch (err) {
                                          showToast(`Error: ${err.message}`, { type: 'error' });
                                        }
                                      }}
                                    />
                                    <span style={{ fontSize: '0.8rem' }}>Also retire device in Intune (removes company data)</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <button
                              className="btn-primary"
                              onClick={async () => {
                                if (!window.confirm(`Set up device archiving for ${editingOrgSettings}?\n\nThis will:\n• Create a dynamic group "Asset Mesh - Archived Devices" in Entra\n• Enable archive/unarchive actions for Microsoft devices\n\nArchived devices will be disabled (not deleted) and excluded from health checks.`)) return;

                                setMicrosoftArchivingSetupLoading(editingOrgSettings);
                                try {
                                  const response = await apiFetch(`${API_URL}/microsoft/setup-archiving`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ org_name: editingOrgSettings })
                                  });
                                  const data = await response.json();
                                  if (data.status === 'success') {
                                    setMicrosoftArchivingStatus(prev => ({
                                      ...prev,
                                      [editingOrgSettings]: {
                                        archiving_enabled: true,
                                        archive_group_id: data.group_id,
                                        intune_retire_enabled: false
                                      }
                                    }));
                                    showToast('Archiving setup complete! Dynamic group created in Entra.', { type: 'success' });
                                  } else {
                                    showToast(`Setup failed: ${data.message}`, { type: 'error' });
                                  }
                                } catch (err) {
                                  showToast(`Error: ${err.message}`, { type: 'error' });
                                }
                                setMicrosoftArchivingSetupLoading(null);
                              }}
                              disabled={microsoftArchivingSetupLoading === editingOrgSettings}
                              style={{ fontSize: '0.85rem', padding: '8px 14px', background: '#8b5cf6' }}
                            >
                              {microsoftArchivingSetupLoading === editingOrgSettings ? 'Setting up...' : 'Set Up Archiving Best Practice'}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{ background: 'rgba(251, 191, 36, 0.1)', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{ color: '#fbbf24', fontSize: '1.2rem' }}>!</span>
                          <strong style={{ color: '#fbbf24' }}>Consent Required</strong>
                        </div>
                        <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '12px' }}>
                          Click below to open Microsoft's admin consent page. The customer's tenant admin must approve.
                          <br /><span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>Tip: Use "Copy Link" and paste into an InPrivate/Incognito window if you see device errors.</span>
                        </p>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            className="btn-primary"
                            onClick={async () => {
                              try {
                                const response = await apiFetch(`${API_URL}/microsoft/consent-url`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ org_name: editingOrgSettings })
                                });
                                const data = await response.json();
                                if (data.status === 'success' && data.consent_url) {
                                  window.open(data.consent_url, '_blank');
                                } else {
                                  showToast(`Failed to generate consent URL: ${data.message}`, { type: 'error' });
                                }
                              } catch (err) {
                                showToast(`Error: ${err.message}`, { type: 'error' });
                              }
                            }}
                            style={{ background: '#3b82f6', padding: '8px 16px' }}
                          >
                            Grant Microsoft Consent
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={async () => {
                              try {
                                const response = await apiFetch(`${API_URL}/microsoft/consent-url`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ org_name: editingOrgSettings })
                                });
                                const data = await response.json();
                                if (data.status === 'success' && data.consent_url) {
                                  await navigator.clipboard.writeText(data.consent_url);
                                  showToast('Consent URL copied! Paste into an InPrivate/Incognito window.', { type: 'success' });
                                } else {
                                  showToast(`Failed to generate consent URL: ${data.message}`, { type: 'error' });
                                }
                              } catch (err) {
                                showToast(`Error: ${err.message}`, { type: 'error' });
                              }
                            }}
                            style={{ background: '#4b5563', padding: '8px 16px' }}
                          >
                            Copy Link
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Mobile Device Settings - always visible when Microsoft is enabled */}
                    <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <span style={{ backgroundColor: '#8b5cf6', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>Mobile</span>
                        Device Visibility
                      </h4>
                      <p className="section-description" style={{ marginBottom: '12px', color: '#9ca3af', fontSize: '0.85rem' }}>
                        Microsoft Intune syncs mobile devices (Android/iOS) in addition to computers.
                        Hide mobile devices to focus on computers in the dashboard.
                      </p>
                      <label className="integration-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={orgSettingsForm.hide_mobile_devices}
                          onChange={(e) => setOrgSettingsForm({...orgSettingsForm, hide_mobile_devices: e.target.checked})}
                        />
                        <span style={{ fontWeight: '500' }}>Hide mobile devices (Android/iOS)</span>
                      </label>
                      <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '8px', marginLeft: '24px' }}>
                        When enabled, Android and iOS devices from Microsoft Intune will not appear in the device list.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setEditingOrgSettings(null)}>Cancel</button>
              <button
                className="btn-save"
                onClick={saveOrgSettings}
                disabled={orgSettingsSaving || dattoRmmChecking}
              >
                {orgSettingsSaving ? 'Saving...' : dattoRmmChecking ? 'Checking...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
